// ============================================================================
// SS2 ENGLISH LANGUAGE — COMPLETE LEARNING MODULE (24 TOPICS)
// NERDC Scheme of Work — First Term to Third Term
// ============================================================================

export const WAEC_ENGLISH_LEARN_SS2 = [
  {
    topic: 'Word Formation: Nominalization and Affixation',
    topicCode: 'SS2-ENG-01',
    module: 'Lexis and Structure',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Word formation</span> is the process by which new words are created from existing words, usually by adding prefixes or suffixes (affixes), or by changing a word\'s grammatical category. <span class="learn-keyword">Nominalization</span> is one of the most common word-formation processes in English — converting verbs and adjectives into nouns.\n</div>\n\n<h3 class="learn-subheading">1. Meaning of Nominalization</h3>\n<p class="learn-p">Nominalization is the process of forming a noun from a word belonging to another word class — usually a verb or an adjective — typically by adding a suffix.</p>\n\n<h3 class="learn-subheading">2. Verb to Noun Nominalization</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Suffix</th><th>Verb</th><th>Noun</th></tr></thead>\n    <tbody>\n      <tr><td>-tion / -sion</td><td>educate, decide</td><td>education, decision</td></tr>\n      <tr><td>-ment</td><td>develop, govern</td><td>development, government</td></tr>\n      <tr><td>-ance / -ence</td><td>perform, differ</td><td>performance, difference</td></tr>\n      <tr><td>-al</td><td>arrive, refuse</td><td>arrival, refusal</td></tr>\n      <tr><td>-ing</td><td>build, read</td><td>building, reading</td></tr>\n      <tr><td>-er / -or</td><td>teach, act</td><td>teacher, actor</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">3. Adjective to Noun Nominalization</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Suffix</th><th>Adjective</th><th>Noun</th></tr></thead>\n    <tbody>\n      <tr><td>-ness</td><td>kind, happy</td><td>kindness, happiness</td></tr>\n      <tr><td>-ity</td><td>active, equal</td><td>activity, equality</td></tr>\n      <tr><td>-th</td><td>strong, wide</td><td>strength, width</td></tr>\n      <tr><td>-cy</td><td>accurate, urgent</td><td>accuracy, urgency</td></tr>\n      <tr><td>-ce</td><td>important, patient</td><td>importance, patience</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">4. Affixation: Prefixes and Suffixes</h3>\n<ul class="learn-list">\n  <li><strong>Prefix:</strong> An affix added BEFORE a root word (e.g., un-, dis-, re-, mis-, pre-, anti-)</li>\n  <li><strong>Suffix:</strong> An affix added AFTER a root word (e.g., -ful, -less, -able, -ize)</li>\n  <li>Prefixes usually change MEANING without changing word class (happy → unhappy: both adjectives)</li>\n  <li>Suffixes often change WORD CLASS (happy [adj] → happiness [noun])</li>\n</ul>\n\n<h3 class="learn-subheading">5. Words of the Week (Vocabulary)</h3>\n<p class="learn-p">Important vocabulary for this term: <strong>bombastic</strong> (using inflated language), <strong>dearth</strong> (scarcity), <strong>hiatus</strong> (a pause/gap), <strong>implacable</strong> (unable to be appeased), <strong>stingy</strong> (unwilling to give/spend), <strong>dour</strong> (stern, unfriendly), <strong>ebullient</strong> (cheerful and full of energy), <strong>ignominious</strong> (deserving disgrace), <strong>impinge</strong> (to have an impact on), <strong>tome</strong> (a large, heavy book).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Nominalization = converting verbs/adjectives into nouns via suffixes (-tion, -ment, -ness, -ity, etc.). Prefixes usually change meaning (un-, dis-, re-); suffixes usually change word class. WAEC frequently tests: \'The noun form of [verb/adjective] is...\'\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Word formation creates new words through affixation. Nominalization converts verbs (-tion, -ment, -ance, -al, -ing, -er) and adjectives (-ness, -ity, -th, -cy, -ce) into nouns. Prefixes (before root) often change meaning; suffixes (after root) often change word class.\n</div>\n  ',
    questions: [
      {
        q: "What is 'nominalization' in English grammar?",
        o: [
          'The process of forming verbs from nouns',
          'The process of forming nouns from words belonging to other word classes, especially verbs and adjectives',
          'The use of capital letters for proper nouns',
          'The process of pluralizing nouns',
        ],
        a: 1,
        e: "Nominalization is the grammatical process of converting a word from another class (typically a verb or adjective) into a noun, usually through the addition of a suffix. For example, 'decide' (verb) becomes 'decision' (noun).",
        h: 'Nominalization: converting verbs/adjectives INTO NOUNS using suffixes.',
        yr: 'SS2',
      },
      {
        q: "Which of the following is the correct noun form of the verb 'educate'?",
        o: ['Educative', 'Educator', 'Education', 'Educable'],
        a: 2,
        e: "'Education' is the noun form of 'educate', formed by adding the suffix '-tion'. 'Educative' is an adjective, 'educator' refers to a person who educates, and 'educable' is an adjective meaning capable of being educated.",
        h: "Add '-tion' to 'educat-' (drop the final 'e') to form the noun.",
        yr: 'SS2',
      },
      {
        q: "The noun form of the adjective 'kind' is formed by adding which suffix?",
        o: ['-ity', '-ness', '-th', '-ence'],
        a: 1,
        e: "'Kindness' is formed by adding '-ness' to the adjective 'kind'. The suffix '-ness' is one of the most productive suffixes for converting adjectives into abstract nouns (e.g., happiness, sadness, darkness).",
        h: 'Kind + suffix = kindness (a common adjective-to-noun suffix).',
        yr: 'SS2',
      },
      {
        q: "Choose the correct noun form of the verb 'perform':",
        o: ['Performal', 'Performation', 'Performance', 'Performity'],
        a: 2,
        e: "'Performance' is formed by adding '-ance' to 'perform'. This suffix is commonly used to nominalize verbs ending in certain sounds (e.g., assist→assistance, appear→appearance).",
        h: "The suffix '-ance' converts 'perform' into its noun form.",
        yr: 'SS2',
      },
      {
        q: "Which suffix correctly converts the adjective 'active' into a noun?",
        o: ['-ness (activeness)', '-ity (activity)', '-tion (activetion)', '-ment (activement)'],
        a: 1,
        e: "'Activity' is the correct noun form of 'active', formed using the suffix '-ity'. This suffix often replaces the final '-e' in adjectives ending in '-ive', '-able', or similar patterns (e.g., equal→equality, real→reality).",
        h: "Active + -ity (drop the 'e') = activity.",
        yr: 'SS2',
      },
      {
        q: "What is the noun form of the verb 'arrive'?",
        o: ['Arrivement', 'Arrival', 'Arrivence', 'Arrivation'],
        a: 1,
        e: "'Arrival' is the noun form of 'arrive', formed by adding the suffix '-al' (and dropping the final 'e'). Other examples following this pattern include: refuse→refusal, propose→proposal, dismiss→dismissal.",
        h: "Arrive (drop 'e') + -al = arrival.",
        yr: 'SS2',
      },
      {
        q: "Which of the following correctly identifies a PREFIX in the word 'unhappy'?",
        o: [
          '-py is the prefix',
          'happy is the prefix',
          "un- is the prefix, attached before the root word 'happy'",
          'There is no prefix in this word',
        ],
        a: 2,
        e: "'Un-' is a prefix attached before the root word 'happy' to form 'unhappy'. Prefixes are added BEFORE the root, while suffixes are added AFTER. 'Un-' here changes the meaning (to the opposite) without changing the word class — 'happy' and 'unhappy' are both adjectives.",
        h: "A prefix comes BEFORE the root word. 'Un-' is before 'happy'.",
        yr: 'SS2',
      },
      {
        q: 'Which statement best describes the typical difference between prefixes and suffixes?',
        o: [
          'Prefixes always change word class; suffixes never do',
          'Prefixes usually change the MEANING of a word without changing its word class; suffixes often change the WORD CLASS of a word',
          'Both prefixes and suffixes are always interchangeable',
          'Prefixes can only be attached to nouns',
        ],
        a: 1,
        e: "Prefixes (e.g., un-, dis-, re-, mis-) typically alter meaning while keeping the word in the same class — 'happy' (adj) → 'unhappy' (adj). Suffixes (e.g., -ness, -tion, -er) often shift the word to a different class — 'happy' (adj) → 'happiness' (noun).",
        h: 'Prefixes: meaning change, same class. Suffixes: often class change.',
        yr: 'SS2',
      },
      {
        q: "The word 'government' is derived from which base verb?",
        o: ['Govern', 'Governs', 'Governance', 'Governmental'],
        a: 0,
        e: "'Government' is the noun form of the verb 'govern', formed by adding the suffix '-ment'. This is a common pattern for nominalizing verbs: develop→development, manage→management, employ→employment.",
        h: "Remove '-ment' from 'government' to find the base verb.",
        yr: 'SS2',
      },
      {
        q: "Which of the following is the correct noun form of 'differ' (verb)?",
        o: ['Differation', 'Differment', 'Difference', 'Differity'],
        a: 2,
        e: "'Difference' is formed by adding '-ence' to 'differ'. This suffix is the variant of '-ance' used after certain consonant clusters and root endings (e.g., prefer→preference, exist→existence).",
        h: 'Differ + -ence = difference.',
        yr: 'SS2',
      },
      {
        q: "What is the noun form of the adjective 'strong'?",
        o: ['Strongness', 'Strongity', 'Strength', 'Strongance'],
        a: 2,
        e: "'Strength' is an irregular nominalization — formed by adding '-th' to 'strong' with a sound/spelling change (the 'g' is dropped). Similarly, 'wide' becomes 'width' and 'long' becomes 'length'. These follow an older, less productive pattern of English word formation.",
        h: "Strong → strength (an irregular '-th' nominalization, like 'wide → width').",
        yr: 'SS2',
      },
      {
        q: "Choose the noun form of the adjective 'accurate':",
        o: ['Accurateness', 'Accuracy', 'Accuration', 'Accurance'],
        a: 1,
        e: "'Accuracy' is formed by adding '-cy' to 'accurate' (with the final 'te' replaced). This suffix forms abstract nouns from certain adjectives ending in '-ate' or '-ent' (e.g., urgent→urgency, fluent→fluency).",
        h: 'Accura(te) + -cy = accuracy.',
        yr: 'SS2',
      },
      {
        q: "Which word means 'using inflated, pompous language meant to impress'?",
        o: ['Dour', 'Bombastic', 'Ebullient', 'Implacable'],
        a: 1,
        e: "'Bombastic' describes language or speech that is high-sounding but with little real meaning — pompous or overblown. Example: 'The politician's bombastic speech impressed no one with substance.'",
        h: "This 'word of the week' describes inflated, pompous speech.",
        yr: 'SS2',
      },
      {
        q: "The word 'dearth' means:",
        o: [
          'An abundance or surplus',
          'A scarcity or lack of something',
          'A type of celebration',
          'A small gift',
        ],
        a: 1,
        e: "'Dearth' means a scarcity or lack — the opposite of abundance. Example: 'There is a dearth of qualified teachers in rural areas.'",
        h: "'Dearth' is related to scarcity, not abundance.",
        yr: 'SS2',
      },
      {
        q: "Which word means 'a person or thing that cannot be appeased or pacified'?",
        o: ['Ebullient', 'Implacable', 'Tome', 'Hiatus'],
        a: 1,
        e: "'Implacable' describes someone or something that cannot be calmed or satisfied — an unyielding, relentless quality. Example: 'His implacable hatred for corruption drove his anti-corruption campaign.'",
        h: 'This word relates to being unable to be calmed or appeased.',
        yr: 'SS2',
      },
      {
        q: "A 'hiatus' refers to:",
        o: [
          'A loud argument',
          'A pause or gap in continuity',
          'A type of musical instrument',
          'A large, heavy book',
        ],
        a: 1,
        e: "'Hiatus' means a pause, break, or gap in an activity or sequence. Example: 'After a two-year hiatus, the musician released a new album.'",
        h: "'Hiatus' is about a break or interruption, not a physical object.",
        yr: 'SS2',
      },
      {
        q: "Which word describes someone who is 'unwilling to spend or give, especially money'?",
        o: ['Ebullient', 'Dour', 'Stingy', 'Tome'],
        a: 2,
        e: "'Stingy' describes a person who is unwilling to spend or give resources, especially money — synonymous with miserly. Example: 'He was too stingy to buy his employees a holiday gift.'",
        h: 'This word means unwilling to spend money.',
        yr: 'SS2',
      },
      {
        q: "The word 'ebullient' best describes a person who is:",
        o: [
          'Sad and withdrawn',
          'Cheerful and full of energy and enthusiasm',
          'Unfriendly and stern',
          'Quiet and shy',
        ],
        a: 1,
        e: "'Ebullient' describes someone overflowing with enthusiasm, excitement, or high spirits. Example: 'The ebullient crowd celebrated the team's victory with joy.'",
        h: 'This word relates to high-energy, cheerful enthusiasm.',
        yr: 'SS2',
      },
      {
        q: "Which of the following is the correct meaning of 'ignominious'?",
        o: [
          'Deserving honour and praise',
          'Deserving public disgrace or shame',
          'Relating to ignorance only',
          'Very intelligent and wise',
        ],
        a: 1,
        e: "'Ignominious' describes something deserving public shame or disgrace — typically used for humiliating defeats or failures. Example: 'The team suffered an ignominious defeat, losing 8-0.'",
        h: 'This word relates to shame or disgrace, not honour.',
        yr: 'SS2',
      },
      {
        q: "Which word means 'a large or heavy book, especially a scholarly one'?",
        o: ['Hiatus', 'Dearth', 'Tome', 'Dour'],
        a: 2,
        e: "'Tome' refers to a large, heavy book, often one of academic or scholarly importance. Example: 'The professor's office was filled with tomes on constitutional law.'",
        h: 'This word describes a physical object — a large book.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Nouns, Noun Phrases, and Noun Clauses',
    topicCode: 'SS2-ENG-02',
    module: 'Lexis and Structure',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  A <span class="learn-keyword">noun</span> is a word that names a person, place, thing, idea, or quality. Nouns can stand alone or be expanded into <span class="learn-keyword">noun phrases</span> and <span class="learn-keyword">noun clauses</span>. Distinguishing these structures is essential for both grammar accuracy and sentence analysis in WAEC English.\n</div>\n\n<h3 class="learn-subheading">1. Types of Nouns</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Type</th><th>Description</th><th>Examples</th></tr></thead>\n    <tbody>\n      <tr><td>Common</td><td>General names for people, places, things</td><td>boy, city, table</td></tr>\n      <tr><td>Proper</td><td>Specific names (capitalised)</td><td>Lagos, Aisha, Nigeria</td></tr>\n      <tr><td>Abstract</td><td>Names of ideas, qualities, states</td><td>honesty, freedom, joy</td></tr>\n      <tr><td>Concrete</td><td>Names of physical things</td><td>book, chair, mango</td></tr>\n      <tr><td>Collective</td><td>Names of groups</td><td>flock, team, jury</td></tr>\n      <tr><td>Countable</td><td>Can be counted; have plurals</td><td>book/books, child/children</td></tr>\n      <tr><td>Uncountable (Mass)</td><td>Cannot normally be counted; no plural</td><td>water, sugar, information</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">2. Noun Phrases — Structure and Function</h3>\n<p class="learn-p">A noun phrase (NP) is a group of words that functions like a noun in a sentence — it has a noun (the \'head\') and may include modifiers (determiners, adjectives) before it, and complements/modifiers after it.</p>\n<p class="learn-p"><strong>Structure:</strong> Determiner + Adjective(s) + Head Noun + (Modifier)<br>Example: <em>\'the tall, intelligent student from Kano\'</em> — \'student\' is the head noun.</p>\n\n<h3 class="learn-subheading">3. Functions of Noun Phrases in a Sentence</h3>\n<ul class="learn-list">\n  <li><strong>Subject:</strong> \'The young boy ran quickly.\'</li>\n  <li><strong>Object (Direct):</strong> \'She bought a new dress.\'</li>\n  <li><strong>Object of Preposition:</strong> \'He sat on the wooden chair.\'</li>\n  <li><strong>Complement:</strong> \'He is a brilliant teacher.\'</li>\n  <li><strong>Appositive:</strong> \'My friend, the doctor, called yesterday.\'</li>\n</ul>\n\n<h3 class="learn-subheading">4. Noun Clauses</h3>\n<p class="learn-p">A noun clause is a dependent clause that functions as a noun in a sentence — it has its own subject and verb but cannot stand alone. Noun clauses often begin with: <strong>that, what, who, whom, whether, why, how, when, where, if</strong>.</p>\n<ul class="learn-list">\n  <li><strong>As Subject:</strong> \'What he said surprised everyone.\'</li>\n  <li><strong>As Object:</strong> \'I know that he is honest.\'</li>\n  <li><strong>As Complement:</strong> \'The truth is that she lied.\'</li>\n  <li><strong>As Object of Preposition:</strong> \'I am interested in what you propose.\'</li>\n</ul>\n\n<h3 class="learn-subheading">5. Distinguishing Noun Phrases from Noun Clauses</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Noun Phrase</th><th>Noun Clause</th></tr></thead>\n    <tbody>\n      <tr><td>No subject-verb structure of its own</td><td>Has its own subject and verb (finite verb)</td></tr>\n      <tr><td>\'the brilliant student\'</td><td>\'that the student is brilliant\'</td></tr>\n      <tr><td>\'his arrival\'</td><td>\'when he arrived\'</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> A NOUN CLAUSE has its own subject + verb (finite verb) and begins with words like that, what, whether, why, who. A NOUN PHRASE has no internal subject-verb structure — it\'s just a noun with modifiers. WAEC often asks you to identify the function of an underlined group of words (subject, object, complement).\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Nouns are classified as common/proper, abstract/concrete, collective, countable/uncountable. A noun phrase = noun + modifiers (no subject-verb). A noun clause = dependent clause with subject+verb functioning as a noun (subject, object, complement). Both can serve as subject, object, or complement in a sentence.\n</div>\n  ',
    questions: [
      {
        q: 'Which of the following is an example of a COLLECTIVE noun?',
        o: ['Honesty', 'Flock', 'Lagos', 'Water'],
        a: 1,
        e: "'Flock' is a collective noun — it refers to a group of animals (especially sheep or birds) treated as a single unit. 'Honesty' is abstract, 'Lagos' is proper, and 'water' is uncountable.",
        h: 'A collective noun names a GROUP of things treated as one unit.',
        yr: 'SS2',
      },
      {
        q: "Identify the abstract noun in this sentence: 'Her honesty earned her the respect of the community.'",
        o: ['Community', 'Respect', 'Honesty', 'Her'],
        a: 2,
        e: "'Honesty' is an abstract noun — it names a quality or idea that cannot be perceived with the senses. Note that 'respect' could also be considered abstract, but 'honesty' is the clearest example here and the primary subject.",
        h: 'An abstract noun names a quality, idea, or state — not a physical object.',
        yr: 'SS2',
      },
      {
        q: "In the sentence 'The tall, intelligent student from Kano won the prize,' what is the HEAD NOUN of the underlined noun phrase 'the tall, intelligent student from Kano'?",
        o: ['Tall', 'Intelligent', 'Student', 'Kano'],
        a: 2,
        e: "The head noun is the central noun around which the rest of the noun phrase is built. 'Student' is the head — 'tall' and 'intelligent' are adjective modifiers before it, and 'from Kano' is a prepositional phrase modifying it after.",
        h: 'The head noun is the core word that the rest of the phrase describes/modifies.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence contains a NOUN CLAUSE functioning as the SUBJECT?',
        o: [
          'He gave the book to his friend.',
          'What he said surprised everyone.',
          'I know that he is honest.',
          "The teacher's explanation was clear.",
        ],
        a: 1,
        e: "'What he said' is a noun clause (it has its own subject 'he' and verb 'said') functioning as the subject of the sentence — it is what 'surprised everyone'. The other options either have no noun clause or use it as an object.",
        h: "Look for a clause (with subject + verb) that performs the action of the main verb 'surprised'.",
        yr: 'SS2',
      },
      {
        q: 'Which of the following is an UNCOUNTABLE (mass) noun?',
        o: ['Books', 'Information', 'Children', 'Tables'],
        a: 1,
        e: "'Information' is an uncountable noun — it has no plural form ('informations' is incorrect) and cannot be preceded by 'a' or 'an'. We say 'some information' or 'a piece of information', not 'an information'.",
        h: "Uncountable nouns have no plural form and cannot take 'a/an'.",
        yr: 'SS2',
      },
      {
        q: "In the sentence 'I am interested in what you propose,' the noun clause 'what you propose' functions as:",
        o: [
          'The subject of the sentence',
          "The direct object of the verb 'am'",
          "The object of the preposition 'in'",
          'An appositive',
        ],
        a: 2,
        e: "'What you propose' follows the preposition 'in', making it the object of that preposition. Noun clauses frequently follow prepositions in this way (e.g., 'I'm worried about what might happen').",
        h: "What comes immediately after the preposition 'in'?",
        yr: 'SS2',
      },
      {
        q: 'Which of the following best distinguishes a noun PHRASE from a noun CLAUSE?',
        o: [
          "A noun phrase always starts with 'that'; a noun clause never does",
          'A noun clause contains its own subject and finite verb; a noun phrase does not',
          'A noun phrase can never function as a subject; a noun clause always can',
          'There is no real difference between the two',
        ],
        a: 1,
        e: "The key distinguishing feature is that a noun clause has its own subject-verb (finite verb) structure (e.g., 'that he is honest' — subject 'he', verb 'is'), while a noun phrase is simply a noun with modifiers and has no internal subject-verb structure (e.g., 'his honesty').",
        h: 'Look for whether the group of words has its own subject AND a finite verb.',
        yr: 'SS2',
      },
      {
        q: "Identify the PROPER noun in the sentence: 'Aisha travelled to Lagos for the conference.'",
        o: ['Conference', 'Travelled', 'Aisha and Lagos', 'For'],
        a: 2,
        e: "'Aisha' (a person's name) and 'Lagos' (a place name) are both proper nouns — they refer to specific, named entities and are capitalised. 'Conference' is a common noun.",
        h: 'Proper nouns are SPECIFIC names of people, places, or things and are always capitalised.',
        yr: 'SS2',
      },
      {
        q: "In the sentence 'My friend, the doctor, gave me good advice,' the phrase 'the doctor' functions as:",
        o: [
          'The subject of the sentence',
          "An appositive renaming 'my friend'",
          'The direct object',
          'A noun clause',
        ],
        a: 1,
        e: "'The doctor' is an appositive — a noun phrase placed next to another noun phrase ('my friend') to rename or further identify it. Appositives are usually set off by commas.",
        h: 'An appositive renames or further explains a nearby noun, often set off by commas.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence contains a noun clause functioning as a COMPLEMENT (after a linking verb)?',
        o: [
          'She is reading a book.',
          'The truth is that she lied about her age.',
          'He walked to the market.',
          'They are playing football.',
        ],
        a: 1,
        e: "'That she lied about her age' is a noun clause functioning as a subject complement — it follows the linking verb 'is' and renames/explains 'the truth'. A complement after a linking verb (be, seem, become) describes or identifies the subject.",
        h: 'Look for a clause following a linking verb (is, was, seems) that identifies the subject.',
        yr: 'SS2',
      },
      {
        q: 'Which of these is a CONCRETE noun?',
        o: ['Freedom', 'Joy', 'Mango', 'Wisdom'],
        a: 2,
        e: "'Mango' is a concrete noun — it refers to a physical object that can be seen, touched, or perceived with the senses. 'Freedom', 'joy', and 'wisdom' are all abstract nouns referring to ideas, feelings, or qualities.",
        h: 'Concrete nouns can be perceived with the SENSES (touch, see, taste, etc.).',
        yr: 'SS2',
      },
      {
        q: "In the sentence 'He sat on the wooden chair,' the noun phrase 'the wooden chair' functions as:",
        o: [
          'The subject',
          'The direct object',
          "The object of the preposition 'on'",
          'A complement',
        ],
        a: 2,
        e: "'The wooden chair' comes after the preposition 'on', making it the object of that preposition. Prepositions are typically followed by noun phrases or noun clauses functioning as their objects.",
        h: "What noun phrase follows the preposition 'on'?",
        yr: 'SS2',
      },
      {
        q: "Choose the sentence in which the noun clause begins with 'whether':",
        o: [
          'I wonder whether he will come to the party.',
          'He said that the test was difficult.',
          'What you did was wrong.',
          'The man who called is my uncle.',
        ],
        a: 0,
        e: "'Whether he will come to the party' is the noun clause, beginning with 'whether' and functioning as the object of 'wonder'. Noun clauses introduced by 'whether' typically express uncertainty between alternatives.",
        h: "Look for the noun clause starter word 'whether', which expresses uncertainty/alternatives.",
        yr: 'SS2',
      },
      {
        q: 'Which group of nouns are all COUNTABLE?',
        o: [
          'Sugar, water, milk',
          'Books, pens, chairs',
          'Information, advice, furniture',
          'Honesty, courage, anger',
        ],
        a: 1,
        e: "'Books', 'pens', and 'chairs' are all countable nouns — they can be counted individually (one book, two pens, three chairs) and have plural forms. Note: 'furniture', 'advice', and 'information' are uncountable even though they refer to multiple items collectively.",
        h: 'Countable nouns can be preceded by numbers (one, two, three) and have plural forms.',
        yr: 'SS2',
      },
      {
        q: "In the sentence 'Why he resigned remains a mystery,' the underlined noun clause 'why he resigned' functions as:",
        o: [
          'The direct object',
          'The subject of the sentence',
          'An appositive',
          'The object of a preposition',
        ],
        a: 1,
        e: "'Why he resigned' is the subject of the sentence — it is what 'remains a mystery'. The noun clause performs the grammatical subject role even though it begins with the question word 'why'.",
        h: "Identify what the verb 'remains' is talking about — that is the subject.",
        yr: 'SS2',
      },
      {
        q: "Which of the following correctly identifies the determiner in the noun phrase 'these three brilliant boys'?",
        o: ['Brilliant', 'Three', 'These', 'Boys'],
        a: 2,
        e: "'These' is the determiner — it specifies which noun is being referred to (demonstrative determiner). 'Three' is a numeral/quantifier modifying the noun, 'brilliant' is an adjective, and 'boys' is the head noun.",
        h: 'A determiner (this, these, the, a, my) typically comes FIRST in a noun phrase.',
        yr: 'SS2',
      },
      {
        q: "Identify the noun phrase functioning as a DIRECT OBJECT in: 'The committee approved the new budget proposal yesterday.'",
        o: ['The committee', 'Approved', 'The new budget proposal', 'Yesterday'],
        a: 2,
        e: "'The new budget proposal' is the direct object — it receives the action of the verb 'approved'. 'The committee' is the subject, and 'yesterday' is an adverb of time, not a noun phrase functioning as an object.",
        h: "The direct object answers 'what?' after the verb — what did the committee approve?",
        yr: 'SS2',
      },
      {
        q: "Which sentence has a noun clause functioning as the object of the verb 'know'?",
        o: [
          'I know him very well.',
          'I know that he is telling the truth.',
          'I know the answer.',
          'I know about Lagos.',
        ],
        a: 1,
        e: "'That he is telling the truth' is a noun clause (with subject 'he' and verb 'is telling') functioning as the direct object of 'know'. The other options have simple pronouns or noun phrases as objects, not noun clauses.",
        h: "Look for the option where a full clause (subject+verb) follows 'know'.",
        yr: 'SS2',
      },
      {
        q: 'In Standard English, which sentence correctly uses an uncountable noun?',
        o: [
          'She gave me a useful advices.',
          'He has many furnitures in his house.',
          'She gave me some useful advice.',
          'I need three informations about the event.',
        ],
        a: 2,
        e: "'Advice' is uncountable and has no plural form — 'some useful advice' is correct. The incorrect options wrongly pluralize 'advice', 'furniture', and 'information', which are all uncountable nouns in English.",
        h: "Uncountable nouns cannot be made plural (no '-s') or counted directly with numbers.",
        yr: 'SS2',
      },
      {
        q: "What part of the sentence is 'a brilliant teacher' in: 'He is a brilliant teacher'?",
        o: [
          'The subject',
          'The direct object',
          "The subject complement, renaming/describing the subject 'he'",
          'The object of a preposition',
        ],
        a: 2,
        e: "'A brilliant teacher' is a subject complement — it follows the linking verb 'is' and gives more information about the subject 'he'. Subject complements rename or describe the subject after linking verbs (be, seem, become, appear).",
        h: "After linking verbs like 'is', the noun phrase that describes the subject is a complement.",
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Pronouns: Types and Usage',
    topicCode: 'SS2-ENG-03',
    module: 'Lexis and Structure',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  A <span class="learn-keyword">pronoun</span> is a word used in place of a noun to avoid repetition. English has several types of pronouns, each with a specific grammatical function. Correct pronoun usage — especially agreement and case — is one of the most heavily tested areas in WAEC Lexis and Structure.\n</div>\n\n<h3 class="learn-subheading">1. Types of Pronouns</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Type</th><th>Examples</th><th>Function</th></tr></thead>\n    <tbody>\n      <tr><td>Personal</td><td>I, you, he, she, it, we, they / me, him, her, us, them</td><td>Refer to specific people/things (subject or object form)</td></tr>\n      <tr><td>Possessive</td><td>mine, yours, his, hers, ours, theirs / my, your, his, her, our, their</td><td>Show ownership</td></tr>\n      <tr><td>Demonstrative</td><td>this, that, these, those</td><td>Point to specific things</td></tr>\n      <tr><td>Interrogative</td><td>who, whom, whose, which, what</td><td>Ask questions</td></tr>\n      <tr><td>Reflexive</td><td>myself, yourself, himself, herself, itself, ourselves, yourselves, themselves</td><td>Refer back to the subject</td></tr>\n      <tr><td>Relative</td><td>who, whom, whose, which, that</td><td>Introduce relative clauses; link to a noun</td></tr>\n      <tr><td>Reciprocal</td><td>each other, one another</td><td>Show mutual action between two or more</td></tr>\n      <tr><td>Indefinite</td><td>someone, anything, everybody, nobody, few, several, all</td><td>Refer to non-specific people/things</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">2. Personal Pronouns: Subject vs Object Case</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Subject (does the action)</th><th>Object (receives the action)</th></tr></thead>\n    <tbody>\n      <tr><td>I</td><td>me</td></tr>\n      <tr><td>he</td><td>him</td></tr>\n      <tr><td>she</td><td>her</td></tr>\n      <tr><td>we</td><td>us</td></tr>\n      <tr><td>they</td><td>them</td></tr>\n    </tbody>\n  </table>\n</div>\n<p class="learn-p">Common error: \'Between you and I\' — INCORRECT. Should be: \'Between you and me\' (object form, as object of preposition \'between\').</p>\n\n<h3 class="learn-subheading">3. Possessive: Determiner vs Pronoun</h3>\n<ul class="learn-list">\n  <li><strong>Possessive determiners</strong> (my, your, his, her, our, their) come BEFORE a noun: \'This is <strong>my</strong> book.\'</li>\n  <li><strong>Possessive pronouns</strong> (mine, yours, his, hers, ours, theirs) STAND ALONE, replacing a noun phrase: \'This book is <strong>mine</strong>.\'</li>\n</ul>\n\n<h3 class="learn-subheading">4. Reflexive Pronouns</h3>\n<p class="learn-p">Used when the subject and object of a verb refer to the same person/thing: \'She cut <strong>herself</strong>.\' Also used for emphasis: \'I will do it <strong>myself</strong>.\'</p>\n\n<h3 class="learn-subheading">5. Relative Pronouns</h3>\n<ul class="learn-list">\n  <li><strong>who</strong> — refers to people (subject): \'The man <strong>who</strong> called is my uncle.\'</li>\n  <li><strong>whom</strong> — refers to people (object): \'The man <strong>whom</strong> I met is my uncle.\'</li>\n  <li><strong>whose</strong> — possessive: \'The student <strong>whose</strong> book was lost...\'</li>\n  <li><strong>which</strong> — refers to things/animals: \'The car <strong>which</strong> I bought...\'</li>\n  <li><strong>that</strong> — refers to people or things (often in restrictive clauses): \'The book <strong>that</strong> I read...\'</li>\n</ul>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> WHO = subject (he did something); WHOM = object (something was done to him). Test: if you can replace with \'he/she\' use WHO; if you can replace with \'him/her\' use WHOM. Reciprocal pronouns (each other/one another) show mutual relationships between TWO OR MORE people. Indefinite pronouns like \'everyone\', \'nobody\', \'each\' take SINGULAR verbs.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Pronoun types: personal (subject/object), possessive (determiner/pronoun), demonstrative, interrogative, reflexive, relative, reciprocal, indefinite. Key distinctions: I/me (subject/object case); my/mine (determiner/pronoun); who/whom (subject/object); each other (two)/one another (more than two, traditionally). Indefinite pronouns (everyone, nobody) usually take singular verbs.\n</div>\n  ',
    questions: [
      {
        q: 'Which sentence correctly uses the OBJECT form of the pronoun?',
        o: [
          'The gift is for you and I.',
          'Between you and I, this is a secret.',
          'She gave the books to him and me.',
          'Him and me went to the market.',
        ],
        a: 2,
        e: "'Him and me' are correctly used as objects of the preposition 'to' (received the books). The other options incorrectly use subject pronouns ('I') where object pronouns ('me') are needed after prepositions like 'for' and 'between'.",
        h: 'After prepositions (to, for, between, with), use OBJECT pronouns (me, him, her, us, them).',
        yr: 'SS2',
      },
      {
        q: "Identify the type of pronoun in: 'This is my book, but that one is yours.'",
        o: [
          "'This' and 'that' are demonstrative pronouns; 'my' is a possessive determiner; 'yours' is a possessive pronoun",
          'All the underlined words are personal pronouns',
          "'This' and 'that' are relative pronouns",
          "'My' and 'yours' are both reflexive pronouns",
        ],
        a: 0,
        e: "'This' and 'that' point to specific items (demonstrative); 'my' comes before a noun, showing possession as a determiner; 'yours' stands alone, replacing a noun phrase (your book), making it a possessive pronoun.",
        h: "Determiners come BEFORE nouns (my book); possessive pronouns STAND ALONE (it's yours).",
        yr: 'SS2',
      },
      {
        q: "Choose the sentence with the correct use of 'who' or 'whom':",
        o: [
          'The man whom called is my uncle.',
          'The man who I met yesterday is my uncle.',
          'Whom is at the door?',
          'Give the book to who needs it most.',
        ],
        a: 1,
        e: "'The man who I met yesterday' is correct — 'who' refers to 'the man' as the object of 'met' in informal usage (though strict grammar might prefer 'whom', 'who' is widely accepted in this position). The other options misuse 'whom' for subjects.",
        h: "WHO = does the action (subject); WHOM = receives the action (object) — though 'who' is often used informally for both.",
        yr: 'SS2',
      },
      {
        q: 'Which sentence uses a REFLEXIVE pronoun correctly?',
        o: [
          'He hurt hisself while playing football.',
          'She bought herself a new dress.',
          'They blamed theirselves for the mistake.',
          'I did it for meself.',
        ],
        a: 1,
        e: "'Herself' is the correct reflexive pronoun for 'she'. 'Hisself', 'theirselves', and 'meself' are non-standard forms — the correct reflexive forms are 'himself', 'themselves', and 'myself'.",
        h: 'Reflexive pronouns: myself, yourself, himself, herself, itself, ourselves, yourselves, themselves.',
        yr: 'SS2',
      },
      {
        q: "Identify the RECIPROCAL pronoun in: 'The two friends helped each other with their homework.'",
        o: ['The two friends', 'Helped', 'Each other', 'Their homework'],
        a: 2,
        e: "'Each other' is a reciprocal pronoun, showing a mutual action/relationship between the two friends (each friend helped the other). Reciprocal pronouns are 'each other' (typically for two) and 'one another' (typically for more than two).",
        h: 'Reciprocal pronouns express MUTUAL action between people: each other, one another.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence demonstrates correct subject-verb agreement with an INDEFINITE pronoun?',
        o: [
          'Everyone are happy with the result.',
          'Everybody has submitted their assignment.',
          'Nobody know the answer.',
          'Each of the students were given a prize.',
        ],
        a: 1,
        e: "'Everybody has' is correct — indefinite pronouns like 'everybody', 'everyone', 'nobody', 'each', and 'someone' are treated as SINGULAR and take singular verbs ('has', not 'have'). 'Their' is commonly accepted as a gender-neutral singular possessive in modern English.",
        h: 'Indefinite pronouns like everybody, everyone, nobody, each are SINGULAR — use singular verbs (has, is, was).',
        yr: 'SS2',
      },
      {
        q: 'Choose the correctly punctuated and worded relative clause: The woman ___ car was stolen reported it to the police.',
        o: ['who', 'whom', 'whose', 'which'],
        a: 2,
        e: "'Whose' is the possessive relative pronoun, used to show ownership — 'the woman's car was stolen' becomes 'the woman whose car was stolen'. 'Who' and 'whom' refer to people but don't show possession; 'which' refers to things, not people.",
        h: 'Look for OWNERSHIP — whose car belongs to the woman.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly distinguishes between a possessive determiner and a possessive pronoun?',
        o: [
          'This is mine pen.',
          "Is this pen yours or her's?",
          "That bag is theirs, not our's.",
          'Her book is on the table, but mine is in my bag.',
        ],
        a: 3,
        e: "'Her' (determiner, before 'book') and 'mine' (pronoun, standing alone) and 'my' (determiner, before 'bag') are all correctly used. Note: possessive pronouns (mine, yours, hers, ours, theirs) NEVER take an apostrophe — 'her's' and 'our's' are incorrect.",
        h: 'Possessive pronouns (mine, yours, hers, ours, theirs) NEVER have apostrophes.',
        yr: 'SS2',
      },
      {
        q: "Identify the INTERROGATIVE pronoun in: 'Whose bag is this on the table?'",
        o: ['Bag', 'Whose', 'This', 'Table'],
        a: 1,
        e: "'Whose' is functioning as an interrogative pronoun here, asking a question about ownership. Interrogative pronouns (who, whom, whose, which, what) are used to form questions.",
        h: 'Interrogative pronouns are used to ASK QUESTIONS: who, whom, whose, which, what.',
        yr: 'SS2',
      },
      {
        q: "Which sentence uses 'each other' and 'one another' correctly according to traditional usage?",
        o: [
          'The five students helped each other with their projects, and the two friends supported one another.',
          'The two friends helped each other with their homework, while the entire class supported one another during exams.',
          'She talked to one another about the issue.',
          'The teacher gave each other instructions to the class.',
        ],
        a: 1,
        e: "Traditionally, 'each other' is used for TWO people/things, while 'one another' is used for MORE THAN TWO. 'The two friends helped each other' (2 people) and 'the entire class supported one another' (many people) follows this traditional distinction, though modern usage often treats them interchangeably.",
        h: 'Traditional rule: each other = TWO; one another = MORE THAN TWO.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following sentences contains a DEMONSTRATIVE pronoun?',
        o: [
          'I bought this book yesterday.',
          'These are the documents you requested.',
          'That car belongs to my father.',
          'Those students are very hardworking.',
        ],
        a: 1,
        e: "'These' in 'These are the documents you requested' is a demonstrative PRONOUN — it stands alone as the subject of the sentence. In the other options, 'this', 'that', and 'those' come before nouns (book, car, students), making them demonstrative DETERMINERS, not pronouns.",
        h: 'A demonstrative pronoun STANDS ALONE (replacing a noun); a demonstrative determiner comes BEFORE a noun.',
        yr: 'SS2',
      },
      {
        q: 'Choose the sentence with the correct personal pronoun in subject position:',
        o: [
          'Me and him went to the cinema.',
          'He and I went to the cinema.',
          'Him and I went to the cinema.',
          'I and he went to the cinema (this order is preferred).',
        ],
        a: 1,
        e: "'He and I' uses correct subject pronouns ('he' and 'I'), and in formal English, the convention is to put 'I' last in a compound subject ('He and I', not 'I and he'). 'Me and him' and 'Him and I' incorrectly use object forms for the subject.",
        h: 'Subject pronouns: I, he, she, we, they — NOT me, him, her, us, them.',
        yr: 'SS2',
      },
      {
        q: "What type of pronoun is 'something' in the sentence 'Something is wrong with the car'?",
        o: ['Personal pronoun', 'Demonstrative pronoun', 'Indefinite pronoun', 'Reflexive pronoun'],
        a: 2,
        e: "'Something' is an indefinite pronoun — it refers to a non-specific thing without identifying exactly what it is. Other indefinite pronouns include: anything, everything, nothing, someone, anyone, everyone, no one, few, many, several, all, both.",
        h: 'Indefinite pronouns refer to NON-SPECIFIC people or things: something, someone, anything, etc.',
        yr: 'SS2',
      },
      {
        q: "Which sentence correctly uses 'which' as a relative pronoun?",
        o: [
          'The man which called is my uncle.',
          'The book which I bought yesterday is interesting.',
          'The girl which won the prize is my sister.',
          'The boy which scored the goal is happy.',
        ],
        a: 1,
        e: "'Which' is correctly used to refer to 'the book' (a thing), as in 'the book which I bought'. The other options incorrectly use 'which' to refer to people (man, girl, boy) — 'who' should be used for people instead.",
        h: "'WHICH' refers to THINGS or ANIMALS, not people. Use 'who' for people.",
        yr: 'SS2',
      },
      {
        q: "In the sentence 'He poured himself a cup of tea,' the reflexive pronoun 'himself' is used for what purpose?",
        o: [
          'To ask a question',
          'To show that the subject performed an action on/for itself',
          'To show possession',
          'To introduce a relative clause',
        ],
        a: 1,
        e: "'Himself' shows that the subject ('he') is also the recipient of the action — he poured tea for himself. This is the primary function of reflexive pronouns: when the subject and object refer to the same entity.",
        h: 'Reflexive pronouns indicate the subject and object are the SAME PERSON/THING.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence demonstrates the correct use of an indefinite pronoun with a singular verb?',
        o: [
          'Few of the students has completed the assignment.',
          'Each of the candidates has submitted their forms.',
          'Several of the books were damage.',
          'All of the food was eat by the guests.',
        ],
        a: 1,
        e: "'Each' (indefinite pronoun) correctly takes the singular verb 'has'. Note: 'few', 'several', 'all', and 'many' typically take PLURAL verbs ('have', 'were'), so option A is incorrect ('has' should be 'have'), and options C and D have other grammatical errors (damage/eat should be 'damaged'/'eaten').",
        h: "'Each', 'every', 'either', 'neither' = SINGULAR. 'Few', 'several', 'many', 'both', 'all' (with plural nouns) = PLURAL.",
        yr: 'SS2',
      },
      {
        q: "Choose the sentence that correctly uses 'whom':",
        o: [
          'Whom is calling, please?',
          'To whom should I address this letter?',
          'Whom wants to come with me?',
          'Whom is the captain of the team?',
        ],
        a: 1,
        e: "'To whom should I address this letter?' is correct — 'whom' is the object of the preposition 'to'. The other options incorrectly use 'whom' as a subject (the doer of an action), where 'who' should be used instead.",
        h: "'Whom' typically follows a PREPOSITION (to whom, for whom, with whom) or is the object of a verb.",
        yr: 'SS2',
      },
      {
        q: "Identify the possessive determiner in the sentence: 'Our team won the championship trophy.'",
        o: ['Team', 'Won', 'Our', 'Trophy'],
        a: 2,
        e: "'Our' is a possessive determiner — it comes before the noun 'team' to show ownership/belonging. Possessive determiners (my, your, his, her, its, our, their) always precede a noun, unlike possessive pronouns which stand alone.",
        h: 'Possessive determiners come immediately BEFORE a noun to show ownership.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence has a grammatical error involving pronoun case?',
        o: [
          "Between you and me, this plan won't work.",
          'She is taller than him.',
          'It was she who called the meeting.',
          'Give the prize to whomever deserves it most.',
        ],
        a: 3,
        e: "Option D has an error: 'whomever' should be 'whoever' because it functions as the SUBJECT of 'deserves' (whoever deserves it most), not as an object. The other sentences are grammatically correct in standard usage.",
        h: "Check whether 'whoever/whomever' is the SUBJECT (whoever) or OBJECT (whomever) of its own clause.",
        yr: 'SS2',
      },
      {
        q: "Which option correctly completes the sentence: 'The trophy was awarded to the team ___ had played the best throughout the season.'",
        o: ['whom', 'which', 'that', 'whose'],
        a: 2,
        e: "'That' is correct here — it can refer to 'the team' (a collective noun, often treated like a thing in restrictive relative clauses) as the SUBJECT of 'had played'. 'Whom' would require an object position, 'which' is less common for collective nouns referring to groups of people, and 'whose' indicates possession, which isn't needed here.",
        h: "Identify the relative pronoun functioning as the subject of 'had played' — referring to 'the team'.",
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Plural Forms of Nouns',
    topicCode: 'SS2-ENG-04',
    module: 'Lexis and Structure',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  English nouns form their <span class="learn-keyword">plurals</span> in several ways — most follow regular patterns, but many common and important nouns have irregular plural forms that must be memorised. Compound nouns also follow special rules. This topic covers all major pluralization patterns tested in WAEC.\n</div>\n\n<h3 class="learn-subheading">1. Regular Plural Formation</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Rule</th><th>Examples</th></tr></thead>\n    <tbody>\n      <tr><td>Add -s (most nouns)</td><td>book→books, cat→cats, table→tables</td></tr>\n      <tr><td>Add -es (nouns ending in -s, -ss, -sh, -ch, -x, -z)</td><td>bus→buses, glass→glasses, brush→brushes, watch→watches, box→boxes</td></tr>\n      <tr><td>-y → -ies (consonant + y)</td><td>baby→babies, city→cities, lady→ladies</td></tr>\n      <tr><td>-y → -ys (vowel + y)</td><td>boy→boys, day→days, key→keys</td></tr>\n      <tr><td>-f/-fe → -ves (some nouns)</td><td>leaf→leaves, knife→knives, wife→wives, life→lives</td></tr>\n      <tr><td>-f → -fs (other nouns)</td><td>roof→roofs, chief→chiefs, belief→beliefs</td></tr>\n      <tr><td>-o → -oes (some nouns)</td><td>potato→potatoes, tomato→tomatoes, hero→heroes</td></tr>\n      <tr><td>-o → -os (other nouns, often loanwords)</td><td>photo→photos, piano→pianos, radio→radios</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">2. Irregular Plurals</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Singular</th><th>Plural</th><th>Type of Change</th></tr></thead>\n    <tbody>\n      <tr><td>man</td><td>men</td><td>Vowel change</td></tr>\n      <tr><td>woman</td><td>women</td><td>Vowel change</td></tr>\n      <tr><td>child</td><td>children</td><td>Different ending</td></tr>\n      <tr><td>tooth</td><td>teeth</td><td>Vowel change</td></tr>\n      <tr><td>foot</td><td>feet</td><td>Vowel change</td></tr>\n      <tr><td>mouse</td><td>mice</td><td>Vowel change</td></tr>\n      <tr><td>ox</td><td>oxen</td><td>Old English plural</td></tr>\n      <tr><td>sheep</td><td>sheep</td><td>No change (zero plural)</td></tr>\n      <tr><td>deer</td><td>deer</td><td>No change (zero plural)</td></tr>\n      <tr><td>fish</td><td>fish (or fishes)</td><td>No change (zero plural common)</td></tr>\n      <tr><td>person</td><td>people / persons</td><td>Irregular</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">3. Foreign-Origin Plurals (Latin/Greek)</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Singular</th><th>Plural</th></tr></thead>\n    <tbody>\n      <tr><td>analysis</td><td>analyses</td></tr>\n      <tr><td>crisis</td><td>crises</td></tr>\n      <tr><td>criterion</td><td>criteria</td></tr>\n      <tr><td>phenomenon</td><td>phenomena</td></tr>\n      <tr><td>datum</td><td>data</td></tr>\n      <tr><td>curriculum</td><td>curricula</td></tr>\n      <tr><td>cactus</td><td>cacti</td></tr>\n      <tr><td>stimulus</td><td>stimuli</td></tr>\n      <tr><td>thesis</td><td>theses</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">4. Plurals of Compound Nouns</h3>\n<ul class="learn-list">\n  <li><strong>Pluralize the main (head) word:</strong> mother-in-law → mothers-in-law; man-of-war → men-of-war; passer-by → passers-by</li>\n  <li><strong>If no clear head noun, pluralize the end:</strong> breakdowns, grown-ups</li>\n  <li><strong>Closed compounds:</strong> pluralize as normal: bedrooms, classrooms, footballs</li>\n</ul>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> WAEC frequently tests irregular plurals (man→men, child→children, tooth→teeth) and compound noun plurals (mother-in-law → mothers-in-law, NOT mother-in-laws). Watch for words with ZERO plural (sheep, deer, fish — same singular/plural). \'Data\' and \'criteria\' are technically plural (datum, criterion are singular), though \'data\' is often used as singular in modern usage.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Regular plurals: -s, -es (after s/ss/sh/ch/x/z), -ies (consonant+y), -ves (some -f/-fe words). Irregular: man→men, child→children, tooth→teeth, foot→feet, mouse→mice, sheep→sheep (no change). Foreign plurals: criterion→criteria, datum→data, analysis→analyses. Compound nouns: pluralize the HEAD noun (mothers-in-law, passers-by).\n</div>\n  ',
    questions: [
      {
        q: "What is the correct plural form of 'child'?",
        o: ['Childs', 'Childes', 'Children', 'Childies'],
        a: 2,
        e: "'Children' is the irregular plural of 'child' — it uses a different ending entirely rather than following any regular pattern (-s, -es, -ies). This is one of the most common irregular plurals in English.",
        h: "'Child' has a completely irregular plural form — memorise it.",
        yr: 'SS2',
      },
      {
        q: "Choose the correct plural form of 'tomato':",
        o: ['Tomatos', 'Tomatoes', 'Tomatas', 'Tomatoies'],
        a: 1,
        e: "'Tomatoes' is correct — many nouns ending in a consonant + 'o' add '-es' for the plural (potato→potatoes, hero→heroes, echo→echoes). Note that some 'o'-ending nouns (often of foreign origin) simply add '-s' (photo→photos, piano→pianos).",
        h: "Some -o ending nouns (potato, tomato, hero) take '-es' in the plural.",
        yr: 'SS2',
      },
      {
        q: "What is the correct plural of 'mother-in-law'?",
        o: ['Mother-in-laws', 'Mothers-in-law', 'Mothers-in-laws', "Mother-in-laws's"],
        a: 1,
        e: "'Mothers-in-law' is correct — in compound nouns of this type, the main noun ('mother', the person being described) is pluralized, not the entire phrase or just the last word ('law'). Similarly: passers-by, men-of-war, sons-in-law.",
        h: "Pluralize the HEAD NOUN (the main person/thing): mother is the key word, not 'law'.",
        yr: 'SS2',
      },
      {
        q: 'Which of these nouns has NO plural form (the singular and plural are the same)?',
        o: ['Box', 'Sheep', 'Child', 'Mouse'],
        a: 1,
        e: "'Sheep' has a 'zero plural' — the singular and plural forms are identical: 'one sheep' and 'ten sheep'. Other examples of zero plurals include 'deer', 'fish' (often), 'series', and 'species'.",
        h: "This word's singular and plural forms are written exactly the same way.",
        yr: 'SS2',
      },
      {
        q: "What is the plural form of 'criterion'?",
        o: ['Criterions', 'Criteria', 'Criterias', 'Criteries'],
        a: 1,
        e: "'Criteria' is the plural of 'criterion', following the Greek pattern of '-on' → '-a' (similar to phenomenon→phenomena). 'Criterions' is incorrect in standard English.",
        h: "This is a Greek-origin word; '-on' endings often become '-a' in the plural.",
        yr: 'SS2',
      },
      {
        q: "Choose the correct plural of 'tooth':",
        o: ['Tooths', 'Toothes', 'Teeth', 'Teeths'],
        a: 2,
        e: "'Teeth' is the irregular plural of 'tooth', formed through a vowel change (oo→ee), similar to 'foot→feet' and 'goose→geese'. There is no '-s' or '-es' added.",
        h: "This irregular plural changes the vowel sound, like 'foot→feet'.",
        yr: 'SS2',
      },
      {
        q: "What is the plural form of 'knife'?",
        o: ['Knifes', 'Knives', 'Kniefs', 'Knifies'],
        a: 1,
        e: "'Knives' is correct — nouns ending in '-fe' often change to '-ves' in the plural (life→lives, wife→wives, knife→knives). However, not all '-f'/'-fe' nouns follow this pattern (roof→roofs, chief→chiefs are exceptions).",
        h: 'Some -f/-fe nouns change to -ves in the plural: life→lives, wife→wives.',
        yr: 'SS2',
      },
      {
        q: "Which sentence correctly uses the plural form of 'datum'?",
        o: [
          'The data was collected last week and is now being analysed.',
          'This data are interesting.',
          'The datas were collected from various sources.',
          'We have one data point and several datas.',
        ],
        a: 0,
        e: "While 'data' is technically the plural of 'datum', in modern usage 'data' is widely treated as a singular mass noun (taking 'was', 'is'). Option A reflects standard modern usage. 'Datas' is never correct.",
        h: "'Data' is the plural of 'datum' but is often used as a singular mass noun in modern English.",
        yr: 'SS2',
      },
      {
        q: "What is the correct plural of 'passer-by'?",
        o: ['Passer-bys', 'Passers-bys', 'Passers-by', "Passer-by's"],
        a: 2,
        e: "'Passers-by' is correct — 'passer' is the head noun (the person who passes), so it takes the plural marker, while 'by' remains unchanged. This follows the same pattern as 'mothers-in-law' and 'men-of-war'.",
        h: "'Passer' is the main noun (the person doing the action) — pluralize that part.",
        yr: 'SS2',
      },
      {
        q: 'Choose the sentence with the correctly pluralized irregular noun:',
        o: [
          'The farmer has ten oxes on his farm.',
          'The farmer has ten oxen on his farm.',
          'The farmer has ten oxs on his farm.',
          'The farmer has ten oxies on his farm.',
        ],
        a: 1,
        e: "'Oxen' is the correct, traditional irregular plural of 'ox' — one of the few surviving Old English '-en' plurals (similar to 'children'). 'Oxes' is incorrect.",
        h: "'Ox' takes an old-fashioned '-en' plural ending, like 'children'.",
        yr: 'SS2',
      },
      {
        q: "What is the correct plural of 'phenomenon'?",
        o: ['Phenomenons', 'Phenomenas', 'Phenomena', 'Phenomenae'],
        a: 2,
        e: "'Phenomena' is the correct plural of 'phenomenon', following the same Greek '-on'→'-a' pattern as 'criterion→criteria'. 'Phenomenons' is non-standard.",
        h: "Like 'criterion→criteria', this Greek-origin '-on' word becomes '-a' in the plural.",
        yr: 'SS2',
      },
      {
        q: "Which of the following words ending in '-y' forms its plural by simply adding '-s' (not changing 'y' to 'i')?",
        o: ['City', 'Lady', 'Boy', 'Baby'],
        a: 2,
        e: "'Boy' becomes 'boys' — when a noun ends in a VOWEL + 'y' (boy, day, key), simply add '-s'. When a noun ends in a CONSONANT + 'y' (city, lady, baby), change 'y' to 'i' and add '-es' (cities, ladies, babies).",
        h: "Check what comes BEFORE the 'y' — a vowel or a consonant?",
        yr: 'SS2',
      },
      {
        q: "Choose the correct plural of 'analysis':",
        o: ['Analysises', 'Analysis', 'Analyses', 'Analysies'],
        a: 2,
        e: "'Analyses' is correct — Greek-origin nouns ending in '-is' typically change to '-es' in the plural (crisis→crises, thesis→theses, basis→bases, analysis→analyses). The pronunciation also changes (the final syllable rhymes with 'ease').",
        h: "Words ending in '-is' (analysis, crisis, thesis) become '-es' in the plural.",
        yr: 'SS2',
      },
      {
        q: "Which sentence correctly uses a noun with a 'zero plural' (same form for singular and plural)?",
        o: [
          'I caught three fish at the river yesterday.',
          'I caught three fishes at the river yesterday.',
          'I caught three fishs at the river yesterday.',
          'I caught three fishies at the river yesterday.',
        ],
        a: 0,
        e: "'Fish' commonly has a zero plural — 'three fish' is correct and most common. ('Fishes' is sometimes used when referring to different species/types of fish, but 'fish' as a simple plural is standard.)",
        h: "'Fish' often keeps the same form for singular and plural — like 'sheep' and 'deer'.",
        yr: 'SS2',
      },
      {
        q: "What is the plural form of 'man-of-war' (a type of ship)?",
        o: ['Man-of-wars', 'Men-of-war', 'Mans-of-war', 'Men-of-wars'],
        a: 1,
        e: "'Men-of-war' is correct — 'man' is the head noun being pluralized to its irregular form 'men', while 'of-war' remains unchanged, following the same compound-noun pattern as 'mother-in-law → mothers-in-law'.",
        h: "'Man' becomes 'men' (irregular); 'of-war' stays the same.",
        yr: 'SS2',
      },
      {
        q: "Choose the correct plural of 'curriculum':",
        o: ['Curriculums', 'Curricula', 'Curriculae', "Curriculae's"],
        a: 1,
        e: "'Curricula' is the traditional Latin-origin plural of 'curriculum' (similar to 'datum→data', 'medium→media'). 'Curriculums' is also increasingly accepted in modern English as an anglicized plural, but 'curricula' is the more traditional/formal form.",
        h: "This Latin '-um' ending word becomes '-a' in its traditional plural form.",
        yr: 'SS2',
      },
      {
        q: "Which word correctly forms its plural by adding '-es' because it ends in a sibilant sound (s, sh, ch, x, z)?",
        o: ['Book → books', 'Watch → watches', 'Pen → pens', 'Car → cars'],
        a: 1,
        e: "'Watch' ends in 'ch' (a sibilant/hissing sound), so it adds '-es' to form 'watches'. This rule applies to nouns ending in -s, -ss, -sh, -ch, -x, and -z (bus→buses, glass→glasses, brush→brushes, box→boxes, quiz→quizzes).",
        h: "Nouns ending in s, ss, sh, ch, x, z add '-es' for the plural.",
        yr: 'SS2',
      },
      {
        q: "What is the correct plural of 'person'?",
        o: [
          'Persons (used for a specific/limited number) or people (general plural)',
          'Persones',
          'Peoples (always)',
          "Persones's",
        ],
        a: 0,
        e: "Both 'people' and 'persons' can be plurals of 'person', but they're used differently: 'people' is the general, more common plural (many people attended); 'persons' is used in formal/legal contexts for a specific countable number (three persons were arrested).",
        h: "'People' = general plural; 'persons' = formal/legal, often with specific numbers.",
        yr: 'SS2',
      },
      {
        q: "Choose the correctly pluralized compound noun: 'The committee appointed three new ___ (vice-chairman) to assist the chairman.'",
        o: ['Vice-chairmans', 'Vice-chairmen', 'Vices-chairman', "Vice-chairman's"],
        a: 1,
        e: "'Vice-chairmen' is correct — 'chairman' (the head noun referring to the person) takes its irregular plural form 'chairmen', while 'vice-' (the prefix) remains unchanged.",
        h: "'Chairman' becomes 'chairmen' (irregular, like 'man→men'); 'vice-' doesn't change.",
        yr: 'SS2',
      },
      {
        q: "Which sentence correctly demonstrates the plural of 'species' (a noun ending in -es that does NOT change in the plural)?",
        o: [
          'Scientists have discovered three new species of frogs.',
          'Scientists have discovered three new specieses of frogs.',
          'Scientists have discovered three new specie of frogs.',
          "Scientists have discovered three new species's of frogs.",
        ],
        a: 0,
        e: "'Species' has a zero plural — 'one species' and 'three species' use the same form. 'Specie' (singular, without 's') actually refers to coined money, not a biological category, so it would be incorrect here.",
        h: "'Species' (like 'sheep', 'fish') has the same form for singular and plural.",
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Adverbs and Adverbial Phrases',
    topicCode: 'SS2-ENG-05',
    module: 'Lexis and Structure',
    term: 'First Term',
    contentHTML:
      "\n<div class=\"learn-intro\">\n  An <span class=\"learn-keyword\">adverb</span> is a word that modifies a verb, adjective, another adverb, or an entire sentence — typically telling us HOW, WHEN, WHERE, HOW OFTEN, or TO WHAT DEGREE something happens. An <span class=\"learn-keyword\">adverbial phrase</span> is a group of words that together perform the function of an adverb.\n</div>\n\n<h3 class=\"learn-subheading\">1. Types of Adverbs</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Type</th><th>Question Answered</th><th>Examples</th></tr></thead>\n    <tbody>\n      <tr><td>Manner</td><td>How?</td><td>quickly, carefully, well, badly</td></tr>\n      <tr><td>Place</td><td>Where?</td><td>here, there, everywhere, abroad</td></tr>\n      <tr><td>Time</td><td>When?</td><td>now, yesterday, soon, later</td></tr>\n      <tr><td>Frequency</td><td>How often?</td><td>always, often, rarely, never</td></tr>\n      <tr><td>Degree</td><td>To what extent?</td><td>very, extremely, quite, almost</td></tr>\n      <tr><td>Reason/Purpose</td><td>Why?</td><td>therefore, hence, consequently</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Formation of Adverbs</h3>\n<ul class=\"learn-list\">\n  <li>Most adverbs of manner are formed by adding '-ly' to an adjective: quick → quickly, careful → carefully</li>\n  <li>Adjectives ending in '-y' change to '-ily': happy → happily, easy → easily</li>\n  <li>Adjectives ending in '-le' drop the 'e' and add '-y': simple → simply, gentle → gently</li>\n  <li><strong>Irregular adverbs:</strong> good → well; fast → fast (same form); hard → hard (same form, NOT 'hardly' which means 'barely')</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Adverbial Phrases</h3>\n<p class=\"learn-p\">An adverbial phrase is a group of words (without its own subject-verb structure) that functions as an adverb — modifying the verb by answering how, when, where, why, etc.</p>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Adverbial Phrase</th><th>Type</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>'in the morning'</td><td>Time</td><td>'She exercises in the morning.'</td></tr>\n      <tr><td>'with great care'</td><td>Manner</td><td>'He repaired the watch with great care.'</td></tr>\n      <tr><td>'at the market'</td><td>Place</td><td>'They met at the market.'</td></tr>\n      <tr><td>'because of the rain'</td><td>Reason</td><td>'The match was cancelled because of the rain.'</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Position of Adverbs</h3>\n<ul class=\"learn-list\">\n  <li><strong>Adverbs of manner</strong> typically come AFTER the verb or object: 'She sings beautifully.'</li>\n  <li><strong>Adverbs of frequency</strong> typically come BEFORE the main verb but AFTER 'be': 'He always arrives early.' / 'He is always late.'</li>\n  <li><strong>Adverbs of time/place</strong> often come at the END of the sentence: 'We will meet tomorrow.'</li>\n</ul>\n\n<h3 class=\"learn-subheading\">5. Comparative and Superlative Adverbs</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Adverb</th><th>Comparative</th><th>Superlative</th></tr></thead>\n    <tbody>\n      <tr><td>fast</td><td>faster</td><td>fastest</td></tr>\n      <tr><td>carefully</td><td>more carefully</td><td>most carefully</td></tr>\n      <tr><td>well</td><td>better</td><td>best</td></tr>\n      <tr><td>badly</td><td>worse</td><td>worst</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">6. Vocabulary: Words of Colour, Smell, and Taste</h3>\n<p class=\"learn-p\">Descriptive vocabulary related to senses: <strong>verdant</strong> (green with vegetation), <strong>pungent</strong> (a sharp, strong smell or taste), <strong>vapid</strong> (lacking taste/flavour, dull), <strong>serene</strong> (calm and peaceful — often used of colour/scenery), <strong>trite</strong> (overused, lacking originality).</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> 'Hardly' does NOT mean 'in a hard manner' — it means 'barely/almost not' (e.g., 'He hardly studies' = he rarely studies). 'Hard' as an adverb means 'with effort' (e.g., 'He works hard'). Adverbs of frequency go BEFORE the main verb but AFTER 'be' verbs. Don't confuse adjectives (describe nouns) with adverbs (describe verbs/adjectives/adverbs).\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Adverbs modify verbs, adjectives, or other adverbs — types include manner, place, time, frequency, degree, reason. Most are formed with '-ly'. Adverbial phrases (groups of words without subject-verb) perform the same function. Position varies by type: manner (after verb), frequency (before main verb), time/place (often at sentence end). 'Hardly' ≠ 'hard' + 'ly' in meaning.\n</div>\n  ",
    questions: [
      {
        q: "Which type of adverb answers the question 'How often?'",
        o: ['Adverb of manner', 'Adverb of degree', 'Adverb of frequency', 'Adverb of place'],
        a: 2,
        e: "Adverbs of frequency (always, often, sometimes, rarely, never, usually) answer the question 'How often?' — they tell us how regularly an action happens.",
        h: "Think of words like 'always', 'often', 'never' — they tell HOW OFTEN something happens.",
        yr: 'SS2',
      },
      {
        q: "What is the adverb form of the adjective 'happy'?",
        o: ['Happyly', 'Happily', 'Happly', 'Happness'],
        a: 1,
        e: "'Happily' is correct — adjectives ending in '-y' change the 'y' to 'i' before adding '-ly' (happy→happily, easy→easily, busy→busily).",
        h: "Adjectives ending in '-y' change 'y' to 'i' before adding '-ly'.",
        yr: 'SS2',
      },
      {
        q: 'Choose the sentence with the CORRECT adverb form:',
        o: [
          'She drives very careful on wet roads.',
          'He sings beautiful at the concert.',
          'The students worked hardly to pass the exam.',
          'She sings beautifully at every concert.',
        ],
        a: 3,
        e: "'Beautifully' (adverb, modifying 'sings') is correct in option D. Options A and B incorrectly use adjectives ('careful', 'beautiful') where adverbs are needed. Option C misuses 'hardly' (meaning 'barely') instead of 'hard' (meaning 'with effort').",
        h: 'Look for the option where an adverb (not adjective) correctly modifies a verb.',
        yr: 'SS2',
      },
      {
        q: "What does 'hardly' mean in the sentence 'He hardly studies for his exams'?",
        o: [
          'He studies with great effort and difficulty',
          'He barely/almost never studies',
          'He studies very hard',
          'He studies in a hard, rigid manner',
        ],
        a: 1,
        e: "'Hardly' means 'barely' or 'almost not at all' — it is NOT the adverb form of 'hard' (which would mean 'with great effort'). 'He hardly studies' means he rarely or barely studies — the opposite of working hard!",
        h: "'Hardly' is a 'false friend' — it does NOT mean 'in a hard/effortful way'.",
        yr: 'SS2',
      },
      {
        q: "Identify the ADVERBIAL PHRASE of MANNER in: 'He repaired the watch with great care.'",
        o: ['He repaired', 'The watch', 'With great care', 'Repaired the watch'],
        a: 2,
        e: "'With great care' is an adverbial phrase of manner — it describes HOW he repaired the watch. Adverbial phrases of manner often begin with 'with' followed by a noun describing the quality of the action.",
        h: 'Which group of words tells us HOW he repaired the watch?',
        yr: 'SS2',
      },
      {
        q: "Where does the adverb of frequency typically go in the sentence: 'He ___ (always) arrives late'?",
        o: [
          'At the very beginning of the sentence: Always he arrives late.',
          'Before the main verb: He always arrives late.',
          'At the very end of the sentence: He arrives late always.',
          'After the object: He arrives late always.',
        ],
        a: 1,
        e: "Adverbs of frequency (always, often, never, usually) typically come BEFORE the main verb: 'He always arrives late.' If the sentence used 'is' (a 'be' verb), the adverb would come AFTER it: 'He is always late.'",
        h: "Adverbs of frequency go BEFORE the main verb (but after 'be' verbs).",
        yr: 'SS2',
      },
      {
        q: "What is the comparative form of the adverb 'well'?",
        o: ['Weller', 'More well', 'Better', 'Best'],
        a: 2,
        e: "'Better' is the irregular comparative form of 'well' (just as 'good' has the comparative 'better'). 'Best' is the superlative form. 'Weller' and 'more well' are incorrect.",
        h: "'Well' has an irregular comparative, like 'good→better'.",
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly identifies an adverbial phrase of REASON?',
        o: [
          'She left the room quickly.',
          'The match was cancelled because of the heavy rain.',
          'He will arrive tomorrow morning.',
          'The book is on the table.',
        ],
        a: 1,
        e: "'Because of the heavy rain' is an adverbial phrase of reason — it explains WHY the match was cancelled. Adverbial phrases of reason often begin with 'because of', 'due to', or 'owing to'.",
        h: "Which phrase explains WHY something happened (the match's cancellation)?",
        yr: 'SS2',
      },
      {
        q: "What is the adverb form of 'gentle'?",
        o: ['Gentlely', 'Gently', 'Gentley', 'Gentlily'],
        a: 1,
        e: "'Gently' is correct — adjectives ending in '-le' drop the final 'e' and add '-y' (gentle→gently, simple→simply, possible→possibly), NOT '-ely'.",
        h: "Adjectives ending in '-le' drop the 'e' and add just '-y'.",
        yr: 'SS2',
      },
      {
        q: "Choose the sentence that correctly uses 'fast' as an adverb:",
        o: [
          'He runs fastly to catch the bus.',
          'He runs fast to catch the bus.',
          'He runs fasterly than his brother.',
          'He runs in a fast manner.',
        ],
        a: 1,
        e: "'Fast' has the same form as both an adjective AND an adverb — 'fastly' is NOT a word. 'He runs fast' correctly uses 'fast' as an adverb describing how he runs. Other examples of adjective=adverb words: 'hard work' (adj) vs 'work hard' (adv).",
        h: "'Fast' is one of the adverbs that does NOT add '-ly' — same form as the adjective.",
        yr: 'SS2',
      },
      {
        q: "Identify the type of adverbial phrase in: 'They will meet at the conference centre.'",
        o: [
          'Adverbial phrase of time',
          'Adverbial phrase of place',
          'Adverbial phrase of manner',
          'Adverbial phrase of reason',
        ],
        a: 1,
        e: "'At the conference centre' is an adverbial phrase of PLACE — it tells us WHERE they will meet. Adverbial phrases of place often begin with prepositions like 'at', 'in', 'on', 'near'.",
        h: 'This phrase answers the question WHERE.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly places the adverb of degree?',
        o: [
          'She is very intelligent student.',
          'She is a very intelligent student.',
          'She is intelligent very student.',
          'Very she is an intelligent student.',
        ],
        a: 1,
        e: "'Very' (adverb of degree) correctly modifies the adjective 'intelligent', and the article 'a' must come before the noun phrase: 'a very intelligent student'. Adverbs of degree typically come directly before the adjective/adverb they modify.",
        h: 'Adverbs of degree (very, quite, extremely) come directly BEFORE the adjective they modify.',
        yr: 'SS2',
      },
      {
        q: "What part of speech is 'verdant' in the sentence 'They walked through the verdant valley'?",
        o: ['Adverb', 'Adjective (describing the valley as green/lush)', 'Noun', 'Preposition'],
        a: 1,
        e: "'Verdant' is an adjective meaning green with vegetation, lush. It describes the noun 'valley'. This vocabulary word relates to colour descriptions in the 'words of colour' category.",
        h: "'Verdant' describes a noun (valley) — what part of speech describes nouns?",
        yr: 'SS2',
      },
      {
        q: "Which word describes a smell or taste that is 'sharp and strong, often unpleasantly so'?",
        o: ['Vapid', 'Serene', 'Pungent', 'Trite'],
        a: 2,
        e: "'Pungent' describes a sharp, strong smell or taste — often associated with spices, smoke, or strong odours. Example: 'The pungent smell of the spice market filled the air.'",
        h: 'This word describes a strong, sharp smell or taste — often spicy or smoky.',
        yr: 'SS2',
      },
      {
        q: "'Vapid' is best used to describe:",
        o: [
          'A vibrant, exciting flavour',
          'Something dull, flat, or lacking in flavour/interest',
          'A loud, sharp sound',
          'A bright colour',
        ],
        a: 1,
        e: "'Vapid' means lacking flavour, liveliness, or interest — dull and uninspiring. Example: 'The vapid conversation bored everyone at the dinner table.'",
        h: "'Vapid' is the opposite of exciting or flavourful — it means dull/flat.",
        yr: 'SS2',
      },
      {
        q: "Choose the sentence with correct adverb usage: 'The children behaved ___ during the assembly.'",
        o: ['good', 'well', 'gooder', 'best (without comparison)'],
        a: 1,
        e: "'Well' is the adverb form corresponding to the adjective 'good' — it correctly modifies the verb 'behaved'. 'Good' is an adjective and cannot directly modify a verb in standard English (though it's common in informal speech).",
        h: "'Good' is an adjective; its adverb counterpart is irregular.",
        yr: 'SS2',
      },
      {
        q: "Identify the adverbial phrase of TIME in: 'We will discuss this matter after the meeting.'",
        o: ['We will discuss', 'This matter', 'After the meeting', 'Discuss this matter'],
        a: 2,
        e: "'After the meeting' is an adverbial phrase of time — it tells us WHEN the discussion will happen. Adverbial phrases of time often use prepositions like 'after', 'before', 'during', 'since'.",
        h: 'This phrase tells us WHEN the discussion will take place.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence demonstrates the CORRECT comparative form of an adverb of manner?',
        o: [
          'She works more carefully than her colleague.',
          'She works carefuller than her colleague.',
          'She works most carefully than her colleague.',
          'She works carefully more than her colleague.',
        ],
        a: 0,
        e: "'More carefully' is correct — adverbs ending in '-ly' (with two or more syllables) form their comparative with 'more' + adverb, not by adding '-er'. 'Carefuller' is incorrect.",
        h: "Multi-syllable '-ly' adverbs use 'more/most' for comparative/superlative forms, not '-er/-est'.",
        yr: 'SS2',
      },
      {
        q: "'Serene' most accurately describes:",
        o: [
          'A chaotic, noisy environment',
          'A calm, peaceful state or scene',
          'A strong unpleasant smell',
          'An overused, unoriginal idea',
        ],
        a: 1,
        e: "'Serene' means calm, peaceful, and untroubled — often used to describe scenery, water, or a person's expression. Example: 'The lake was serene at sunrise.'",
        h: 'This word describes calmness and peace, often used for scenery.',
        yr: 'SS2',
      },
      {
        q: "Which underlined word in the sentence 'She speaks English fluently and confidently' functions as an adverb of manner?",
        o: ['She', 'Speaks', 'Fluently and confidently', 'English'],
        a: 2,
        e: "'Fluently and confidently' are both adverbs of manner — they describe HOW she speaks English. Both end in '-ly', the most common adverb-forming suffix.",
        h: 'These words describe HOW she speaks — the manner of her speaking.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Oral English: Consonant Sounds, Clusters, and Word Stress',
    topicCode: 'SS2-ENG-06',
    module: 'Oral English',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Oral English</span> covers the spoken aspects of the language — the sounds (phonemes), how they combine into clusters, and how stress patterns affect pronunciation and meaning. WAEC tests these areas through written questions about sound classification, transcription, and stress placement.\n</div>\n\n<h3 class="learn-subheading">1. Voiced and Voiceless Consonants</h3>\n<p class="learn-p">Consonant sounds are classified as <strong>voiced</strong> (vocal cords vibrate) or <strong>voiceless</strong> (vocal cords do not vibrate). Many consonants come in voiced/voiceless PAIRS made at the same place in the mouth:</p>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Voiceless</th><th>Voiced</th><th>Example Pair</th></tr></thead>\n    <tbody>\n      <tr><td>/p/</td><td>/b/</td><td>pin / bin</td></tr>\n      <tr><td>/t/</td><td>/d/</td><td>tin / din</td></tr>\n      <tr><td>/k/</td><td>/g/</td><td>cap / gap</td></tr>\n      <tr><td>/f/</td><td>/v/</td><td>fan / van</td></tr>\n      <tr><td>/s/</td><td>/z/</td><td>sip / zip</td></tr>\n      <tr><td>/θ/ (thin)</td><td>/ð/ (this)</td><td>thigh / thy</td></tr>\n      <tr><td>/ʃ/ (ship)</td><td>/ʒ/ (measure)</td><td>—</td></tr>\n      <tr><td>/tʃ/ (chip)</td><td>/dʒ/ (judge)</td><td>—</td></tr>\n    </tbody>\n  </table>\n</div>\n<p class="learn-p"><strong>Always voiced (no voiceless pair):</strong> /m/, /n/, /ŋ/ (sing), /l/, /r/, /w/, /j/</p>\n\n<h3 class="learn-subheading">2. Consonant Clusters</h3>\n<p class="learn-p">A consonant cluster is a sequence of two or more consonant sounds occurring together with no vowel between them.</p>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Cluster Size</th><th>Examples</th></tr></thead>\n    <tbody>\n      <tr><td>Two consonants</td><td>stop (/st/), play (/pl/), free (/fr/)</td></tr>\n      <tr><td>Three consonants</td><td>street (/str/), splash (/spl/), scream (/skr/)</td></tr>\n      <tr><td>Four consonants</td><td>texts (/ksts/), twelfths (/lfθs/)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">3. Word Stress Rules</h3>\n<ul class="learn-list">\n  <li><strong>Two-syllable nouns/adjectives:</strong> stress usually on the FIRST syllable — TAble, HAPpy, PRESent (noun)</li>\n  <li><strong>Two-syllable verbs:</strong> stress usually on the SECOND syllable — preSENT (verb), beGIN, deCIDE</li>\n  <li><strong>Words ending in \'-tion\', \'-sion\', \'-ic\', \'-ical\':</strong> stress on the syllable BEFORE the suffix — eduCAtion, geoGRAPHic</li>\n  <li><strong>Compound nouns:</strong> stress usually on the FIRST part — BLACKboard, GREENhouse</li>\n  <li><strong>Compound verbs/adjectives:</strong> stress on the SECOND part — underSTAND, well-KNOWN</li>\n</ul>\n\n<h3 class="learn-subheading">4. Stress and Word Class (Noun/Verb Pairs)</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Word</th><th>As Noun (stress 1st syllable)</th><th>As Verb (stress 2nd syllable)</th></tr></thead>\n    <tbody>\n      <tr><td>record</td><td>RECord (an album)</td><td>reCORD (to register/save)</td></tr>\n      <tr><td>present</td><td>PREsent (a gift)</td><td>preSENT (to give/show)</td></tr>\n      <tr><td>contract</td><td>CONtract (an agreement)</td><td>conTRACT (to shrink/agree)</td></tr>\n      <tr><td>object</td><td>OBject (a thing)</td><td>obJECT (to protest)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">5. Emphatic Stress</h3>\n<p class="learn-p">Emphatic stress is extra stress placed on a particular word in a sentence to highlight, contrast, or emphasise meaning. Changing which word receives emphatic stress can change the meaning/focus of a sentence:</p>\n<ul class="learn-list">\n  <li>\'<strong>I</strong> didn\'t take your book\' (emphasis: not me — someone else did)</li>\n  <li>\'I didn\'t take <strong>your</strong> book\' (emphasis: not yours — a different book)</li>\n  <li>\'I didn\'t <strong>take</strong> your book\' (emphasis: I didn\'t take it — maybe I borrowed it)</li>\n</ul>\n\n<h3 class="learn-subheading">6. Vocabulary: Words of the Week (Selection)</h3>\n<p class="learn-p"><strong>partisan</strong> (strongly supporting a particular party/cause), <strong>vivacious</strong> (attractively lively and animated), <strong>surmise</strong> (to guess based on incomplete info), <strong>stoic</strong> (enduring pain/hardship without complaint), <strong>paragon</strong> (a model of excellence), <strong>vilify</strong> (to speak/write about in an abusive way), <strong>exonerate</strong> (to clear of blame), <strong>surreptitious</strong> (secret/stealthy), <strong>incendiary</strong> (tending to cause anger/conflict), <strong>querulous</strong> (complaining in a whining manner).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Noun-verb pairs often shift stress: NOUN = stress on FIRST syllable; VERB = stress on SECOND syllable (RECord vs reCORD). Words ending in -tion/-sion/-ic stress the syllable BEFORE the suffix. Always-voiced consonants with no voiceless pair: m, n, ŋ, l, r, w, j. Emphatic stress changes the FOCUS/meaning of a sentence depending on which word is stressed.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Consonants: voiced (vocal cords vibrate) vs voiceless pairs (p/b, t/d, k/g, f/v, s/z). Clusters: 2-4 consonants together (st-, str-, -ksts). Word stress: nouns stress 1st syllable, verbs stress 2nd (often); -tion/-sion/-ic stress syllable before suffix; compound nouns stress 1st part, compound verbs stress 2nd part. Emphatic stress highlights specific words to change meaning/focus.\n</div>\n  ',
    questions: [
      {
        q: 'Which pair of words demonstrates a VOICED and VOICELESS consonant contrast?',
        o: ['Cat and bat', 'Pin and bin', 'Sit and set', 'Run and ran'],
        a: 1,
        e: "'Pin' (/p/, voiceless) and 'bin' (/b/, voiced) demonstrate the voiceless/voiced pair /p/-/b/, both made by closing the lips (bilabial). The other pairs do not represent a single consonant contrast in this clean way.",
        h: 'Look for two words that differ by ONE consonant sound, one voiced and one voiceless, made the same way.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following consonant sounds has NO voiceless counterpart (it is always voiced)?',
        o: ['/p/', '/k/', '/m/', '/s/'],
        a: 2,
        e: "/m/ (as in 'man') is always voiced — there is no voiceless equivalent in English. Similarly, /n/, /ŋ/ (sing), /l/, /r/, /w/, and /j/ are always voiced consonants with no voiceless pairs.",
        h: 'Nasal sounds (m, n) and approximants (l, r, w, y) are always voiced.',
        yr: 'SS2',
      },
      {
        q: "How many consonant sounds form the cluster at the beginning of the word 'street'?",
        o: ['One', 'Two', 'Three', 'Four'],
        a: 2,
        e: "'Street' begins with a three-consonant cluster: /s/, /t/, /r/ — all before the vowel sound. This is an example of a three-consonant cluster, common in words like 'spring', 'scream', and 'splash'.",
        h: 'Count the consonant SOUNDS before the vowel: s-t-r-eet.',
        yr: 'SS2',
      },
      {
        q: 'Where is the stress typically placed in TWO-SYLLABLE NOUNS in English?',
        o: [
          'On the second syllable',
          'On the first syllable',
          'Equally on both syllables',
          'There is no general rule for nouns',
        ],
        a: 1,
        e: 'Two-syllable NOUNS in English typically have stress on the FIRST syllable (e.g., TAble, HAPpy as adjective, PREsent as a gift). This contrasts with two-syllable VERBS, which often stress the SECOND syllable.',
        h: "Think of 'TAble', 'PENcil' — where is the stress?",
        yr: 'SS2',
      },
      {
        q: "In the word pair 'record' (noun) and 'record' (verb), how does the stress pattern differ?",
        o: [
          'Both forms have identical stress',
          'As a noun, stress is on the first syllable (RECord); as a verb, stress is on the second syllable (reCORD)',
          'As a noun, stress is on the second syllable; as a verb, on the first',
          'Stress placement has no effect on meaning for this word',
        ],
        a: 1,
        e: "'RECord' (noun, stress on first syllable) refers to an album or document. 'reCORD' (verb, stress on second syllable) means to register or save. This noun-verb stress shift pattern applies to many two-syllable words (present, contract, object, produce).",
        h: 'Noun = stress FIRST syllable; Verb = stress SECOND syllable (a common pattern).',
        yr: 'SS2',
      },
      {
        q: 'Which suffix typically causes stress to fall on the syllable IMMEDIATELY BEFORE it?',
        o: ['-ful', '-ness', '-tion', '-er'],
        a: 2,
        e: "'-tion' (and similarly '-sion', '-ic', '-ical') causes stress to fall on the syllable immediately before the suffix: eduCAtion, informAtion, geoGRAPHic. This is a reliable stress-placement rule for these common suffixes.",
        h: "Think of 'eduCAtion', 'informAtion' — where is the stress relative to '-tion'?",
        yr: 'SS2',
      },
      {
        q: 'What is the function of EMPHATIC STRESS in spoken English?',
        o: [
          'It has no function — it is random',
          'It marks the END of a sentence',
          'It highlights a particular word to emphasise meaning, contrast, or focus',
          'It is only used in questions',
        ],
        a: 2,
        e: "Emphatic stress places extra emphasis on a specific word to highlight contrast or important information. For example, 'I SAID I would help, not that I WOULD do it alone' — the stressed words signal what the speaker wants to emphasise.",
        h: 'Emphatic stress changes which part of the sentence the LISTENER focuses on.',
        yr: 'SS2',
      },
      {
        q: "Which word means 'a model or example of excellence'?",
        o: ['Vilify', 'Paragon', 'Surmise', 'Querulous'],
        a: 1,
        e: "'Paragon' refers to a model of excellence — a perfect example of a particular quality. Example: 'She is a paragon of patience, never losing her temper.'",
        h: 'This word describes a perfect EXAMPLE of a quality.',
        yr: 'SS2',
      },
      {
        q: "To 'exonerate' someone means to:",
        o: [
          'Accuse them of a crime',
          'Clear them of blame or wrongdoing',
          'Praise them publicly',
          'Punish them severely',
        ],
        a: 1,
        e: "'Exonerate' means to officially clear someone of blame or a charge — to declare them not guilty. Example: 'New evidence exonerated the accused man after ten years in prison.'",
        h: 'This word relates to being found NOT GUILTY or cleared of blame.',
        yr: 'SS2',
      },
      {
        q: "Which word describes someone who 'endures pain or hardship without complaining'?",
        o: ['Vivacious', 'Stoic', 'Querulous', 'Partisan'],
        a: 1,
        e: "'Stoic' describes a person who endures suffering or hardship without showing their feelings or complaining — calm and unemotional in the face of difficulty. Example: 'He remained stoic despite the devastating news.'",
        h: 'This word relates to enduring hardship calmly, without complaint.',
        yr: 'SS2',
      },
      {
        q: "'Vivacious' best describes a person who is:",
        o: [
          'Quiet and reserved',
          'Attractively lively, energetic, and animated',
          'Always complaining',
          'Secretive and stealthy',
        ],
        a: 1,
        e: "'Vivacious' describes someone full of life, energy, and high spirits in an attractive way. Example: 'Her vivacious personality made her popular at every gathering.'",
        h: 'This word relates to being lively and energetic in an attractive way.',
        yr: 'SS2',
      },
      {
        q: "To 'vilify' someone means to:",
        o: [
          'Praise them excessively',
          'Speak or write about them in an abusively disparaging manner',
          'Help them improve their reputation',
          'Forgive them for a mistake',
        ],
        a: 1,
        e: "'Vilify' means to attack someone's character through harsh, abusive criticism — to defame or slander. Example: 'The press vilified the politician after the scandal broke.'",
        h: 'This word relates to harshly criticising or defaming someone.',
        yr: 'SS2',
      },
      {
        q: "Which word means 'done secretly or stealthily, often because illicit'?",
        o: ['Surreptitious', 'Querulous', 'Stoic', 'Vivacious'],
        a: 0,
        e: "'Surreptitious' describes actions done secretly, often to avoid being noticed because they may be wrong or forbidden. Example: 'He took a surreptitious glance at his phone during the meeting.'",
        h: 'This word describes SECRET, stealthy actions, often slightly suspicious.',
        yr: 'SS2',
      },
      {
        q: "An 'incendiary' speech or remark is one that:",
        o: [
          'Calms a tense situation down',
          'Tends to provoke anger, conflict, or violence',
          'Is boring and uninteresting',
          'Is highly technical and difficult to understand',
        ],
        a: 1,
        e: "'Incendiary' (literally relating to setting fires) figuratively describes speech or actions likely to provoke anger, hostility, or conflict. Example: 'His incendiary comments about other ethnic groups sparked outrage.'",
        h: 'This word relates to causing CONFLICT or ANGER, like setting something on fire.',
        yr: 'SS2',
      },
      {
        q: "Which word describes a person who 'complains a lot in a whining, irritable manner'?",
        o: ['Partisan', 'Paragon', 'Querulous', 'Stoic'],
        a: 2,
        e: "'Querulous' describes someone who frequently complains in a whining, irritable way. Example: 'The querulous customer complained about every small detail of the service.'",
        h: 'This word describes someone who is WHINY and complains a lot.',
        yr: 'SS2',
      },
      {
        q: "If a person is described as 'partisan', it means they:",
        o: [
          'Are neutral and unbiased on all issues',
          'Strongly support a particular party, cause, or viewpoint, often without considering opposing views fairly',
          'Are very calm under pressure',
          'Speak very loudly',
        ],
        a: 1,
        e: "'Partisan' describes strong, often biased, support for a particular party or cause — someone who is partisan tends to favour one side without considering the other fairly. Example: 'The debate became too partisan to reach any agreement.'",
        h: 'This word relates to STRONG, biased support for one side/party.',
        yr: 'SS2',
      },
      {
        q: "How many syllables does the word 'examination' have, and where does the stress fall?",
        o: [
          '3 syllables, stress on the 1st',
          '4 syllables, stress on the 1st',
          '5 syllables, stress on the 4th syllable (before -tion)',
          '5 syllables, stress on the 1st syllable',
        ],
        a: 2,
        e: "'Examination' has 5 syllables: ex-a-mi-NA-tion. Following the rule that words ending in '-tion' stress the syllable immediately before the suffix, the stress falls on the 4th syllable 'NA'.",
        h: "Apply the '-tion' rule: stress falls on the syllable just before '-tion'.",
        yr: 'SS2',
      },
      {
        q: 'Which sentence demonstrates emphatic stress used to correct a misunderstanding (the speaker, not someone else, did the action)?',
        o: [
          "I did call you yesterday (with stress on 'did' to confirm the action happened, contradicting a denial).",
          'I called you yesterday (no special emphasis).',
          'Did I call you yesterday?',
          'I will call you tomorrow.',
        ],
        a: 0,
        e: "Stressing 'did' in 'I DID call you yesterday' emphasises that the action DID occur, often used to contradict a claim that it didn't happen. This is a common use of emphatic stress to assert or confirm something against doubt.",
        h: 'Look for the sentence where emphasis would CONFIRM that an action happened, against doubt.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is an example of a word with a FOUR-consonant cluster?',
        o: ['Spring', 'Street', 'Texts (/teksts/)', 'Play'],
        a: 2,
        e: "'Texts' contains a four-consonant cluster at the end: /ksts/ (k-s-t-s). 'Spring' and 'street' have three-consonant clusters (/spr/, /str/), and 'play' has a two-consonant cluster (/pl/).",
        h: "Count the consonant sounds at the END of 'texts': /k/-/s/-/t/-/s/.",
        yr: 'SS2',
      },
      {
        q: "Where does the stress typically fall in COMPOUND NOUNS like 'blackboard' or 'greenhouse'?",
        o: [
          'On the second part of the compound',
          'On the first part of the compound',
          'Equally on both parts',
          'There is no consistent pattern',
        ],
        a: 1,
        e: 'Compound NOUNS typically stress the FIRST part: BLACKboard, GREENhouse, BOOKshop. This contrasts with compound verbs and adjectives, which often stress the second part (underSTAND, well-KNOWN).',
        h: 'Compound nouns: stress the FIRST element (BLACKboard, not blackBOARD).',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Summary Writing Skills',
    topicCode: 'SS2-ENG-07',
    module: 'Comprehension and Summary',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Summary writing</span> is the skill of condensing a longer passage into a shorter version, retaining only the most important points in the writer\'s own words. It is a heavily examined skill in WAEC English, testing comprehension, selection, and concise expression.\n</div>\n\n<h3 class="learn-subheading">1. Meaning and Features of a Good Summary</h3>\n<ul class="learn-list">\n  <li>A summary presents the MAIN IDEAS of a passage in a SHORTER form</li>\n  <li>It must be written in the SUMMARISER\'S OWN WORDS (not copied verbatim)</li>\n  <li>It must be OBJECTIVE — no personal opinions or additions</li>\n  <li>It must retain the LOGICAL ORDER/sequence of the original ideas</li>\n  <li>It must fit within the specified WORD LIMIT</li>\n</ul>\n\n<h3 class="learn-subheading">2. Steps for Writing a Summary</h3>\n<ol class="learn-list">\n  <li><strong>Read the passage carefully</strong> (more than once) to understand the overall meaning</li>\n  <li><strong>Identify the topic sentence</strong> of each paragraph — this usually contains the main idea</li>\n  <li><strong>Underline or note key points</strong> — eliminate examples, illustrations, repetitions</li>\n  <li><strong>Write the points in your own words</strong> — avoid copying phrases directly</li>\n  <li><strong>Combine points into coherent sentences</strong> using appropriate linking words</li>\n  <li><strong>Check the word count</strong> and ensure you have not exceeded the limit</li>\n  <li><strong>Proofread</strong> for grammar, spelling, and coherence</li>\n</ol>\n\n<h3 class="learn-subheading">3. Do\'s and Don\'ts of Summary Writing</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>DO</th><th>DON\'T</th></tr></thead>\n    <tbody>\n      <tr><td>Use your own words/paraphrase</td><td>Copy sentences directly from the passage</td></tr>\n      <tr><td>Include only main ideas</td><td>Include examples, illustrations, or minor details</td></tr>\n      <tr><td>Maintain the original sequence of ideas</td><td>Rearrange ideas in a confusing order</td></tr>\n      <tr><td>Write in continuous prose (unless told otherwise)</td><td>Write in point form unless specifically instructed</td></tr>\n      <tr><td>Stay within the word limit</td><td>Add your own opinions, comments, or conclusions</td></tr>\n      <tr><td>Use linking words for coherence</td><td>Use direct/reported speech, abbreviations</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">4. Summarising Arguments</h3>\n<p class="learn-p">When summarising an ARGUMENTATIVE passage:</p>\n<ul class="learn-list">\n  <li>Identify the WRITER\'S POSITION/THESIS (what is being argued for or against)</li>\n  <li>Identify the SUPPORTING POINTS/REASONS given for the argument</li>\n  <li>Note any COUNTER-ARGUMENTS the writer addresses</li>\n  <li>Present these points concisely without adding YOUR OWN opinion on the issue</li>\n</ul>\n\n<h3 class="learn-subheading">5. Vocabulary: Words of the Week (Selection)</h3>\n<p class="learn-p"><strong>serendipity</strong> (a pleasant surprise/happy accident), <strong>reprieve</strong> (a delay/cancellation of punishment), <strong>pariah</strong> (an outcast, despised person), <strong>maelstrom</strong> (a state of confused, violent movement), <strong>wistful</strong> (having sad/longing thoughts), <strong>xenophobia</strong> (fear/hatred of foreigners), <strong>upbraid</strong> (to criticise/scold severely), <strong>ephemeral</strong> (lasting a very short time), <strong>hierarchy</strong> (a ranked system/order), <strong>inept</strong> (lacking skill, incompetent), <strong>rectitude</strong> (moral correctness/honesty), <strong>abdicate</strong> (to give up power/responsibility formally), <strong>apathetic</strong> (showing no interest/concern).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Summary writing rules: (1) NEVER copy verbatim — paraphrase; (2) Drop examples/illustrations — keep only main points; (3) Stay within word limit (going over loses marks); (4) Write in continuous prose unless instructed otherwise; (5) Don\'t add your own opinion. When the question asks for a specific number of points, count carefully and present exactly that number.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> A good summary: shorter than original, in your own words, objective (no added opinions), maintains logical order, within word limit. Steps: read carefully, identify topic sentences, note key points (drop examples), paraphrase, combine coherently, check word count, proofread. For argumentative passages: identify thesis, supporting points, counter-arguments — without adding your own opinion.\n</div>\n  ',
    questions: [
      {
        q: 'Which of the following is the MOST important feature of a good summary?',
        o: [
          'It should be longer than the original passage',
          "It should be written in the summariser's own words while retaining the main ideas",
          "It should include the writer's personal opinion about the topic",
          'It should copy the most interesting sentences verbatim',
        ],
        a: 1,
        e: "A good summary condenses the main ideas of a passage using the summariser's OWN WORDS — directly copying sentences (plagiarism) is heavily penalised in WAEC. It must also be shorter than the original and must NOT include personal opinions.",
        h: 'The most fundamental rule of summary writing relates to WHOSE WORDS are used.',
        yr: 'SS2',
      },
      {
        q: 'What is the FIRST step in writing a summary of a passage?',
        o: [
          'Start writing immediately to save time',
          'Read the passage carefully (more than once) to understand its overall meaning',
          'Copy down the longest sentences',
          'Write your opinion of the topic first',
        ],
        a: 1,
        e: "Before writing anything, you must read the passage carefully — often more than once — to fully understand its content, structure, and main ideas. Skipping this step leads to missing important points or misrepresenting the passage's meaning.",
        h: "Comprehension must come BEFORE writing — you cannot summarise what you don't understand.",
        yr: 'SS2',
      },
      {
        q: 'When summarising, what should you generally do with EXAMPLES and ILLUSTRATIONS given in the original passage?',
        o: [
          'Include all of them in full detail',
          'Generally omit them, focusing on the main idea they illustrate',
          'Replace them with your own examples',
          'Copy them word-for-word',
        ],
        a: 1,
        e: 'Examples and illustrations support or explain a main idea but are usually not essential to the summary — the main idea/point itself should be retained, but specific examples can typically be omitted to save space and focus on key points.',
        h: 'Examples SUPPORT main ideas — the main idea matters more than its illustration in a summary.',
        yr: 'SS2',
      },
      {
        q: "Which of the following is a 'DON'T' in summary writing?",
        o: [
          'Maintaining the logical sequence of the original ideas',
          'Using linking words for coherence',
          'Adding your own opinions or conclusions not present in the original',
          'Staying within the specified word limit',
        ],
        a: 2,
        e: 'Adding your own opinions, comments, or conclusions is a major error in summary writing — a summary must be OBJECTIVE, representing only what the original passage states, not what the summariser thinks about it.',
        h: "A summary represents the ORIGINAL PASSAGE'S content, not the summariser's views.",
        yr: 'SS2',
      },
      {
        q: 'When summarising an ARGUMENTATIVE passage, what should you primarily identify?',
        o: [
          'Only the title of the passage',
          "The writer's position/thesis and the supporting points/reasons given for it",
          'Grammatical errors in the original text',
          'The total number of words in the passage',
        ],
        a: 1,
        e: "For argumentative passages, identify: (1) the writer's overall POSITION or THESIS (what they argue for/against), and (2) the SUPPORTING REASONS/EVIDENCE they provide. These form the backbone of the summary.",
        h: 'Arguments have a POSITION (thesis) and SUPPORT (reasons) — both must be captured.',
        yr: 'SS2',
      },
      {
        q: "What does 'serendipity' mean?",
        o: [
          'A planned, deliberate event',
          'A pleasant surprise or fortunate accident',
          'A type of formal ceremony',
          'A state of confusion',
        ],
        a: 1,
        e: "'Serendipity' refers to the occurrence of pleasant or beneficial events by chance — a happy accident. Example: 'Finding that rare book was pure serendipity.'",
        h: 'This word relates to a LUCKY, unplanned happy occurrence.',
        yr: 'SS2',
      },
      {
        q: "Which word means 'an outcast or person despised by others'?",
        o: ['Paragon', 'Pariah', 'Reprieve', 'Hierarchy'],
        a: 1,
        e: "'Pariah' refers to a person who is rejected or despised by society — an outcast. Example: 'After the scandal, he became a social pariah in his community.'",
        h: 'This word describes someone who has been REJECTED by society.',
        yr: 'SS2',
      },
      {
        q: "A 'reprieve' refers to:",
        o: [
          'A permanent solution to a problem',
          'A temporary delay or cancellation of a punishment or unpleasant event',
          'A celebration of victory',
          'A loud public announcement',
        ],
        a: 1,
        e: "'Reprieve' means a temporary postponement or cancellation of a punishment (especially execution) or an unwelcome event. Example: 'The court granted the prisoner a last-minute reprieve.'",
        h: 'This word relates to a DELAY of punishment — a temporary relief.',
        yr: 'SS2',
      },
      {
        q: "Which word means 'lasting for a very short time'?",
        o: ['Hierarchy', 'Ephemeral', 'Inept', 'Rectitude'],
        a: 1,
        e: "'Ephemeral' describes something short-lived or transient — lasting only briefly. Example: 'The beauty of cherry blossoms is ephemeral, lasting only a few days.'",
        h: 'This word means SHORT-LIVED or TEMPORARY.',
        yr: 'SS2',
      },
      {
        q: "To 'upbraid' someone means to:",
        o: [
          'Promote them to a higher position',
          'Criticise or scold them severely',
          'Compliment them publicly',
          'Help them complete a task',
        ],
        a: 1,
        e: "'Upbraid' means to criticise or find fault with someone harshly — to scold severely. Example: 'The manager upbraided the staff for the late delivery.'",
        h: 'This word relates to SCOLDING someone severely.',
        yr: 'SS2',
      },
      {
        q: "What does 'xenophobia' mean?",
        o: [
          'A love of travel and foreign cultures',
          'Fear or hatred of foreigners or strangers',
          'A fear of small spaces',
          'A type of formal greeting',
        ],
        a: 1,
        e: "'Xenophobia' is the fear, dislike, or hatred of people from other countries or cultures. Example: 'The government condemned the xenophobic attacks on foreign workers.'",
        h: "Break down the word: 'xeno-' relates to 'foreign/strange'; '-phobia' relates to 'fear'.",
        yr: 'SS2',
      },
      {
        q: "Which word means 'lacking skill; clumsy or incompetent'?",
        o: ['Vivacious', 'Inept', 'Stoic', 'Paragon'],
        a: 1,
        e: "'Inept' describes someone lacking skill or ability, especially in performing a task — clumsy or incompetent. Example: 'His inept handling of the situation made things worse.'",
        h: 'This word means LACKING SKILL or being clumsy/incompetent.',
        yr: 'SS2',
      },
      {
        q: "'Rectitude' refers to:",
        o: [
          'Physical strength and endurance',
          'Moral integrity and correctness of behaviour or judgment',
          'A type of legal document',
          'Speed and quickness',
        ],
        a: 1,
        e: "'Rectitude' means moral integrity, righteousness, or correctness in conduct and judgment. Example: 'The judge was known for his rectitude and fairness.'",
        h: 'This word relates to MORAL CORRECTNESS and honesty.',
        yr: 'SS2',
      },
      {
        q: "To 'abdicate' a responsibility or position means to:",
        o: [
          'Take on additional responsibilities',
          'Formally give up or renounce a position, duty, or right',
          'Excel at performing a duty',
          'Request more time to complete a task',
        ],
        a: 1,
        e: "'Abdicate' means to formally renounce or give up a position of power, duty, or responsibility. Most commonly associated with monarchs giving up the throne, but also used more broadly (e.g., 'He abdicated his responsibility as a father').",
        h: 'This word is most famously used for monarchs GIVING UP the throne.',
        yr: 'SS2',
      },
      {
        q: "Which word describes someone showing 'no interest, enthusiasm, or concern'?",
        o: ['Vivacious', 'Apathetic', 'Querulous', 'Stoic'],
        a: 1,
        e: "'Apathetic' describes a lack of interest, enthusiasm, or concern — indifference. Example: 'Many young people seem apathetic about voting in elections.'",
        h: 'This word describes a lack of INTEREST or CONCERN — indifference.',
        yr: 'SS2',
      },
      {
        q: "A 'maelstrom' refers to:",
        o: [
          'A peaceful, calm scene',
          'A state of violent confusion, turmoil, or upheaval',
          'A type of musical performance',
          'A formal academic essay',
        ],
        a: 1,
        e: "'Maelstrom' originally referred to a powerful whirlpool, but is commonly used figuratively to describe a situation of chaotic confusion or turmoil. Example: 'The country was thrown into a maelstrom of political unrest.'",
        h: 'This word originally meant a powerful WHIRLPOOL — now used for chaotic situations.',
        yr: 'SS2',
      },
      {
        q: 'Which word describes a feeling of sad, vague longing, especially for something in the past?',
        o: ['Wistful', 'Incendiary', 'Apathetic', 'Surreptitious'],
        a: 0,
        e: "'Wistful' describes a feeling of melancholic longing, especially nostalgia for something that has passed. Example: 'She looked at the old photos with a wistful smile.'",
        h: 'This word relates to a SAD LONGING, often for the past.',
        yr: 'SS2',
      },
      {
        q: "When a question asks you to summarise a passage 'in not more than 80 words', which of the following is most important?",
        o: [
          'Writing exactly 80 words, no more or less',
          'Ensuring your summary does NOT exceed 80 words while capturing the main points concisely',
          'Writing as close to 80 words as possible regardless of content',
          'Ignoring the word limit if you have important points to add',
        ],
        a: 1,
        e: "'Not more than 80 words' sets a MAXIMUM — your summary should be at or below this limit. Exceeding it typically results in lost marks (often the excess words beyond the limit are simply not marked). The priority is capturing main points within the limit, not hitting an exact count.",
        h: "'Not more than X words' = a MAXIMUM limit, not an exact target.",
        yr: 'SS2',
      },
      {
        q: "'Hierarchy' refers to:",
        o: [
          'A type of decorative pattern',
          'A system in which people or things are ranked according to status or authority',
          'A loud celebration',
          'A formal apology',
        ],
        a: 1,
        e: "'Hierarchy' refers to a ranked system or structure, typically of people, organisations, or things, arranged according to status, authority, or importance. Example: 'In the company's hierarchy, the CEO is at the top.'",
        h: 'This word relates to a RANKED ORDER or SYSTEM of authority.',
        yr: 'SS2',
      },
      {
        q: "Why should a summary maintain the LOGICAL ORDER of the original passage's ideas?",
        o: [
          'Because rearranging ideas always improves clarity',
          'Because the original sequence often reflects the logical development of the argument or narrative, and rearranging it can distort meaning or confuse the reader',
          'Because WAEC requires alphabetical ordering',
          'Order does not matter in summaries',
        ],
        a: 1,
        e: "The original passage's sequence often reflects cause-and-effect, chronological, or logical argumentative development. Rearranging this order in a summary can distort the original meaning or create confusion, even if the individual points themselves are accurate.",
        h: 'The ORDER of ideas often reflects how the argument/narrative DEVELOPS logically.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Essay Writing: Expository and Argumentative Essays',
    topicCode: 'SS2-ENG-08',
    module: 'Composition',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Expository essays</span> explain or inform the reader about a topic using facts, examples, and analysis. <span class="learn-keyword">Argumentative essays</span> take a position on a debatable issue and persuade the reader using evidence and reasoning. Both follow a clear structural framework.\n</div>\n\n<h3 class="learn-subheading">1. Structure of an Expository Essay</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Part</th><th>Content</th></tr></thead>\n    <tbody>\n      <tr><td>Introduction</td><td>States the topic and gives a brief overview/background; may state the thesis (what aspects will be discussed)</td></tr>\n      <tr><td>Body paragraphs</td><td>Each paragraph explores ONE aspect/cause/effect/solution with facts, examples, and explanation</td></tr>\n      <tr><td>Conclusion</td><td>Summarises the main points; may offer a final reflection or call to action</td></tr>\n    </tbody>\n  </table>\n</div>\n<p class="learn-p"><strong>Example topic:</strong> \'Controlling HIV/AIDS in Nigeria\' — body paragraphs might cover: causes of spread, prevention methods, government interventions, role of education, challenges faced.</p>\n\n<h3 class="learn-subheading">2. Structure of an Argumentative Essay</h3>\n<ul class="learn-list">\n  <li><strong>Introduction:</strong> States the issue and the writer\'s CLEAR POSITION (for/against)</li>\n  <li><strong>Body paragraphs:</strong> Each paragraph presents ONE POINT supporting the writer\'s position, with evidence and reasoning. May also ACKNOWLEDGE and REBUT opposing views</li>\n  <li><strong>Conclusion:</strong> Restates the position and summarises the strongest arguments; may end with a recommendation</li>\n</ul>\n<p class="learn-p"><strong>Example topics:</strong> \'Should female circumcision be abolished?\' / \'Military rule is better than civilian rule.\' — writer must take a clear stance and support it with logical reasoning.</p>\n\n<h3 class="learn-subheading">3. Key Differences: Expository vs Argumentative</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Expository</th><th>Argumentative</th></tr></thead>\n    <tbody>\n      <tr><td>Purpose: to INFORM/explain</td><td>Purpose: to PERSUADE</td></tr>\n      <tr><td>Presents multiple perspectives objectively</td><td>Takes a CLEAR POSITION/SIDE</td></tr>\n      <tr><td>Uses facts and explanations</td><td>Uses facts, evidence, AND persuasive reasoning</td></tr>\n      <tr><td>May not require a \'for/against\' stance</td><td>Must address counter-arguments</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">4. Linking Words for Essays</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Purpose</th><th>Linking Words</th></tr></thead>\n    <tbody>\n      <tr><td>Adding points</td><td>furthermore, moreover, in addition, also</td></tr>\n      <tr><td>Contrast</td><td>however, on the other hand, nevertheless, although</td></tr>\n      <tr><td>Cause/effect</td><td>therefore, consequently, as a result, thus</td></tr>\n      <tr><td>Examples</td><td>for instance, for example, such as</td></tr>\n      <tr><td>Conclusion</td><td>in conclusion, to sum up, in summary, finally</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">5. Vocabulary: Words of the Week (Selection)</h3>\n<p class="learn-p"><strong>connive</strong> (to secretly plan something illegal/harmful), <strong>daunting</strong> (intimidating, discouraging), <strong>promulgate</strong> (to formally proclaim/put into effect), <strong>rancour</strong> (deep bitterness/resentment), <strong>serene</strong> (calm, peaceful), <strong>clandestine</strong> (secret, hidden), <strong>prescient</strong> (having foreknowledge of events), <strong>servile</strong> (excessively obedient/submissive), <strong>torpid</strong> (sluggish, lacking energy), <strong>trite</strong> (overused, lacking originality), <strong>pliable</strong> (easily bent/influenced), <strong>pittance</strong> (a very small amount of money).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Expository = explain/inform (multiple aspects, balanced). Argumentative = persuade (clear position FOR or AGAINST + addresses counter-arguments). Both need: clear introduction with topic statement, body paragraphs each developing ONE idea, and a conclusion that summarises. WAEC penalises essays without a clear introduction/conclusion or that wander off-topic.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Expository essay: informs/explains, multiple perspectives, structured around aspects/causes/effects. Argumentative essay: persuades, takes a CLEAR position, addresses counter-arguments. Both have introduction-body-conclusion structure. Use linking words appropriately (furthermore, however, therefore, for instance, in conclusion) to connect ideas coherently.\n</div>\n  ',
    questions: [
      {
        q: 'What is the PRIMARY PURPOSE of an expository essay?',
        o: [
          'To persuade the reader to adopt a particular opinion',
          'To inform or explain a topic to the reader using facts and examples',
          'To entertain the reader with a fictional story',
          "To express the writer's personal feelings without evidence",
        ],
        a: 1,
        e: 'Expository essays are written to INFORM or EXPLAIN — they present information about a topic in an organised way, often covering multiple aspects (causes, effects, types, solutions) using facts and examples, without necessarily arguing for one side.',
        h: "'Expository' relates to 'exposing' or explaining information — think INFORM.",
        yr: 'SS2',
      },
      {
        q: 'What is the KEY FEATURE that distinguishes an argumentative essay from an expository essay?',
        o: [
          'Argumentative essays are always shorter',
          'Argumentative essays take a CLEAR POSITION on a debatable issue and aim to persuade; expository essays explain/inform without necessarily taking a side',
          'Expository essays never use examples',
          'Argumentative essays do not need an introduction',
        ],
        a: 1,
        e: 'The defining feature of argumentative essays is taking a CLEAR STANCE (for or against) on a debatable issue and using evidence/reasoning to PERSUADE the reader. Expository essays primarily inform, often presenting multiple aspects without arguing for a particular position.',
        h: 'Argumentative = takes a SIDE and persuades; Expository = informs/explains.',
        yr: 'SS2',
      },
      {
        q: 'In an argumentative essay, what should the writer do with OPPOSING VIEWS on the topic?',
        o: [
          'Completely ignore them',
          "Acknowledge and address (rebut) them to strengthen the writer's own position",
          "Agree with them fully, abandoning the writer's own position",
          'Mention them only in the introduction',
        ],
        a: 1,
        e: 'A strong argumentative essay acknowledges counter-arguments and then REBUTS (responds to/refutes) them — this demonstrates that the writer has considered other perspectives and strengthens their own position by showing why it is more convincing.',
        h: 'Addressing opposing views and REBUTTING them makes an argument STRONGER, not weaker.',
        yr: 'SS2',
      },
      {
        q: 'Which linking word/phrase is most appropriate for introducing a CONTRASTING idea?',
        o: ['Furthermore', 'For instance', 'However', 'In conclusion'],
        a: 2,
        e: "'However' signals a contrast or shift in direction — it is used to introduce an idea that differs from or contradicts what was just stated. 'Furthermore' adds similar ideas, 'for instance' gives examples, and 'in conclusion' signals the ending.",
        h: 'Which word signals a CHANGE of direction or an opposing idea?',
        yr: 'SS2',
      },
      {
        q: 'What should the INTRODUCTION of an essay (expository or argumentative) typically accomplish?',
        o: [
          'Provide the full conclusion of the essay',
          "Introduce the topic, provide background/context, and (for argumentative essays) state the writer's position",
          'List all the body paragraphs in detail',
          'Be the longest part of the essay',
        ],
        a: 1,
        e: "An effective introduction: introduces the topic to orient the reader, provides relevant background/context, and (especially for argumentative essays) clearly states the writer's thesis/position. It should be concise — not a detailed preview of every point.",
        h: 'The introduction ORIENTS the reader to the topic and (for arguments) states the POSITION.',
        yr: 'SS2',
      },
      {
        q: "To 'connive' means to:",
        o: [
          'Openly and honestly discuss a plan',
          'Secretly plan or cooperate to do something illegal or harmful',
          'Publicly announce a new policy',
          'Calmly accept a difficult situation',
        ],
        a: 1,
        e: "'Connive' means to secretly cooperate or conspire to do something wrong, often by allowing it to happen without openly participating. Example: 'The officials connived to embezzle public funds.'",
        h: 'This word relates to SECRET plotting or conspiracy, often for wrongdoing.',
        yr: 'SS2',
      },
      {
        q: "Which word best describes a task or challenge that is 'intimidating and discouraging'?",
        o: ['Daunting', 'Serene', 'Trite', 'Pliable'],
        a: 0,
        e: "'Daunting' describes something that seems difficult to deal with, often causing apprehension or discouragement. Example: 'The mountain of paperwork seemed daunting at first.'",
        h: 'This word describes a CHALLENGING task that seems INTIMIDATING.',
        yr: 'SS2',
      },
      {
        q: "To 'promulgate' a law or idea means to:",
        o: [
          'Secretly hide it from the public',
          'Formally announce or put it into effect',
          'Reject and abandon it',
          'Translate it into another language',
        ],
        a: 1,
        e: "'Promulgate' means to formally proclaim or put a law, doctrine, or idea into effect — to make it widely known officially. Example: 'The government promulgated the new education policy.'",
        h: 'This word relates to OFFICIALLY ANNOUNCING/putting something into effect.',
        yr: 'SS2',
      },
      {
        q: "'Rancour' refers to:",
        o: [
          'Deep, long-lasting bitterness or resentment',
          'Joyful celebration',
          'A peaceful agreement',
          'Quick decision-making',
        ],
        a: 0,
        e: "'Rancour' means deep-seated bitterness, resentment, or ill will. Example: 'Despite their disagreement, they resolved the issue without rancour.'",
        h: 'This word describes deep BITTERNESS or RESENTMENT.',
        yr: 'SS2',
      },
      {
        q: "Which word means 'secret or hidden, often because the activity is illicit'?",
        o: ['Serene', 'Clandestine', 'Servile', 'Torpid'],
        a: 1,
        e: "'Clandestine' describes activities that are kept secret or done in secrecy, often because they are illegal or unauthorised. Example: 'They held a clandestine meeting to discuss the coup.'",
        h: 'This word means SECRET, often relating to illegal or unauthorised activity.',
        yr: 'SS2',
      },
      {
        q: "A person described as 'prescient' is one who:",
        o: [
          'Lacks any foresight about the future',
          'Seems to have knowledge of events before they happen; shows foresight',
          'Is excessively obedient to others',
          'Is physically very strong',
        ],
        a: 1,
        e: "'Prescient' describes someone who seems to know about things before they happen — having foresight or the ability to predict future events. Example: 'Her prescient warning about the economic crisis proved accurate.'",
        h: 'This word relates to having FORESIGHT — knowing things before they happen.',
        yr: 'SS2',
      },
      {
        q: "Which word describes behaviour that is 'excessively obedient or submissive, like a servant'?",
        o: ['Servile', 'Prescient', 'Daunting', 'Pliable'],
        a: 0,
        e: "'Servile' describes excessive willingness to serve or please others — overly submissive, like a servant, often in a demeaning way. Example: 'His servile attitude toward his boss embarrassed his colleagues.'",
        h: "This word relates to 'servant' — overly submissive/obedient behaviour.",
        yr: 'SS2',
      },
      {
        q: "'Torpid' describes a state of being:",
        o: [
          'Highly energetic and active',
          'Sluggish, inactive, lacking energy or motivation',
          'Extremely intelligent',
          'Loud and aggressive',
        ],
        a: 1,
        e: "'Torpid' means physically or mentally inactive, sluggish, lacking energy or vigour. Example: 'The heat made everyone feel torpid in the afternoon.'",
        h: 'This word means SLUGGISH or lacking energy — the opposite of lively.',
        yr: 'SS2',
      },
      {
        q: "Which word describes something 'easily bent, shaped, or influenced'?",
        o: ['Rigid', 'Pliable', 'Torpid', 'Incendiary'],
        a: 1,
        e: "'Pliable' describes something that can be easily bent or shaped (like soft material) — or figuratively, a person easily influenced or persuaded. Example: 'The young leader was pliable and easily swayed by his advisers.'",
        h: 'This word relates to FLEXIBILITY — easily bent or influenced.',
        yr: 'SS2',
      },
      {
        q: "A 'pittance' refers to:",
        o: [
          'A large fortune',
          'A very small, often inadequate amount of money',
          'A formal celebration',
          'A type of legal contract',
        ],
        a: 1,
        e: "'Pittance' means a very small amount of money, especially as a wage or allowance — often considered insufficient. Example: 'The workers were paid a mere pittance for their long hours.'",
        h: 'This word describes a TINY amount of money, often inadequate.',
        yr: 'SS2',
      },
      {
        q: "In a well-structured argumentative essay about 'Military rule is better than civilian rule', each body paragraph should typically focus on:",
        o: [
          'A completely different, unrelated topic',
          "ONE specific point or piece of evidence supporting the writer's position (or rebutting a counter-argument)",
          'Restating the introduction in different words',
          'Only the conclusion of the essay',
        ],
        a: 1,
        e: "Each body paragraph in an argumentative essay should develop ONE clear point — whether that's a supporting argument, evidence, or a rebuttal of a counter-argument. Mixing multiple unrelated points in one paragraph weakens clarity and organisation.",
        h: 'Good paragraph structure: ONE main idea per paragraph, developed with evidence/reasoning.',
        yr: 'SS2',
      },
      {
        q: 'Which linking phrase is most appropriate for introducing an EXAMPLE in an essay?',
        o: ['Therefore', 'However', 'For instance', 'In conclusion'],
        a: 2,
        e: "'For instance' (along with 'for example' and 'such as') is used to introduce a specific example illustrating a point already made. 'Therefore' shows cause/effect, 'however' shows contrast, and 'in conclusion' signals the ending.",
        h: 'Which phrase is used to give a SPECIFIC EXAMPLE of a general point?',
        yr: 'SS2',
      },
      {
        q: 'What should the CONCLUSION of an argumentative essay typically do?',
        o: [
          'Introduce a completely new argument not mentioned in the body',
          "Restate the writer's position and summarise the strongest supporting arguments, possibly with a final recommendation",
          'Simply repeat the introduction word-for-word',
          'Apologise for any errors in the essay',
        ],
        a: 1,
        e: "An effective conclusion reinforces the writer's position by summarising the key arguments presented, without introducing brand-new points. It often ends with a final thought, recommendation, or call to action that flows from the arguments made.",
        h: 'The conclusion WRAPS UP the argument — restating the position and summarising key points.',
        yr: 'SS2',
      },
      {
        q: "For an expository essay on 'Managing Population Explosion', which of the following would be an appropriate structure for body paragraphs?",
        o: [
          'Each paragraph repeats the same point in different words',
          'Each paragraph addresses a different aspect: causes of population growth, effects on resources, government policies, and possible solutions',
          'Only one very long paragraph covering everything',
          'Body paragraphs about completely unrelated topics like sports and music',
        ],
        a: 1,
        e: 'A well-organised expository essay on population explosion would logically divide the topic into distinct aspects — causes, effects, existing policies/responses, and possible solutions — with each body paragraph developing one of these aspects in depth.',
        h: 'Expository essays organise the topic into DISTINCT ASPECTS, each developed in its own paragraph.',
        yr: 'SS2',
      },
      {
        q: "Which of the following best demonstrates an appropriate use of 'consequently' in a sentence?",
        o: [
          'Consequently, I like reading novels in my free time.',
          'The government banned the importation of the product; consequently, local manufacturers saw increased demand.',
          'Consequently, the weather was sunny yesterday.',
          'She is consequently very tall.',
        ],
        a: 1,
        e: "'Consequently' shows a CAUSE-AND-EFFECT relationship — the ban (cause) led to increased demand for local manufacturers (effect). The other options use 'consequently' without any logical cause-effect relationship being expressed.",
        h: "'Consequently' shows that one thing happened BECAUSE OF/AS A RESULT OF another.",
        yr: 'SS2',
      },
    ],
  },
  {
    topic:
      'Punctuation Marks: Apostrophe, Question Mark, Exclamation Mark, Colon, Semi-colon, Hyphen',
    topicCode: 'SS2-ENG-09',
    module: 'Lexis and Structure',
    term: 'Second Term',
    contentHTML:
      "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Punctuation marks</span> are symbols used in writing to separate sentences, clarify meaning, and indicate pauses or relationships between ideas. Correct punctuation is essential for clear written communication and is a frequently tested area in WAEC Lexis and Structure.\n</div>\n\n<h3 class=\"learn-subheading\">1. The Possessive Apostrophe</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Rule</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>Singular noun: add 's</td><td>the boy's bag, James's car</td></tr>\n      <tr><td>Plural noun ending in -s: add only '</td><td>the boys' bag, the students' results</td></tr>\n      <tr><td>Plural noun NOT ending in -s: add 's</td><td>the children's toys, the men's room</td></tr>\n      <tr><td>Joint possession (two owners, one item)</td><td>John and Mary's house (they share one house)</td></tr>\n      <tr><td>Separate possession (two owners, separate items)</td><td>John's and Mary's houses (each has their own)</td></tr>\n      <tr><td>Contractions (apostrophe shows omitted letters)</td><td>don't (do not), it's (it is), can't (cannot)</td></tr>\n    </tbody>\n  </table>\n</div>\n<p class=\"learn-p\"><strong>Important distinction:</strong> 'its' (possessive, no apostrophe) vs 'it's' (contraction of 'it is'/'it has'). 'The dog wagged its tail' (possessive) vs 'It's raining' (it is raining).</p>\n\n<h3 class=\"learn-subheading\">2. Question Mark and Exclamation Mark</h3>\n<ul class=\"learn-list\">\n  <li><strong>Question mark (?):</strong> Used at the end of a DIRECT question. 'Where are you going?' (NOT used for indirect/reported questions: 'He asked where I was going.')</li>\n  <li><strong>Exclamation mark (!):</strong> Used to express strong emotion, surprise, or emphasis. 'What a beautiful sunset!' / 'Stop right there!'</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. The Colon (:)</h3>\n<ul class=\"learn-list\">\n  <li>Introduces a LIST: 'Bring the following: a pen, a notebook, and a calculator.'</li>\n  <li>Introduces an EXPLANATION or ELABORATION: 'There was one reason for his success: hard work.'</li>\n  <li>Introduces a QUOTATION (formal): 'The teacher said: \"Practice makes perfect.\"'</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. The Semi-colon (;)</h3>\n<ul class=\"learn-list\">\n  <li>Joins two CLOSELY RELATED independent clauses without a conjunction: 'She studied hard; she passed the exam.'</li>\n  <li>Separates items in a COMPLEX LIST (especially when items themselves contain commas): 'We visited Lagos, Nigeria; Accra, Ghana; and Dakar, Senegal.'</li>\n  <li>Often used before connecting words like 'however', 'therefore', 'moreover': 'He was tired; however, he continued working.'</li>\n</ul>\n\n<h3 class=\"learn-subheading\">5. The Hyphen (-)</h3>\n<ul class=\"learn-list\">\n  <li>Joins words to form COMPOUND adjectives before a noun: 'a well-known author', 'a five-year-old child'</li>\n  <li>Joins NUMBERS written as words: 'twenty-one', 'forty-five'</li>\n  <li>Used with certain PREFIXES: 'ex-president', 'self-control', 'co-operate'</li>\n  <li>Avoids ambiguity: 're-cover' (cover again) vs 'recover' (get better)</li>\n</ul>\n\n<h3 class=\"learn-subheading\">6. Vocabulary: Words of the Week (Selection)</h3>\n<p class=\"learn-p\"><strong>morass</strong> (a complicated/messy situation; also a marsh), <strong>abject</strong> (extremely bad/miserable), <strong>blandish</strong> (to coax with flattery), <strong>duplicity</strong> (deceitfulness), <strong>ennui</strong> (boredom/listlessness), <strong>maudlin</strong> (self-pityingly sentimental), <strong>abscond</strong> (to leave secretly to escape), <strong>alias</strong> (a false/assumed name), <strong>dither</strong> (to hesitate/be indecisive), <strong>emaciated</strong> (abnormally thin from illness/hunger).</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Plural noun ending in -s gets ONLY an apostrophe (boys'); singular nouns and plurals NOT ending in -s get 's (boy's, children's). 'Its' = possessive; 'It's' = it is/it has. Colon introduces lists/explanations; semicolon joins related independent clauses. Question marks ONLY for direct questions, never reported/indirect questions.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Apostrophe: 's for singular/irregular plural, ' alone for regular plural, contractions for omitted letters. Question mark: direct questions only. Exclamation mark: strong emotion. Colon: introduces lists/explanations. Semi-colon: joins related independent clauses or separates complex list items. Hyphen: compound adjectives, written numbers, certain prefixes, avoiding ambiguity.\n</div>\n  ",
    questions: [
      {
        q: "Which sentence correctly uses the possessive apostrophe for a PLURAL noun ending in '-s'?",
        o: [
          "The boy's books were on the table.",
          "The boys's books were on the table.",
          "The boys' books were on the table.",
          "The boys books' were on the table.",
        ],
        a: 2,
        e: "'Boys'' is correct — when a plural noun already ends in '-s' (boys), you add ONLY an apostrophe after the 's', not another 's'. 'Boys's' is incorrect because it adds an unnecessary extra 's'.",
        h: 'Plural noun ALREADY ending in -s: just add the apostrophe, nothing more.',
        yr: 'SS2',
      },
      {
        q: "Choose the correct possessive form for the irregular plural 'children':",
        o: ["Childrens' toys", "Children's toys", 'Childrens toys', "Childrens's toys"],
        a: 1,
        e: "'Children's' is correct — since 'children' is a plural that does NOT end in '-s', you add 's (apostrophe + s), just like a singular noun. The same rule applies to 'men's', 'women's', 'people's'.",
        h: "'Children' doesn't end in -s, so treat it like a singular noun: add 's.",
        yr: 'SS2',
      },
      {
        q: "Which sentence correctly distinguishes between 'its' and \"it's\"?",
        o: [
          "Its raining outside, so bring it's umbrella.",
          "It's raining outside, so bring its umbrella.",
          'Its raining outside, so bring its umbrella.',
          "It's raining outside, so bring it's umbrella.",
        ],
        a: 1,
        e: "\"It's\" (contraction of 'it is') is correct for 'It's raining', and 'its' (possessive, no apostrophe) is correct for 'its umbrella' (the umbrella belonging to it/someone). Never use an apostrophe for the possessive 'its'.",
        h: "\"It's\" = it is/it has (contraction). 'Its' = possessive (belonging to it) — NO apostrophe.",
        yr: 'SS2',
      },
      {
        q: 'Which punctuation mark is used to introduce a LIST of items in a sentence?',
        o: ['Semi-colon (;)', 'Colon (:)', 'Hyphen (-)', "Apostrophe (')"],
        a: 1,
        e: "The colon (:) is used to introduce a list, especially after a complete independent clause: 'Bring the following: a pen, a notebook, and a calculator.' It signals that a list, explanation, or elaboration is about to follow.",
        h: 'This mark comes BEFORE a list and looks like two dots stacked vertically.',
        yr: 'SS2',
      },
      {
        q: 'Choose the sentence that correctly uses a SEMI-COLON:',
        o: [
          'She studied hard, she passed the exam.',
          'She studied hard; she passed the exam.',
          'She studied hard: she passed the exam.',
          'She studied hard - she passed the exam, comma.',
        ],
        a: 1,
        e: "The semi-colon correctly joins two CLOSELY RELATED independent clauses without using a conjunction like 'and' or 'so'. Using a comma alone here (option A) would create a 'comma splice' error.",
        h: 'A semi-colon joins two RELATED COMPLETE sentences without a conjunction.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly uses a hyphen in a compound adjective?',
        o: [
          'She is a well known author in Nigeria.',
          'She is a well-known author in Nigeria.',
          'She is a well, known author in Nigeria.',
          'She is a well; known author in Nigeria.',
        ],
        a: 1,
        e: "'Well-known' is correctly hyphenated as a compound adjective modifying 'author' and placed BEFORE the noun. Compound adjectives before a noun are typically hyphenated for clarity (e.g., 'a five-year-old child', 'a well-written essay').",
        h: 'Compound adjectives BEFORE a noun usually need a hyphen joining the words.',
        yr: 'SS2',
      },
      {
        q: 'When should a question mark be used?',
        o: [
          "At the end of any sentence containing the word 'who' or 'what'",
          'At the end of a DIRECT question only, not an indirect/reported question',
          'At the end of every sentence in a dialogue',
          'Only in formal academic writing',
        ],
        a: 1,
        e: "A question mark is used ONLY for direct questions ('Where are you going?'), NOT for indirect/reported questions, which use a full stop instead ('He asked where I was going.').",
        h: 'Question marks are for DIRECT questions, not reported speech about questions.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence demonstrates JOINT possession correctly?',
        o: [
          "John's and Mary's house is beautiful (they own one house together).",
          "John and Mary's house is beautiful (they own one house together).",
          'Johns and Marys house is beautiful.',
          "John's and Mary house is beautiful.",
        ],
        a: 1,
        e: "For JOINT possession (when two people share ONE item), the apostrophe is added only to the LAST name: 'John and Mary's house'. If they each owned SEPARATE houses, you would say 'John's and Mary's houses' (apostrophe on both).",
        h: 'Joint possession (ONE shared item): apostrophe goes on the LAST name only.',
        yr: 'SS2',
      },
      {
        q: "What does 'duplicity' mean?",
        o: [
          'Honesty and transparency',
          'Deceitfulness; the act of being deliberately deceptive in behaviour',
          'A type of legal document',
          'Extreme generosity',
        ],
        a: 1,
        e: "'Duplicity' refers to deceitfulness or dishonesty — pretending to feel or believe one thing while acting in a different, deceptive way. Example: 'His duplicity was exposed when his lies were discovered.'",
        h: 'This word relates to DECEIT and dishonest behaviour.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly uses a hyphen with written-out numbers?',
        o: [
          'She is twenty one years old.',
          'She is twenty-one years old.',
          'She is twenty, one years old.',
          'She is twenty;one years old.',
        ],
        a: 1,
        e: "Compound numbers from twenty-one to ninety-nine are hyphenated when written out: 'twenty-one', 'forty-five', 'sixty-three'. This is a standard rule of English number-writing.",
        h: 'Two-word numbers between 21-99 are joined with a hyphen.',
        yr: 'SS2',
      },
      {
        q: "To 'abscond' means to:",
        o: [
          "Openly announce one's departure",
          'Leave secretly, especially to escape from somewhere or avoid responsibility/punishment',
          'Celebrate a major achievement',
          'Carefully plan a future event',
        ],
        a: 1,
        e: "'Abscond' means to leave hurriedly and secretly, typically to escape from custody or avoid arrest, prosecution, or detection. Example: 'The accountant absconded with the company's funds.'",
        h: 'This word relates to ESCAPING or fleeing secretly, often after wrongdoing.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly uses an exclamation mark?',
        o: [
          'What time is it.',
          'What time is it!',
          'What a magnificent view!',
          'Please tell me what time it is!.',
        ],
        a: 2,
        e: "'What a magnificent view!' correctly uses an exclamation mark to express strong emotion (admiration/surprise). The other options either misuse exclamation marks for ordinary statements/questions or incorrectly combine punctuation marks.",
        h: 'Exclamation marks express STRONG EMOTION — surprise, admiration, alarm, or emphasis.',
        yr: 'SS2',
      },
      {
        q: "An 'alias' is:",
        o: [
          'A formal legal contract',
          "A false or assumed name, often used to conceal one's true identity",
          'A type of punctuation mark',
          'A medical condition',
        ],
        a: 1,
        e: "'Alias' refers to a false or alternative name used by a person, often to hide their real identity. Example: 'The criminal operated under several aliases to avoid detection.'",
        h: 'This word relates to a FALSE NAME used to hide identity.',
        yr: 'SS2',
      },
      {
        q: "What does 'ennui' mean?",
        o: [
          'Extreme excitement and energy',
          'A feeling of listlessness, boredom, and dissatisfaction from lack of activity/interest',
          'A formal greeting',
          'A type of celebration',
        ],
        a: 1,
        e: "'Ennui' describes a feeling of weariness, boredom, and lack of satisfaction, often arising from having nothing interesting to do. Example: 'A sense of ennui settled over him during the long holiday.'",
        h: 'This French-derived word relates to BOREDOM and listlessness.',
        yr: 'SS2',
      },
      {
        q: 'Choose the sentence that correctly uses a semi-colon to separate items in a complex list:',
        o: [
          'We visited Lagos, Nigeria, Accra, Ghana, and Dakar, Senegal.',
          'We visited Lagos, Nigeria; Accra, Ghana; and Dakar, Senegal.',
          'We visited Lagos; Nigeria, Accra; Ghana, and Dakar; Senegal.',
          'We visited Lagos, Nigeria: Accra, Ghana: and Dakar, Senegal.',
        ],
        a: 1,
        e: "When list items themselves contain commas (city, country pairs), semi-colons are used to clearly separate the items: 'Lagos, Nigeria; Accra, Ghana; and Dakar, Senegal.' This prevents confusion about where one item ends and the next begins.",
        h: 'When list items already contain commas, use SEMI-COLONS to separate the items clearly.',
        yr: 'SS2',
      },
      {
        q: "Which word describes someone or something 'extremely bad, miserable, or wretched'?",
        o: ['Maudlin', 'Abject', 'Dither', 'Emaciated'],
        a: 1,
        e: "'Abject' describes something experienced or present to the maximum degree, typically something very bad — extremely miserable, hopeless, or humiliating. Example: 'They lived in abject poverty.'",
        h: 'This word intensifies a NEGATIVE state — extremely bad/miserable.',
        yr: 'SS2',
      },
      {
        q: "To 'dither' means to:",
        o: [
          'Make a quick, confident decision',
          'Be indecisive; hesitate due to uncertainty about what to do',
          'Celebrate joyfully',
          'Speak very loudly',
        ],
        a: 1,
        e: "'Dither' means to hesitate or be uncertain about how to act, often wasting time through indecision. Example: 'Stop dithering and make a decision already!'",
        h: 'This word relates to HESITATION and being unable to decide.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly uses a hyphen with a prefix?',
        o: [
          'The expresident gave a speech.',
          'The ex-president gave a speech.',
          'The ex president gave a speech.',
          'The ex, president gave a speech.',
        ],
        a: 1,
        e: "'Ex-president' correctly uses a hyphen — certain prefixes like 'ex-', 'self-', and 'co-' are typically joined to the root word with a hyphen rather than directly attached or left as separate words.",
        h: 'Certain prefixes (ex-, self-, co-) typically need a hyphen to join to the root word.',
        yr: 'SS2',
      },
      {
        q: "'Emaciated' describes a person or animal that is:",
        o: [
          'Extremely strong and muscular',
          'Abnormally thin, typically as a result of illness or starvation',
          'Very tall',
          'Cheerful and lively',
        ],
        a: 1,
        e: "'Emaciated' describes a body that is abnormally thin, often due to severe illness, starvation, or malnutrition. Example: 'The emaciated refugees needed urgent medical attention.'",
        h: 'This word relates to extreme THINNESS, usually from illness or hunger.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly demonstrates the use of a colon to introduce an explanation?',
        o: [
          'There was one reason for his success, hard work.',
          'There was one reason for his success: hard work.',
          'There was one reason for his success; hard work.',
          'There was one reason for his success - hard work, that is.',
        ],
        a: 1,
        e: "A colon correctly introduces an explanation or elaboration of what was just stated: 'There was one reason for his success: hard work.' The colon signals that the following word(s) will clarify or explain the preceding statement.",
        h: 'A colon introduces an EXPLANATION of what came before it.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Sentence Types: Declarative, Imperative, Interrogative, and Exclamatory',
    topicCode: 'SS2-ENG-10',
    module: 'Lexis and Structure',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  Sentences in English can be classified by their FUNCTION (purpose) into four types: <span class="learn-keyword">declarative</span>, <span class="learn-keyword">interrogative</span>, <span class="learn-keyword">imperative</span>, and <span class="learn-keyword">exclamatory</span>. Each type has a distinct structure and punctuation pattern.\n</div>\n\n<h3 class="learn-subheading">1. Declarative Sentences</h3>\n<ul class="learn-list">\n  <li><strong>Function:</strong> Makes a statement; gives information</li>\n  <li><strong>Structure:</strong> Subject + Verb (+ Object/Complement)</li>\n  <li><strong>Punctuation:</strong> Ends with a full stop (.)</li>\n  <li><strong>Examples:</strong> \'Nigeria is the most populous country in Africa.\' / \'She enjoys reading novels.\'</li>\n</ul>\n\n<h3 class="learn-subheading">2. Interrogative Sentences</h3>\n<ul class="learn-list">\n  <li><strong>Function:</strong> Asks a question</li>\n  <li><strong>Structure:</strong> Often begins with auxiliary verb (do, does, did, is, are, can) or WH-word (who, what, where, when, why, how) — involves SUBJECT-VERB INVERSION</li>\n  <li><strong>Punctuation:</strong> Ends with a question mark (?)</li>\n  <li><strong>Examples:</strong> \'Where is the nearest hospital?\' / \'Did you complete your homework?\'</li>\n</ul>\n\n<h3 class="learn-subheading">3. Imperative Sentences</h3>\n<ul class="learn-list">\n  <li><strong>Function:</strong> Gives a command, instruction, request, or advice</li>\n  <li><strong>Structure:</strong> Begins with the BASE FORM of the verb; subject \'you\' is usually IMPLIED (not stated)</li>\n  <li><strong>Punctuation:</strong> Ends with a full stop (.) for instructions, or an exclamation mark (!) for urgent commands</li>\n  <li><strong>Examples:</strong> \'Close the door.\' / \'Please submit your assignment by Friday.\' / \'Stop!\'</li>\n</ul>\n\n<h3 class="learn-subheading">4. Exclamatory Sentences</h3>\n<ul class="learn-list">\n  <li><strong>Function:</strong> Expresses strong feeling/emotion (surprise, joy, anger, admiration)</li>\n  <li><strong>Structure:</strong> Often begins with \'What\' or \'How\' followed by an adjective/noun phrase</li>\n  <li><strong>Punctuation:</strong> Ends with an exclamation mark (!)</li>\n  <li><strong>Examples:</strong> \'What a beautiful day!\' / \'How clever she is!\'</li>\n</ul>\n\n<h3 class="learn-subheading">5. Sentence Structures by Complexity</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Type</th><th>Structure</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>Simple sentence</td><td>One independent clause</td><td>\'She sings.\'</td></tr>\n      <tr><td>Compound sentence</td><td>Two+ independent clauses joined by a coordinating conjunction (and, but, or, so)</td><td>\'She sings, and he dances.\'</td></tr>\n      <tr><td>Complex sentence</td><td>One independent clause + one or more dependent clauses</td><td>\'Although she was tired, she sang beautifully.\'</td></tr>\n      <tr><td>Compound-complex sentence</td><td>Two+ independent clauses + at least one dependent clause</td><td>\'Although she was tired, she sang, and the audience cheered.\'</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">6. Vocabulary: Words of the Week (Selection)</h3>\n<p class="learn-p"><strong>enmity</strong> (deep-seated hostility/hatred), <strong>laudatory</strong> (expressing praise), <strong>pinnacle</strong> (the highest point/peak of achievement), <strong>flabbergasted</strong> (utterly astonished), <strong>extant</strong> (still existing, not extinct/destroyed), <strong>laconic</strong> (using very few words; terse), <strong>pertinacious</strong> (persistent, holding firmly to a purpose), <strong>enervate</strong> (to weaken/drain of energy).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Declarative = statement (full stop). Interrogative = question (question mark, subject-verb inversion). Imperative = command (implied \'you\' subject, base verb form). Exclamatory = strong emotion (exclamation mark, often starts with What/How). Compound sentence = TWO independent clauses + coordinating conjunction. Complex sentence = independent + DEPENDENT clause.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Four sentence types by function: declarative (statement), interrogative (question), imperative (command, implied \'you\'), exclamatory (strong emotion). Sentence structures by complexity: simple (1 clause), compound (2+ independent clauses + conjunction), complex (independent + dependent clause), compound-complex (both features combined).\n</div>\n  ',
    questions: [
      {
        q: 'Which sentence is an example of a DECLARATIVE sentence?',
        o: [
          'Close the door!',
          'Where are you going?',
          'Nigeria is located in West Africa.',
          'What a wonderful surprise!',
        ],
        a: 2,
        e: "'Nigeria is located in West Africa' is a declarative sentence — it makes a statement/gives information and ends with a full stop. Declarative sentences are the most common sentence type, used to convey facts or opinions.",
        h: 'A declarative sentence makes a STATEMENT and ends with a full stop.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence is IMPERATIVE?',
        o: [
          'She is reading a novel.',
          'Please close the window.',
          'Is she reading a novel?',
          'What an interesting novel!',
        ],
        a: 1,
        e: "'Please close the window' is imperative — it gives a request/command, with the subject 'you' implied rather than stated, and uses the base form of the verb 'close'.",
        h: "Imperative sentences give COMMANDS/REQUESTS; the subject 'you' is usually not stated.",
        yr: 'SS2',
      },
      {
        q: "What is the implied SUBJECT in the imperative sentence 'Submit your assignment by Friday'?",
        o: ['Assignment', 'Friday', 'You (implied, not stated)', 'There is no subject'],
        a: 2,
        e: "Imperative sentences have an implied subject 'you' that is not explicitly stated. 'Submit your assignment by Friday' really means '(You) submit your assignment by Friday.'",
        h: "Commands are directed AT someone — that 'someone' (you) is the implied subject.",
        yr: 'SS2',
      },
      {
        q: 'Which of the following is an EXCLAMATORY sentence?',
        o: [
          'She arrived early.',
          'Did she arrive early?',
          'Arrive early!',
          'How early she arrived!',
        ],
        a: 3,
        e: "'How early she arrived!' is exclamatory — it expresses strong feeling/surprise and follows the typical exclamatory pattern of beginning with 'How' + adjective/adverb, ending in an exclamation mark.",
        h: "Exclamatory sentences often begin with 'What' or 'How' and express STRONG EMOTION.",
        yr: 'SS2',
      },
      {
        q: 'Which sentence demonstrates a COMPOUND sentence structure?',
        o: [
          'Although it rained, the match continued.',
          'She sings, and he dances.',
          'She sings.',
          'Because she was tired, she went to bed early.',
        ],
        a: 1,
        e: "'She sings, and he dances' is a compound sentence — it joins TWO independent clauses ('she sings' and 'he dances') using the coordinating conjunction 'and'. The other options are either simple sentences or complex sentences (with dependent clauses introduced by 'although' or 'because').",
        h: "A compound sentence has TWO INDEPENDENT clauses joined by 'and, but, or, so'.",
        yr: 'SS2',
      },
      {
        q: "Identify the sentence type of: 'Although she was tired, she sang beautifully.'",
        o: [
          'Simple sentence',
          'Compound sentence',
          'Complex sentence',
          'Compound-complex sentence',
        ],
        a: 2,
        e: "This is a COMPLEX sentence — it has ONE independent clause ('she sang beautifully') and ONE dependent clause ('although she was tired'). Complex sentences combine an independent clause with one or more dependent (subordinate) clauses.",
        h: "Look for ONE independent clause + ONE dependent clause (introduced by 'although', 'because', 'since', etc.).",
        yr: 'SS2',
      },
      {
        q: 'Which sentence is INTERROGATIVE?',
        o: [
          'The students completed the exam.',
          'Did the students complete the exam?',
          'Complete the exam now.',
          'What a difficult exam that was!',
        ],
        a: 1,
        e: "'Did the students complete the exam?' is interrogative — it asks a question, uses subject-verb inversion with the auxiliary verb 'did', and ends with a question mark.",
        h: 'Interrogative sentences ASK QUESTIONS and typically invert the subject and auxiliary verb.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence demonstrates a COMPOUND-COMPLEX structure?',
        o: [
          'She sings.',
          'She sings, and he dances.',
          'Although she was tired, she sang.',
          'Although she was tired, she sang, and the audience cheered.',
        ],
        a: 3,
        e: "This sentence has BOTH features: a dependent clause ('Although she was tired') AND two independent clauses joined by a coordinating conjunction ('she sang' + 'and' + 'the audience cheered'). This combination makes it compound-complex.",
        h: 'Look for a sentence with BOTH a dependent clause AND two+ independent clauses.',
        yr: 'SS2',
      },
      {
        q: "Which word means 'deep-seated hostility or hatred between people/groups'?",
        o: ['Laudatory', 'Enmity', 'Pinnacle', 'Extant'],
        a: 1,
        e: "'Enmity' refers to a state of deep, mutual hatred or hostility, often between long-standing enemies. Example: 'There was longstanding enmity between the two rival families.'",
        h: 'This word relates to deep HOSTILITY or HATRED between people.',
        yr: 'SS2',
      },
      {
        q: "What does 'flabbergasted' mean?",
        o: [
          'Completely calm and unmoved',
          'Utterly astonished or surprised',
          'Extremely angry',
          'Slightly amused',
        ],
        a: 1,
        e: "'Flabbergasted' describes a state of overwhelming surprise or astonishment, often leaving someone speechless. Example: 'She was flabbergasted when she won the lottery.'",
        h: 'This word describes EXTREME surprise or astonishment.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly punctuates an imperative sentence with mild urgency?',
        o: ['Stop?', 'Stop;', 'Stop.', 'Stop, please?'],
        a: 2,
        e: "'Stop.' correctly uses a full stop for a simple instruction. (If urgency/strong emotion were intended, 'Stop!' with an exclamation mark would also be appropriate, but a question mark or semi-colon would never be correct here.)",
        h: 'Standard instructions end with a FULL STOP; urgent commands may use an exclamation mark.',
        yr: 'SS2',
      },
      {
        q: "'Laconic' describes a person or style of speech/writing that is:",
        o: [
          'Excessively wordy and elaborate',
          'Using very few words; terse and concise',
          'Always polite and formal',
          'Difficult to understand',
        ],
        a: 1,
        e: "'Laconic' describes brevity in speech or writing — using few words to express ideas, often to the point of seeming brusque. Example: 'His laconic reply gave no further details.'",
        h: 'This word relates to using FEW WORDS — being terse/brief.',
        yr: 'SS2',
      },
      {
        q: "Which word means 'still existing; not extinct or destroyed'?",
        o: ['Extant', 'Enervate', 'Pertinacious', 'Pinnacle'],
        a: 0,
        e: "'Extant' describes something that still exists, especially referring to documents, species, or buildings that have survived from an earlier time. Example: 'The oldest extant manuscript dates from the 12th century.'",
        h: 'This word means STILL EXISTING (the opposite of extinct).',
        yr: 'SS2',
      },
      {
        q: "What does 'pertinacious' mean?",
        o: [
          'Easily discouraged or giving up quickly',
          'Persistent and holding firmly to a purpose, opinion, or course of action',
          'Extremely shy and withdrawn',
          'Lacking any ambition',
        ],
        a: 1,
        e: "'Pertinacious' describes someone who is stubbornly persistent or determined, holding firmly to their purpose despite obstacles. Example: 'Her pertinacious efforts eventually paid off.'",
        h: 'This word relates to PERSISTENCE and stubborn determination.',
        yr: 'SS2',
      },
      {
        q: "Identify the sentence type: 'What a magnificent performance that was!'",
        o: ['Declarative', 'Interrogative', 'Imperative', 'Exclamatory'],
        a: 3,
        e: "This is exclamatory — it begins with 'What' + adjective + noun phrase, expresses strong admiration, and ends with an exclamation mark, all typical features of exclamatory sentences.",
        h: "Strong emotion + 'What a...!' pattern = exclamatory sentence.",
        yr: 'SS2',
      },
      {
        q: "To 'enervate' someone means to:",
        o: [
          'Energise and motivate them',
          'Weaken them or drain them of energy/vitality',
          'Praise them highly',
          'Confuse them with complex information',
        ],
        a: 1,
        e: "'Enervate' means to cause someone to feel drained of energy or vitality — to weaken physically or mentally. Example: 'The intense heat enervated the entire team.'",
        h: 'This word means to WEAKEN or DRAIN someone of energy.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence demonstrates the typical SUBJECT-VERB INVERSION pattern of an interrogative sentence?',
        o: [
          'You are coming to the party.',
          'Are you coming to the party?',
          'Come to the party.',
          'What a great party that was!',
        ],
        a: 1,
        e: "'Are you coming to the party?' shows subject-verb inversion — the auxiliary verb 'are' comes BEFORE the subject 'you', which is typical of interrogative (question) sentences in English.",
        h: 'In questions, the auxiliary verb typically comes BEFORE the subject (inverted order).',
        yr: 'SS2',
      },
      {
        q: "'Laudatory' remarks are those that:",
        o: [
          'Criticise someone harshly',
          'Express praise or approval',
          'Are neutral and unbiased',
          'Contain a question',
        ],
        a: 1,
        e: "'Laudatory' describes speech or writing that expresses praise or commendation. Example: 'The principal gave a laudatory speech about the graduating class's achievements.'",
        h: 'This word relates to PRAISE — laudatory comments commend/compliment someone.',
        yr: 'SS2',
      },
      {
        q: "A 'pinnacle' refers to:",
        o: [
          'The lowest point of failure',
          'The highest point of achievement, success, or development',
          'A type of punctuation mark',
          'A simple, basic structure',
        ],
        a: 1,
        e: "'Pinnacle' refers to the highest or most successful point of something — the peak of achievement. Example: 'Winning the World Cup was the pinnacle of his football career.'",
        h: 'This word relates to the HIGHEST POINT of success or achievement.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence type typically begins with a WH-word (who, what, where, when, why, how) when seeking specific information?',
        o: ['Declarative', 'Interrogative', 'Imperative', 'Exclamatory (always)'],
        a: 1,
        e: "Interrogative sentences seeking specific information (as opposed to yes/no questions) typically begin with WH-words: 'Where are you going?' / 'Why did you leave early?' Note: exclamatory sentences can also begin with 'What' or 'How', but they express emotion, not questions.",
        h: 'WH-questions (who, what, where, when, why, how) seek specific INFORMATION through questions.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Formal Letter Writing: Letter of Complaint',
    topicCode: 'SS2-ENG-11',
    module: 'Composition',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  A <span class="learn-keyword">formal letter</span> is a written communication addressed to someone in an official, professional, or business capacity. The <span class="learn-keyword">letter of complaint</span> is one of the most commonly examined formal letter types in WAEC — it requires a clear, polite, but firm tone.\n</div>\n\n<h3 class="learn-subheading">1. Layout of a Formal Letter</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Part</th><th>Position/Content</th></tr></thead>\n    <tbody>\n      <tr><td>Writer\'s address</td><td>Top right corner (no name)</td></tr>\n      <tr><td>Date</td><td>Below the writer\'s address</td></tr>\n      <tr><td>Recipient\'s address</td><td>Left side, below the date (includes name/title if known)</td></tr>\n      <tr><td>Salutation</td><td>\'Dear Sir/Madam,\' or \'Dear Mr./Mrs. [Surname],\'</td></tr>\n      <tr><td>Subject heading (optional but recommended)</td><td>Underlined, summarising the letter\'s purpose</td></tr>\n      <tr><td>Body</td><td>Introduction, main content (in paragraphs), conclusion/request</td></tr>\n      <tr><td>Complimentary close</td><td>\'Yours faithfully,\' (if salutation is Sir/Madam) or \'Yours sincerely,\' (if salutation uses a name)</td></tr>\n      <tr><td>Signature</td><td>Full name (and sometimes designation) below the close</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">2. Structure of a Letter of Complaint</h3>\n<ol class="learn-list">\n  <li><strong>Paragraph 1:</strong> State the purpose of the letter and what is being complained about</li>\n  <li><strong>Paragraph 2:</strong> Give details of the problem (what happened, when, where, who was involved)</li>\n  <li><strong>Paragraph 3:</strong> Explain the effect/inconvenience caused by the problem</li>\n  <li><strong>Paragraph 4:</strong> State the desired action/resolution clearly (refund, replacement, apology, repair)</li>\n  <li><strong>Closing:</strong> Express hope for prompt action; thank the recipient</li>\n</ol>\n\n<h3 class="learn-subheading">3. Tone and Language</h3>\n<ul class="learn-list">\n  <li>Maintain a POLITE but FIRM tone — avoid being rude or aggressive</li>\n  <li>Be SPECIFIC — include dates, reference numbers, names where relevant</li>\n  <li>Use FORMAL register — avoid slang, contractions (don\'t write \'I didn\'t\', write \'I did not\')</li>\n  <li>State clearly what RESOLUTION you expect</li>\n</ul>\n\n<h3 class="learn-subheading">4. Example Salutation/Closing Pairs</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Salutation</th><th>Complimentary Close</th></tr></thead>\n    <tbody>\n      <tr><td>Dear Sir/Madam,</td><td>Yours faithfully,</td></tr>\n      <tr><td>Dear Mr. Adamu,</td><td>Yours sincerely,</td></tr>\n      <tr><td>Dear Manager,</td><td>Yours faithfully,</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">5. Vocabulary Development: Press and Journalism Words</h3>\n<p class="learn-p"><strong>headline</strong> (the title of a news article), <strong>byline</strong> (the writer\'s name on an article), <strong>editorial</strong> (an article expressing the publisher\'s opinion), <strong>scoop</strong> (an exclusive news story), <strong>tabloid</strong> (a newspaper with sensational, brief stories), <strong>columnist</strong> (a writer of a regular opinion piece), <strong>circulation</strong> (the number of copies distributed), <strong>correspondent</strong> (a reporter, often abroad/specialised), <strong>press release</strong> (an official statement issued to media), <strong>libel</strong> (a published false statement damaging reputation).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Match the closing to the salutation: \'Dear Sir/Madam\' → \'Yours faithfully\'; \'Dear Mr./Mrs. [Name]\' → \'Yours sincerely\'. Never use contractions in formal letters (write \'do not\', not \'don\'t\'). Letter of complaint structure: state problem → give details → explain effect → state desired resolution. Always include the writer\'s address but NOT the writer\'s name above it.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Formal letter layout: writer\'s address (top right, no name), date, recipient\'s address (left), salutation, subject heading, body (intro-details-effect-resolution), complimentary close matched to salutation, signature. Letter of complaint: polite but firm tone, specific details (dates/names), formal register (no contractions/slang), clear statement of desired resolution.\n</div>\n  ',
    questions: [
      {
        q: "Where is the WRITER'S ADDRESS placed in a formal letter?",
        o: [
          'Bottom left corner',
          'Top right corner',
          'Centre of the page',
          'It is not included in formal letters',
        ],
        a: 1,
        e: "The writer's address is conventionally placed in the TOP RIGHT corner of a formal letter, without the writer's name (the name appears later, with the signature).",
        h: "Formal letter layout: writer's address goes in the TOP RIGHT corner.",
        yr: 'SS2',
      },
      {
        q: "Which complimentary close should be used when the salutation is 'Dear Sir/Madam,'?",
        o: ['Yours sincerely,', 'Yours faithfully,', 'Best regards,', 'With love,'],
        a: 1,
        e: "'Yours faithfully,' is correctly paired with 'Dear Sir/Madam,' (used when the recipient's NAME is unknown). 'Yours sincerely,' is used when the recipient is addressed by name (e.g., 'Dear Mr. Adamu,').",
        h: 'Unknown name (Sir/Madam) = Yours faithfully. Known name = Yours sincerely.',
        yr: 'SS2',
      },
      {
        q: 'In a letter of complaint, what should the FIRST paragraph typically establish?',
        o: [
          "A detailed history of the writer's life",
          'The purpose of the letter and what is being complained about',
          'The complimentary close',
          'Random unrelated information',
        ],
        a: 1,
        e: 'The opening paragraph of a complaint letter should clearly state the purpose — what product/service is being complained about and the general nature of the problem — setting up the details that follow.',
        h: 'The first paragraph should clearly state WHY you are writing.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is INAPPROPRIATE language for a formal letter of complaint?',
        o: [
          'I am writing to express my dissatisfaction with...',
          'I would be grateful if you could...',
          "This is absolutely ridiculous and I'm furious!",
          'I look forward to your prompt response.',
        ],
        a: 2,
        e: "'This is absolutely ridiculous and I'm furious!' is inappropriately informal and aggressive for a formal letter. Even when complaining, formal letters should maintain a polite, professional tone — firm but not rude or overly emotional.",
        h: 'Formal complaints should be FIRM but POLITE — avoid aggressive/emotional language.',
        yr: 'SS2',
      },
      {
        q: 'What should the FINAL paragraph of a letter of complaint typically include?',
        o: [
          'A new, unrelated complaint',
          'A clear statement of the desired resolution (refund, replacement, apology, etc.)',
          "Personal information about the writer's family",
          'A joke to lighten the mood',
        ],
        a: 1,
        e: 'The concluding paragraph(s) of a complaint letter should clearly state what action/resolution the writer expects — whether a refund, replacement, repair, apology, or other specific remedy. This gives the recipient a clear understanding of what is needed.',
        h: 'End by clearly stating what YOU WANT done about the problem.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence avoids using a contraction, as required in FORMAL writing?',
        o: [
          "I didn't receive the item I ordered.",
          'I have not received the item I ordered.',
          "I haven't received the item I ordered.",
          "I won't accept this kind of service.",
        ],
        a: 1,
        e: "'I have not received...' avoids contractions entirely, which is the convention for formal letters. The other options use contractions ('didn't', 'haven't', 'won't'), which are appropriate for informal writing but should be avoided in formal correspondence.",
        h: "Formal letters avoid contractions — write out 'do not', 'have not', 'will not' in full.",
        yr: 'SS2',
      },
      {
        q: "In journalism, what is a 'byline'?",
        o: [
          'The title of a news article',
          "The writer's name printed on an article, usually below the headline",
          'A short summary at the start of an article',
          'An advertisement within a newspaper',
        ],
        a: 1,
        e: "A 'byline' is the line giving the author's name, typically appearing just below the headline of an article ('By John Adeyemi'). It credits the journalist who wrote the piece.",
        h: 'This term relates to who WROTE the article — their name credit.',
        yr: 'SS2',
      },
      {
        q: "What is a 'scoop' in journalism?",
        o: [
          'A type of newspaper format',
          'An exclusive news story obtained and published before other media outlets',
          'The total number of newspapers sold',
          'A correction notice',
        ],
        a: 1,
        e: "A 'scoop' refers to a news story that a journalist or publication obtains and publishes before any competitors — gaining a journalistic advantage. Example: 'The reporter got a major scoop on the corruption scandal.'",
        h: 'This term relates to being FIRST to report an exclusive story.',
        yr: 'SS2',
      },
      {
        q: "What does 'circulation' mean in the context of newspapers?",
        o: [
          'The editorial opinion of the newspaper',
          'The number of copies of a publication distributed or sold',
          'A type of news article',
          'The layout design of a newspaper',
        ],
        a: 1,
        e: "'Circulation' refers to the total number of copies of a newspaper or magazine that are distributed/sold, often used as a measure of a publication's popularity and reach.",
        h: 'This term relates to HOW MANY copies are distributed/sold.',
        yr: 'SS2',
      },
      {
        q: "Which sentence correctly uses the recipient's address in a formal letter when the recipient's name is UNKNOWN?",
        o: [
          'Dear John Adeyemi, The Customer Service Manager...',
          'The Customer Service Manager, ABC Company, Lagos. Dear Sir/Madam,',
          'Hello there, ABC Company...',
          'To whoever reads this, ABC Company...',
        ],
        a: 1,
        e: "When the recipient's specific name is unknown, the address uses their TITLE/POSITION (e.g., 'The Customer Service Manager') followed by the company address, and the salutation appropriately uses 'Dear Sir/Madam,'.",
        h: "Unknown name: address by TITLE/POSITION, then use 'Dear Sir/Madam,' as salutation.",
        yr: 'SS2',
      },
      {
        q: "What is an 'editorial' in a newspaper?",
        o: [
          'A photograph accompanying a news story',
          "An article expressing the newspaper's or publisher's official opinion on a current issue",
          'The advertisement section',
          'A weather forecast',
        ],
        a: 1,
        e: "An 'editorial' is an article, usually unsigned, that expresses the opinion of the newspaper's editorial board or publisher on a significant issue, distinct from objective news reporting.",
        h: "This term relates to the newspaper's OFFICIAL OPINION on an issue.",
        yr: 'SS2',
      },
      {
        q: 'Which of the following details should be included in the SECOND paragraph of a letter of complaint about a faulty product?',
        o: [
          'A general apology to the recipient',
          'Specific details: what happened, when it was purchased, where, and what exactly is faulty',
          "The writer's personal opinions about unrelated matters",
          'The complimentary close',
        ],
        a: 1,
        e: 'The body of a complaint letter should provide SPECIFIC, factual details: purchase date, location, product description, and the exact nature of the fault/problem. Specificity makes the complaint credible and actionable.',
        h: 'Details should be SPECIFIC and FACTUAL: dates, locations, exact problem description.',
        yr: 'SS2',
      },
      {
        q: "A 'tabloid' newspaper is typically characterised by:",
        o: [
          'Long, detailed investigative articles only',
          'Sensational, often shorter stories, frequently focused on celebrity or scandal content',
          'Exclusively academic/scholarly content',
          'No images or photographs at all',
        ],
        a: 1,
        e: "'Tabloid' newspapers are typically smaller in size and known for their sensational, attention-grabbing content — often celebrity gossip, scandal, or dramatic human-interest stories — as opposed to more serious 'broadsheet' newspapers.",
        h: 'This term relates to SENSATIONAL, often celebrity/scandal-focused news content.',
        yr: 'SS2',
      },
      {
        q: "What does 'libel' mean in journalism and law?",
        o: [
          "A true statement that damages someone's reputation",
          "A published false statement that damages a person's reputation",
          'An exclusive news story',
          'A correction printed in a newspaper',
        ],
        a: 1,
        e: "'Libel' refers to a false, defamatory statement made in print or other published/broadcast form that harms a person's reputation. (The spoken equivalent is called 'slander'.) Publishing libellous content can lead to legal consequences.",
        h: 'This word relates to FALSE, damaging statements published about someone.',
        yr: 'SS2',
      },
      {
        q: 'Which subject heading would be MOST appropriate for a letter of complaint about a delayed delivery?',
        o: [
          'Application for Employment',
          'Complaint Regarding Delayed Delivery of Order No. 12345',
          'Invitation to a Wedding Ceremony',
          'Notice of Resignation',
        ],
        a: 1,
        e: "'Complaint Regarding Delayed Delivery of Order No. 12345' is appropriately specific and relevant — it immediately tells the recipient the letter's purpose (a complaint) and the specific issue (delayed delivery), even including a reference number for clarity.",
        h: "A subject heading should be SPECIFIC and immediately convey the letter's PURPOSE.",
        yr: 'SS2',
      },
      {
        q: "A 'press release' is best described as:",
        o: [
          'A private letter between two friends',
          'An official statement issued by an organisation to the media for publication or broadcast',
          'A type of newspaper subscription',
          'A legal complaint filed in court',
        ],
        a: 1,
        e: "A 'press release' is a formal, written statement issued by an organisation, company, or individual to news outlets, typically announcing something newsworthy and intended for media coverage.",
        h: 'This term relates to an OFFICIAL STATEMENT given to the media.',
        yr: 'SS2',
      },
      {
        q: 'Why should a letter of complaint avoid being overly emotional or aggressive?',
        o: [
          'Because complaints should never express any dissatisfaction',
          'Because a polite, professional tone is more likely to be taken seriously and lead to a satisfactory resolution',
          'Because formal letters cannot mention any problems',
          'Because the recipient will not read an aggressive letter',
        ],
        a: 1,
        e: 'A professional, measured tone is more persuasive and effective — recipients are more likely to respond positively and helpfully to a polite, clearly-reasoned complaint than to an aggressive, emotional one, which may be dismissed or provoke a defensive response.',
        h: 'Professional tone is more PERSUASIVE and likely to achieve the desired RESOLUTION.',
        yr: 'SS2',
      },
      {
        q: "What is a 'correspondent' in journalism?",
        o: [
          'A person who writes letters to the editor',
          'A reporter, often based in a specific location or specialising in a particular subject area, who provides news reports',
          "A newspaper's owner",
          'A reader who subscribes to a newspaper',
        ],
        a: 1,
        e: "A 'correspondent' is a journalist who reports on news from a particular place (e.g., 'foreign correspondent') or specialises in a specific subject area (e.g., 'sports correspondent'), often providing firsthand or on-location reporting.",
        h: 'This term relates to a REPORTER, often specialised by location or subject.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following best completes a polite request for action in a letter of complaint?',
        o: [
          "You'd better fix this immediately or else!",
          'I demand a refund right now without question.',
          'I would be grateful if you could arrange for a replacement at your earliest convenience.',
          'Fix it now.',
        ],
        a: 2,
        e: "'I would be grateful if you could arrange for a replacement at your earliest convenience' politely but clearly states the desired action, using appropriately formal and courteous language — the hallmark of an effective formal complaint.",
        h: 'Look for the option that is both POLITE and CLEAR about the desired action.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is the most appropriate way to begin a formal letter of complaint?',
        o: [
          "Hey there, I've got a big problem with your company!",
          'I am writing to express my dissatisfaction with the laptop I purchased from your store on 15th March.',
          'You guys really messed up this time.',
          'Just wanted to let you know something went wrong.',
        ],
        a: 1,
        e: "'I am writing to express my dissatisfaction with the laptop I purchased from your store on 15th March' is appropriately formal, specific (includes the product and date), and professionally states the purpose of the letter — exactly what a strong opening to a formal complaint requires.",
        h: 'Look for the FORMAL, SPECIFIC opening that clearly states the purpose with relevant details.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Informal Letter Writing: Letter to a Friend',
    topicCode: 'SS2-ENG-12',
    module: 'Composition',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  An <span class="learn-keyword">informal letter</span> is a personal letter written to family or friends, using a relaxed, conversational tone. Unlike formal letters, informal letters allow for warmth, humour, and personal expression, while still following a recognisable structure.\n</div>\n\n<h3 class="learn-subheading">1. Layout of an Informal Letter</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Part</th><th>Content</th></tr></thead>\n    <tbody>\n      <tr><td>Writer\'s address</td><td>Top right corner (simpler than formal — may omit postal code in some styles)</td></tr>\n      <tr><td>Date</td><td>Below the address</td></tr>\n      <tr><td>Salutation</td><td>\'Dear [First Name],\' e.g., \'Dear Chioma,\'</td></tr>\n      <tr><td>Opening</td><td>A friendly opening line — asking about wellbeing, referencing previous contact</td></tr>\n      <tr><td>Body</td><td>Main content in a warm, conversational tone — news, stories, feelings</td></tr>\n      <tr><td>Closing</td><td>A friendly closing remark (looking forward to a reply, sending regards)</td></tr>\n      <tr><td>Complimentary close</td><td>\'Yours affectionately,\' \'Love,\' \'Your friend,\' \'Best wishes,\'</td></tr>\n      <tr><td>Signature</td><td>First name only (no surname needed)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">2. Differences Between Formal and Informal Letters</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Feature</th><th>Formal Letter</th><th>Informal Letter</th></tr></thead>\n    <tbody>\n      <tr><td>Salutation</td><td>Dear Sir/Madam, Dear Mr. [Surname],</td><td>Dear [First Name],</td></tr>\n      <tr><td>Tone</td><td>Polite, professional, distant</td><td>Warm, friendly, personal</td></tr>\n      <tr><td>Language</td><td>No contractions, formal vocabulary</td><td>Contractions allowed, colloquial expressions</td></tr>\n      <tr><td>Complimentary close</td><td>Yours faithfully/sincerely,</td><td>Love, Best wishes, Your friend,</td></tr>\n      <tr><td>Signature</td><td>Full name</td><td>First name only</td></tr>\n      <tr><td>Content</td><td>Single, focused purpose</td><td>Multiple topics, personal news</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">3. Sample Content for \'Letter to a Friend in Another Town\'</h3>\n<ul class="learn-list">\n  <li>Opening pleasantries (How are you? How is your family?)</li>\n  <li>News about your own life (school, family, recent events)</li>\n  <li>Questions about the friend\'s life/situation</li>\n  <li>An invitation, request, or shared plan</li>\n  <li>Closing with affection and anticipation of a reply</li>\n</ul>\n\n<h3 class="learn-subheading">4. Vocabulary: Words of the Week (Selection)</h3>\n<p class="learn-p"><strong>cherish</strong> (to hold dear, treasure), <strong>capricious</strong> (unpredictable, changing mood/behaviour suddenly), <strong>eschew</strong> (to deliberately avoid), <strong>perusal</strong> (a careful reading/examination), <strong>renunciation</strong> (formal rejection/giving up of something), <strong>spurious</strong> (false, not genuine), <strong>tenable</strong> (able to be defended/maintained, as an argument), <strong>turgid</strong> (overly complex/pompous in language; also swollen), <strong>antediluvian</strong> (extremely old-fashioned/outdated).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Informal letters use FIRST NAME ONLY in salutation and signature. Contractions ARE allowed (unlike formal letters). Closing phrases like \'Love,\' \'Best wishes,\' or \'Your friend,\' replace \'Yours faithfully/sincerely.\' Content can cover MULTIPLE topics (unlike formal letters, which focus on one issue). WAEC examiners check for appropriate WARMTH and PERSONAL tone in informal letters.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Informal letter layout: address, date, \'Dear [First Name],\' friendly opening, conversational body (multiple topics allowed), warm closing, affectionate complimentary close (Love/Best wishes), first name signature. Differs from formal letters in: tone (warm vs professional), language (contractions allowed), salutation/close style, and content focus (personal vs single purpose).\n</div>\n  ',
    questions: [
      {
        q: 'How should the SALUTATION of an informal letter to a friend typically be written?',
        o: [
          'Dear Sir/Madam,',
          'Dear Mr. Adeyemi,',
          "Dear Chioma, (using the friend's first name)",
          'To Whom It May Concern,',
        ],
        a: 2,
        e: "Informal letters use the recipient's FIRST NAME in the salutation: 'Dear Chioma,'. This reflects the personal, friendly relationship, unlike formal letters which use 'Sir/Madam' or surnames with titles.",
        h: 'Informal letters address the FIRST NAME of the friend/family member directly.',
        yr: 'SS2',
      },
      {
        q: 'Which complimentary close is MOST appropriate for an informal letter to a close friend?',
        o: [
          'Yours faithfully,',
          'Yours sincerely,',
          'Best wishes, / Love, / Your friend,',
          'Respectfully yours,',
        ],
        a: 2,
        e: "Informal letters use warm, personal closings like 'Best wishes,' 'Love,' or 'Your friend,' reflecting the close relationship. 'Yours faithfully' and 'Yours sincerely' are reserved for formal correspondence.",
        h: 'Informal closes show WARMTH and PERSONAL connection — not formal/distant phrases.',
        yr: 'SS2',
      },
      {
        q: 'How should the writer SIGN an informal letter to a friend?',
        o: [
          'Full name with title (e.g., Mr. John Adeyemi)',
          'First name only (e.g., John)',
          'Initials only',
          'No signature is needed',
        ],
        a: 1,
        e: 'Informal letters are signed with the FIRST NAME only, reflecting the personal, casual nature of the correspondence — unlike formal letters, which require the full name (and sometimes designation).',
        h: 'Informal letters use just the FIRST NAME for the signature.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is a key difference between formal and informal letters?',
        o: [
          'Formal letters always use contractions; informal letters never do',
          'Informal letters allow contractions and colloquial language; formal letters avoid them, using complete, formal expressions',
          'Formal letters never include a date',
          'Informal letters must always be typed, never handwritten',
        ],
        a: 1,
        e: "Informal letters permit contractions (I'm, don't, can't) and casual/colloquial expressions because of their personal nature. Formal letters avoid contractions, using complete words and more formal vocabulary throughout.",
        h: 'Contractions are ALLOWED in informal letters but AVOIDED in formal letters.',
        yr: 'SS2',
      },
      {
        q: 'Unlike a formal letter (which usually focuses on ONE main purpose), an informal letter can typically:',
        o: [
          'Only discuss one topic as well',
          'Cover multiple topics — personal news, questions, invitations — in a conversational flow',
          'Never include any questions to the recipient',
          'Only be written to family members, never friends',
        ],
        a: 1,
        e: 'Informal letters, reflecting natural conversation between friends/family, often cover MULTIPLE topics in one letter — sharing news, asking questions, discussing plans — moving conversationally between subjects, unlike the single-focus structure of formal letters.',
        h: 'Informal letters can naturally cover SEVERAL topics, like a real conversation.',
        yr: 'SS2',
      },
      {
        q: "What does 'cherish' mean?",
        o: [
          'To completely ignore or dismiss something',
          'To hold something dear; to treasure and care for deeply',
          'To formally reject or give up something',
          'To examine something very carefully',
        ],
        a: 1,
        e: "'Cherish' means to protect and care for something/someone lovingly, treasuring them deeply. Example: 'She cherishes the memories of her childhood.'",
        h: 'This word relates to TREASURING and holding something/someone dear.',
        yr: 'SS2',
      },
      {
        q: "Which word describes someone who is 'capricious' — likely to change mood or behaviour suddenly and unpredictably?",
        o: [
          'Predictable and consistent',
          'Capricious — given to sudden, unpredictable changes in mood or behaviour',
          'Always calm and steady',
          'Extremely shy',
        ],
        a: 1,
        e: "'Capricious' describes a person (or sometimes weather/circumstances) prone to sudden, unpredictable changes — unreliable in their moods or decisions. Example: 'His capricious nature made it hard to plan anything with him.'",
        h: 'This word means UNPREDICTABLE — given to sudden changes.',
        yr: 'SS2',
      },
      {
        q: "To 'eschew' something means to:",
        o: [
          'Embrace and welcome it eagerly',
          'Deliberately avoid or abstain from it',
          'Examine it very carefully',
          'Formally announce it',
        ],
        a: 1,
        e: "'Eschew' means to deliberately avoid doing something or using something, often for moral or practical reasons. Example: 'She decided to eschew junk food in favour of a healthier diet.'",
        h: 'This word means to deliberately AVOID something.',
        yr: 'SS2',
      },
      {
        q: "What does 'perusal' mean?",
        o: [
          'A quick, careless glance',
          'A careful, thorough reading or examination',
          'A formal celebration',
          'A loud argument',
        ],
        a: 1,
        e: "'Perusal' refers to a careful and thorough reading or examination of something, especially written material. Example: 'After careful perusal of the contract, she signed it.'",
        h: 'This word relates to a CAREFUL, thorough READING of something.',
        yr: 'SS2',
      },
      {
        q: "Which word means 'false or not genuine, especially when something pretends to be authentic'?",
        o: ['Tenable', 'Spurious', 'Turgid', 'Antediluvian'],
        a: 1,
        e: "'Spurious' describes something that is fake, false, or not genuine, especially a claim, argument, or document presented as authentic. Example: 'The spurious claims were quickly debunked by experts.'",
        h: 'This word means FAKE or NOT GENUINE — false despite appearing real.',
        yr: 'SS2',
      },
      {
        q: "An argument described as 'tenable' is one that:",
        o: [
          'Cannot be defended or supported with evidence',
          'Can be defended, maintained, or supported reasonably with evidence/logic',
          'Is completely irrelevant to the discussion',
          'Has already been proven false',
        ],
        a: 1,
        e: "'Tenable' describes a position, theory, or argument that can be reasonably defended or maintained — supported by sufficient evidence or logical reasoning. Example: 'Her tenable argument convinced the panel.'",
        h: 'This word means DEFENSIBLE — capable of being supported/maintained.',
        yr: 'SS2',
      },
      {
        q: "Writing or speech described as 'turgid' is:",
        o: [
          'Clear, simple, and easy to understand',
          'Overly complex, pompous, or swollen in language, often hard to follow',
          'Very short and concise',
          'Humorous and light-hearted',
        ],
        a: 1,
        e: "'Turgid' describes writing or speech that is excessively elaborate, complicated, or pompous — difficult to read or understand due to overblown language. Example: 'The turgid legal document confused most readers.'",
        h: 'This word means OVERLY COMPLEX/pompous (in language) — difficult to follow.',
        yr: 'SS2',
      },
      {
        q: "Something described as 'antediluvian' is:",
        o: [
          'Extremely modern and futuristic',
          'Extremely old-fashioned or outdated, as if from before the biblical flood',
          'Related to water and rivers specifically',
          'A type of formal document',
        ],
        a: 1,
        e: "'Antediluvian' (literally 'before the flood') figuratively describes something extremely old-fashioned, outdated, or primitive. Example: 'His antediluvian views on technology frustrated his children.'",
        h: "This word means EXTREMELY OUTDATED — figuratively 'from before the flood'.",
        yr: 'SS2',
      },
      {
        q: "What does 'renunciation' mean?",
        o: [
          'The act of accepting or embracing something fully',
          'The formal rejection or giving up of a belief, claim, position, or habit',
          'A type of formal celebration',
          'A careful reading of a document',
        ],
        a: 1,
        e: "'Renunciation' refers to the formal act of giving up or rejecting something — a right, claim, belief, or habit. Example: 'His renunciation of his title surprised the royal family.'",
        h: 'This word relates to formally GIVING UP or REJECTING something.',
        yr: 'SS2',
      },
      {
        q: 'Which opening line would be MOST appropriate for an informal letter to a friend in another town?',
        o: [
          'I am writing to formally notify you of...',
          'Dear Sir, I wish to draw your attention to...',
          'Hi Tunde! How have you been? It feels like ages since we last spoke!',
          'To Whom It May Concern,',
        ],
        a: 2,
        e: "'Hi Tunde! How have you been? It feels like ages since we last spoke!' is warm, personal, and conversational — exactly the tone expected in an informal letter to a friend. The other options are inappropriately formal for this context.",
        h: 'Look for a WARM, PERSONAL opening that reflects genuine friendship.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following sentences demonstrates appropriate INFORMAL register (use of contractions/casual language)?',
        o: [
          'I am writing to inform you that I shall not be able to attend.',
          "I can't wait to see you again — it's been way too long!",
          'I would be grateful if you could kindly respond at your earliest convenience.',
          'Please be advised that the meeting has been rescheduled.',
        ],
        a: 1,
        e: "\"I can't wait to see you again — it's been way too long!\" uses contractions ('can't', 'it's') and casual, enthusiastic language appropriate for an informal letter between friends. The other options are too formal/stiff for this context.",
        h: 'Look for contractions and CASUAL, enthusiastic language typical of friendly writing.',
        yr: 'SS2',
      },
      {
        q: 'In terms of content, what is a common feature of letters between friends discussing an upcoming visit or event?',
        o: [
          'Strictly formal legal language with no personal touches',
          "Personal questions about the friend's wellbeing, sharing of news, and discussion of shared plans/invitations",
          'Only business-related matters with no personal content',
          'Complete avoidance of any questions to the recipient',
        ],
        a: 1,
        e: 'Informal letters between friends naturally include personal elements: asking how the friend is doing, sharing recent news/experiences, and discussing shared plans like visits or events — reflecting the genuine, personal nature of the relationship.',
        h: 'Informal letters include PERSONAL elements: wellbeing questions, shared news, plans.',
        yr: 'SS2',
      },
      {
        q: 'Which feature distinguishes the SALUTATION style of an informal letter from a formal one?',
        o: [
          'Informal letters never have a salutation at all',
          "Informal letters use first names; formal letters use titles/surnames or 'Sir/Madam'",
          'Both use exactly the same salutation style always',
          'Formal letters always use first names too',
        ],
        a: 1,
        e: "The key distinguishing feature: informal letters address the recipient by FIRST NAME ('Dear Tunde,'), while formal letters use TITLES with surnames ('Dear Mr. Adeyemi,') or 'Sir/Madam' when the name is unknown.",
        h: 'First name = informal; Title + surname (or Sir/Madam) = formal.',
        yr: 'SS2',
      },
      {
        q: "Why might a WAEC examiner deduct marks from an informal letter that uses an overly formal tone throughout (e.g., 'I am writing to inform you...')?",
        o: [
          'Formal tone is always preferred regardless of context',
          'The task specifically requires an INFORMAL register matching the personal relationship; using formal language fails to meet this requirement',
          'Examiners never consider tone in marking',
          'Informal letters should not have any structure at all',
        ],
        a: 1,
        e: "WAEC essay marking specifically assesses whether the candidate's writing matches the REQUIRED REGISTER for the task. An informal letter using inappropriately formal language fails to demonstrate the candidate's ability to adapt tone/register to context — a key writing skill.",
        h: 'Marks are awarded for matching the APPROPRIATE register/tone to the task type.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following closing sentences is most appropriate for an informal letter before the complimentary close?',
        o: [
          'I trust this letter finds you in good health and look forward to your earliest written response.',
          'Do write back soon and tell me all your news — I miss you loads!',
          'Please be advised that a response is required within fourteen days.',
          'Kindly acknowledge receipt of this correspondence at your convenience.',
        ],
        a: 1,
        e: "'Do write back soon and tell me all your news — I miss you loads!' is warm, casual, and personal — exactly matching the conversational tone expected at the close of an informal letter. The other options are all inappropriately formal/stiff for a letter to a friend.",
        h: 'Look for the WARM, CASUAL closing that expresses genuine affection, not formal business language.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Sequence of Tenses',
    topicCode: 'SS2-ENG-13',
    module: 'Lexis and Structure',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  The <span class="learn-keyword">sequence of tenses</span> refers to the grammatical rules governing how the tense of a verb in one clause relates to (and is often determined by) the tense of the verb in another clause within the same sentence — especially in REPORTED SPEECH and complex sentences.\n</div>\n\n<h3 class="learn-subheading">1. The Basic Rule</h3>\n<p class="learn-p">When the main (reporting) clause is in the PAST TENSE, the verb in the subordinate clause usually also shifts to a PAST form — this is called \'backshift\'.</p>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Direct/Original</th><th>Reported (after past reporting verb)</th></tr></thead>\n    <tbody>\n      <tr><td>Present Simple: \'I am happy.\'</td><td>Past Simple: He said he was happy.</td></tr>\n      <tr><td>Present Continuous: \'I am eating.\'</td><td>Past Continuous: He said he was eating.</td></tr>\n      <tr><td>Present Perfect: \'I have finished.\'</td><td>Past Perfect: He said he had finished.</td></tr>\n      <tr><td>Past Simple: \'I went home.\'</td><td>Past Perfect: He said he had gone home.</td></tr>\n      <tr><td>Will: \'I will come.\'</td><td>Would: He said he would come.</td></tr>\n      <tr><td>Can: \'I can swim.\'</td><td>Could: He said he could swim.</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">2. Exceptions to Backshift</h3>\n<ul class="learn-list">\n  <li><strong>Universal/general truths:</strong> No backshift needed — \'The teacher said the earth revolves around the sun\' (NOT \'revolved\', because this is always true)</li>\n  <li><strong>If the reporting verb is in the PRESENT tense:</strong> No backshift — \'He says he IS tired\' (not \'was\')</li>\n</ul>\n\n<h3 class="learn-subheading">3. Sequence of Tenses in Complex Sentences (Time Clauses)</h3>\n<ul class="learn-list">\n  <li>In clauses introduced by \'when\', \'before\', \'after\', \'since\', \'as soon as\' — the SAME general time frame is usually maintained: \'When she ARRIVED, we WERE waiting.\' (both past)</li>\n  <li>\'Since\' + Past Simple is used with Present Perfect in the main clause: \'I have lived here SINCE I WAS born.\'</li>\n</ul>\n\n<h3 class="learn-subheading">4. Conditional Sentences and Tense Sequence</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Type</th><th>If-clause</th><th>Main clause</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>Zero (general truth)</td><td>Present Simple</td><td>Present Simple</td><td>If you heat water, it boils.</td></tr>\n      <tr><td>First (real future)</td><td>Present Simple</td><td>Will + base verb</td><td>If it rains, I will stay home.</td></tr>\n      <tr><td>Second (unreal present)</td><td>Past Simple</td><td>Would + base verb</td><td>If I were rich, I would travel.</td></tr>\n      <tr><td>Third (unreal past)</td><td>Past Perfect</td><td>Would have + past participle</td><td>If I had studied, I would have passed.</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">5. Vocabulary: Words of the Week (Selection)</h3>\n<p class="learn-p"><strong>propriety</strong> (correctness of behaviour/morals), <strong>exigent</strong> (urgent, requiring immediate action), <strong>forestall</strong> (to prevent by acting first), <strong>tranquil</strong> (calm, peaceful), <strong>heterogeneous</strong> (diverse, made of different elements), <strong>unctuous</strong> (excessively flattering/insincere), <strong>canvass</strong> (to solicit votes/opinions), <strong>carouse</strong> (to drink and enjoy oneself noisily), <strong>nefarious</strong> (wicked, criminal), <strong>novice</strong> (a beginner, inexperienced person).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Backshift rule: PAST reporting verb → subordinate clause verb shifts BACK one tense (present→past, past→past perfect). NO backshift for universal truths or when reporting verb is present tense. Conditionals: Zero/First use present forms; Second uses past simple + would; Third uses past perfect + would have + past participle.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Sequence of tenses: when reporting verb is PAST, subordinate clause backshifts (present→past, past→past perfect, will→would, can→could). Exceptions: universal truths (no backshift), present reporting verb (no backshift). Time clauses maintain consistent time frame. Conditionals: Zero (present+present), First (present+will), Second (past+would), Third (past perfect+would have+participle).\n</div>\n  ',
    questions: [
      {
        q: "Convert to reported speech: He said, 'I am tired.'",
        o: [
          'He said that he is tired.',
          'He said that he was tired.',
          'He said that he will be tired.',
          'He said that he has been tired.',
        ],
        a: 1,
        e: "When the reporting verb 'said' is in the PAST tense, the present simple 'am' backshifts to past simple 'was'. This is the standard sequence of tenses rule for reported speech.",
        h: "Past reporting verb ('said') → present tense backshifts to past tense.",
        yr: 'SS2',
      },
      {
        q: "Which sentence correctly reports: She said, 'I have finished my homework.'",
        o: [
          'She said that she has finished her homework.',
          'She said that she had finished her homework.',
          'She said that she finishes her homework.',
          'She said that she will finish her homework.',
        ],
        a: 1,
        e: "Present Perfect ('have finished') backshifts to Past Perfect ('had finished') when the reporting verb is in the past tense. This follows the standard sequence of tenses pattern.",
        h: 'Present Perfect backshifts to PAST PERFECT after a past reporting verb.',
        yr: 'SS2',
      },
      {
        q: "Choose the correctly reported sentence for: He said, 'I went to the market yesterday.'",
        o: [
          'He said that he goes to the market.',
          'He said that he had gone to the market the day before.',
          'He said that he will go to the market.',
          'He said that he is going to the market.',
        ],
        a: 1,
        e: "Past Simple ('went') backshifts to Past Perfect ('had gone') when reported with a past reporting verb. Also note: 'yesterday' changes to 'the day before' (time expression shift required in reported speech).",
        h: "Past Simple backshifts to PAST PERFECT; 'yesterday' becomes 'the day before'.",
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly applies the EXCEPTION to backshift for universal/general truths?',
        o: [
          'The teacher said that water boiled at 100 degrees Celsius.',
          'The teacher said that water boils at 100 degrees Celsius.',
          'The teacher said that water had boiled at 100 degrees Celsius.',
          'The teacher said that water will boil at 100 degrees Celsius.',
        ],
        a: 1,
        e: "Universal/general truths (scientific facts) do NOT undergo backshift, even when the reporting verb is past — 'water boils at 100°C' remains in the present tense because it is always true, regardless of when it was said.",
        h: 'Scientific/universal FACTS keep their PRESENT tense even in past-tense reported speech.',
        yr: 'SS2',
      },
      {
        q: "In the sentence 'If I were rich, I would travel the world,' which TYPE of conditional is this?",
        o: ['Zero conditional', 'First conditional', 'Second conditional', 'Third conditional'],
        a: 2,
        e: "This is a SECOND conditional — used for unreal/hypothetical PRESENT situations. It follows the pattern: If + Past Simple ('were'), + would + base verb ('would travel'). It expresses an imaginary present scenario.",
        h: "Past Simple + 'would' + base verb = Second conditional (unreal present).",
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly demonstrates a THIRD conditional structure?',
        o: [
          'If it rains, I will stay home.',
          'If I were rich, I would travel.',
          'If I had studied harder, I would have passed the exam.',
          'If you heat water, it boils.',
        ],
        a: 2,
        e: "'If I had studied harder, I would have passed the exam' is a THIRD conditional — used for unreal PAST situations (the studying didn't happen, so the passing didn't happen either). Structure: If + Past Perfect, + would have + past participle.",
        h: "Past Perfect + 'would have' + past participle = Third conditional (unreal past).",
        yr: 'SS2',
      },
      {
        q: 'Choose the correct sentence demonstrating a ZERO conditional (general truth):',
        o: [
          'If you heat water, it boils.',
          'If you heat water, it will boil.',
          'If you heated water, it would boil.',
          'If you had heated water, it would have boiled.',
        ],
        a: 0,
        e: "'If you heat water, it boils' is a ZERO conditional — both clauses use Present Simple to express a general truth/scientific fact that is always true under the stated condition.",
        h: 'Present Simple + Present Simple = Zero conditional (always true/general fact).',
        yr: 'SS2',
      },
      {
        q: "Which modal verb correctly replaces 'can' when backshifted in reported speech?",
        o: ['May', 'Could', 'Might', 'Should'],
        a: 1,
        e: "'Can' backshifts to 'could' in reported speech: 'I can swim' → 'He said he could swim.' This is one of the standard modal verb shifts in the sequence of tenses rule.",
        h: 'Can → Could (the standard backshift for this modal verb).',
        yr: 'SS2',
      },
      {
        q: "What does 'exigent' mean?",
        o: [
          'Calm and relaxed, requiring no immediate action',
          'Urgent; requiring immediate action or attention',
          'Extremely old-fashioned',
          'Excessively flattering',
        ],
        a: 1,
        e: "'Exigent' describes a situation requiring immediate action or attention — pressing, urgent. Example: 'The exigent circumstances required an immediate response from the government.'",
        h: 'This word means URGENT — requiring immediate attention.',
        yr: 'SS2',
      },
      {
        q: "To 'forestall' something means to:",
        o: [
          'Allow it to happen without interference',
          'Prevent it by acting in advance, anticipating and acting before it happens',
          'Celebrate its occurrence',
          'Document it in detail after it happens',
        ],
        a: 1,
        e: "'Forestall' means to prevent or hinder something by taking action beforehand — to act in anticipation. Example: 'The government took measures to forestall the economic crisis.'",
        h: 'This word means to PREVENT by acting FIRST/in advance.',
        yr: 'SS2',
      },
      {
        q: "Which word describes a place or state that is 'calm and peaceful'?",
        o: ['Nefarious', 'Tranquil', 'Unctuous', 'Heterogeneous'],
        a: 1,
        e: "'Tranquil' describes a state of calmness and peace, free from disturbance. Example: 'The tranquil lake reflected the evening sky perfectly.'",
        h: 'This word means CALM and PEACEFUL.',
        yr: 'SS2',
      },
      {
        q: "'Heterogeneous' describes something that is:",
        o: [
          'Made up of uniform, identical elements',
          'Diverse, made up of different/varied elements or kinds',
          'Extremely small in size',
          'Related only to chemistry',
        ],
        a: 1,
        e: "'Heterogeneous' describes a mixture or group consisting of diverse, dissimilar elements. Example: 'Nigeria has a heterogeneous population with over 250 ethnic groups.' (Its opposite is 'homogeneous', meaning uniform/similar.)",
        h: 'This word means DIVERSE/VARIED (opposite: homogeneous = uniform).',
        yr: 'SS2',
      },
      {
        q: "Which word describes speech or behaviour that is 'excessively flattering or insincere, often unpleasantly so'?",
        o: ['Tranquil', 'Unctuous', 'Exigent', 'Forestall'],
        a: 1,
        e: "'Unctuous' describes excessively flattering, ingratiating, or insincere behaviour — often making others uncomfortable. Example: 'The unctuous salesman's compliments seemed insincere.'",
        h: 'This word means EXCESSIVELY FLATTERING in an unpleasant, insincere way.',
        yr: 'SS2',
      },
      {
        q: "What does 'nefarious' mean?",
        o: [
          'Honest and morally upright',
          'Wicked or criminal, especially involving illegal activity',
          'Calm and peaceful',
          'Extremely generous',
        ],
        a: 1,
        e: "'Nefarious' describes actions or persons that are wicked, villainous, or criminal — typically involving deliberate wrongdoing. Example: 'The criminal gang's nefarious activities included drug trafficking.'",
        h: 'This word means WICKED or CRIMINAL.',
        yr: 'SS2',
      },
      {
        q: "A 'novice' is:",
        o: [
          'An expert with many years of experience',
          'A beginner; someone new to and inexperienced in a particular activity or field',
          'A person who has retired from their profession',
          'A formal title of nobility',
        ],
        a: 1,
        e: "'Novice' refers to a person who is new to a particular skill, job, or activity — a beginner lacking experience. Example: 'As a novice driver, she was nervous on the highway.'",
        h: 'This word means a BEGINNER — someone new and inexperienced.',
        yr: 'SS2',
      },
      {
        q: "Choose the correctly sequenced sentence with a time clause: 'When she ___ (arrive), we ___ (wait) at the station.'",
        o: [
          'arrives / wait',
          'arrived / were waiting',
          'will arrive / wait',
          'has arrived / are waiting',
        ],
        a: 1,
        e: "'When she arrived, we were waiting' correctly maintains a consistent PAST time frame across both clauses — the past simple action ('arrived') interrupting an ongoing past action ('were waiting'), a common pattern with time clauses.",
        h: "Time clauses with 'when' typically maintain a CONSISTENT past time frame.",
        yr: 'SS2',
      },
      {
        q: "Which sentence demonstrates the correct use of 'since' with the Present Perfect?",
        o: [
          'I have lived here since I am born.',
          'I have lived here since I was born.',
          'I lived here since I am born.',
          'I am living here since I was born.',
        ],
        a: 1,
        e: "'I have lived here since I was born' correctly pairs Present Perfect ('have lived') in the main clause with Past Simple ('was born') after 'since' — this is the standard structure for expressing duration from a specific past point to now.",
        h: "'Since' + past time point pairs with Present Perfect in the main clause.",
        yr: 'SS2',
      },
      {
        q: "Convert to reported speech: She said, 'I will help you tomorrow.'",
        o: [
          'She said that she will help me tomorrow.',
          'She said that she would help me the following day.',
          'She said that she helps me tomorrow.',
          'She said that she has helped me tomorrow.',
        ],
        a: 1,
        e: "'Will' backshifts to 'would', and the time expression 'tomorrow' changes to 'the following day' (or 'the next day') when reporting in the past tense — both backshift and time-expression shift rules apply together.",
        h: "Will → Would; 'tomorrow' → 'the following day' (time expression shift).",
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly demonstrates the FIRST conditional?',
        o: [
          'If it rains, I would stay home.',
          'If it rains, I will stay home.',
          'If it rained, I will stay home.',
          'If it had rained, I would have stayed home.',
        ],
        a: 1,
        e: "'If it rains, I will stay home' is a FIRST conditional — expresses a real, likely future condition. Structure: If + Present Simple, + will + base verb. It refers to realistic future possibilities.",
        h: "Present Simple + 'will' + base verb = First conditional (real future possibility).",
        yr: 'SS2',
      },
      {
        q: 'Which of the following sentences does NOT require backshift, even though the reporting verb is in the past tense?',
        o: [
          'He said he was happy.',
          'He said he had finished the work.',
          'The scientist explained that the earth rotates on its axis.',
          'She said she would call later.',
        ],
        a: 2,
        e: "'The scientist explained that the earth rotates on its axis' correctly keeps the PRESENT tense ('rotates') because it states a universal/scientific truth — this is the exception to the backshift rule, regardless of when it was reported.",
        h: "Universal/scientific TRUTHS don't backshift, even with a past reporting verb.",
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Phrasal Verbs',
    topicCode: 'SS2-ENG-14',
    module: 'Lexis and Structure',
    term: 'Second Term',
    contentHTML:
      "\n<div class=\"learn-intro\">\n  A <span class=\"learn-keyword\">phrasal verb</span> is a verb combined with one or more particles (prepositions and/or adverbs) to create a meaning that is often different from the individual words. Phrasal verbs are extremely common in everyday English and frequently tested in WAEC.\n</div>\n\n<h3 class=\"learn-subheading\">1. Common Phrasal Verbs and Their Meanings</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Phrasal Verb</th><th>Meaning</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>look up to</td><td>admire/respect</td><td>'She looks up to her mother.'</td></tr>\n      <tr><td>put up with</td><td>tolerate</td><td>'I can't put up with his rudeness.'</td></tr>\n      <tr><td>look forward to</td><td>anticipate eagerly</td><td>'We look forward to seeing you.'</td></tr>\n      <tr><td>give up</td><td>stop trying / surrender</td><td>'Don't give up on your dreams.'</td></tr>\n      <tr><td>come across</td><td>find by chance</td><td>'I came across an old photo.'</td></tr>\n      <tr><td>break down</td><td>stop functioning / become emotional</td><td>'The car broke down on the highway.'</td></tr>\n      <tr><td>carry out</td><td>perform/execute (a task)</td><td>'They carried out the experiment.'</td></tr>\n      <tr><td>turn down</td><td>reject / lower volume</td><td>'She turned down the job offer.'</td></tr>\n      <tr><td>run out of</td><td>have no more of something</td><td>'We ran out of fuel.'</td></tr>\n      <tr><td>look down on</td><td>regard as inferior</td><td>'He looks down on people less educated than him.'</td></tr>\n      <tr><td>get away with</td><td>escape punishment for wrongdoing</td><td>'He won't get away with this.'</td></tr>\n      <tr><td>put off</td><td>postpone</td><td>'They put off the meeting until Monday.'</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Types of Phrasal Verbs</h3>\n<ul class=\"learn-list\">\n  <li><strong>Two-word (single particle):</strong> give up, break down, carry out</li>\n  <li><strong>Three-word (two particles):</strong> look forward to, put up with, look down on, get away with</li>\n  <li><strong>Transitive:</strong> takes a direct object — 'turn down the offer'</li>\n  <li><strong>Intransitive:</strong> no direct object — 'the car broke down'</li>\n  <li><strong>Separable:</strong> object can go between verb and particle — 'turn the offer down' / 'turn down the offer'</li>\n  <li><strong>Inseparable:</strong> object must come AFTER the full phrasal verb — 'look after the children' (NOT 'look the children after')</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Vocabulary Development: Words Associated with Building and Construction</h3>\n<p class=\"learn-p\"><strong>culvert</strong> (a drain/channel allowing water to pass under a road), <strong>foundation</strong> (the base structure of a building), <strong>scaffold</strong> (temporary structure for workers during construction), <strong>masonry</strong> (stonework/brickwork), <strong>girder</strong> (a large beam supporting a structure), <strong>cantilever</strong> (a beam fixed at one end, projecting), <strong>parapet</strong> (a low protective wall at the edge of a roof/balcony), <strong>facade</strong> (the front face of a building).</p>\n\n<h3 class=\"learn-subheading\">4. Words of the Week (Selection)</h3>\n<p class=\"learn-p\"><strong>asylum</strong> (protection given to refugees; also a mental institution), <strong>consensus</strong> (general agreement among a group), <strong>hospice</strong> (a care facility for the terminally ill), <strong>wishy-washy</strong> (lacking conviction/strength of character), <strong>unsightly</strong> (unpleasant to look at).</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Phrasal verbs CANNOT usually be guessed from their individual words — they must be MEMORISED as units. 'Put up with' = tolerate (NOT about placing something up). Separable phrasal verbs allow the object BETWEEN verb and particle when the object is a PRONOUN: 'turn it down' (NOT 'turn down it'). Inseparable ones never split: 'look after them' (NOT 'look them after').\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Phrasal verbs = verb + particle(s) with idiomatic meaning. Common ones: look up to (admire), put up with (tolerate), give up (stop), come across (find by chance), break down (stop functioning), carry out (perform), turn down (reject), run out of (deplete), get away with (escape punishment), put off (postpone). Types: two-word/three-word, transitive/intransitive, separable/inseparable.\n</div>\n  ",
    questions: [
      {
        q: "What does the phrasal verb 'look up to' mean?",
        o: [
          'To physically look in an upward direction',
          'To admire or respect someone',
          'To search for information',
          'To visit someone briefly',
        ],
        a: 1,
        e: "'Look up to' means to admire or respect someone, often as a role model. Example: 'Young students look up to their teachers.' This is an idiomatic meaning that cannot be deduced from the individual words.",
        h: 'This phrasal verb relates to ADMIRATION/respect, not physical direction.',
        yr: 'SS2',
      },
      {
        q: "Which sentence correctly uses 'put up with' to mean 'tolerate'?",
        o: [
          'She put up with her bag on the table.',
          'I cannot put up with his constant complaining.',
          'He put the shelf up with nails.',
          'They put up with the decorations for the party.',
        ],
        a: 1,
        e: "'Put up with' means to tolerate or endure something unpleasant — 'I cannot put up with his constant complaining' correctly uses this idiomatic meaning. The other options misuse 'put up' in its literal sense (placing/installing something).",
        h: "'Put up with' = TOLERATE/endure something unpleasant.",
        yr: 'SS2',
      },
      {
        q: "What does 'come across' mean in the sentence 'I came across an old photograph in the drawer'?",
        o: [
          'To cross a physical road or river',
          'To find something by chance, without searching for it deliberately',
          'To argue with someone',
          'To deliberately search for and locate something',
        ],
        a: 1,
        e: "'Come across' means to find or encounter something unexpectedly, by chance — not through deliberate searching. 'I came across an old photograph' means I found it accidentally while doing something else.",
        h: "'Come across' = find something BY CHANCE, unexpectedly.",
        yr: 'SS2',
      },
      {
        q: "Which phrasal verb correctly completes: 'He decided to ___ his job offer because the salary was too low.'",
        o: ['turn down', 'turn up', 'turn on', 'turn over'],
        a: 0,
        e: "'Turn down' means to reject or refuse something, such as a job offer. 'Turn up' means to arrive or increase volume, 'turn on' means to activate, and 'turn over' means to flip or hand something to someone.",
        h: 'Which phrasal verb means to REJECT an offer?',
        yr: 'SS2',
      },
      {
        q: "What does 'get away with' mean in: 'He thinks he can get away with cheating on the exam'?",
        o: [
          'To physically run away from a location',
          'To escape punishment or consequences for wrongdoing',
          'To win a prize or award',
          'To travel somewhere quickly',
        ],
        a: 1,
        e: "'Get away with' means to escape blame, punishment, or negative consequences for doing something wrong. 'He thinks he can get away with cheating' means he believes he won't be caught or punished.",
        h: "'Get away with' = avoid PUNISHMENT for wrongdoing.",
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly demonstrates a SEPARABLE phrasal verb with a pronoun object?',
        o: [
          'She turned down it.',
          'She turned it down.',
          'She turned down the it.',
          'Both A and B are correct.',
        ],
        a: 1,
        e: "'She turned it down' is correct — with separable phrasal verbs, when the object is a PRONOUN (it, him, her, them), the pronoun MUST go between the verb and particle. 'Turned down it' is grammatically incorrect.",
        h: 'Separable phrasal verbs: PRONOUN objects MUST go between verb and particle.',
        yr: 'SS2',
      },
      {
        q: "Identify the INSEPARABLE phrasal verb in the following: 'look after', 'turn down', 'pick up', 'put off'.",
        o: ['Turn down', 'Pick up', 'Look after', 'Put off'],
        a: 2,
        e: "'Look after' (meaning to take care of) is inseparable — the object must always follow the complete phrasal verb: 'look after the children' (correct), NOT 'look the children after' (incorrect). The other three listed are separable phrasal verbs.",
        h: 'Inseparable phrasal verbs NEVER allow the object to split the verb and particle.',
        yr: 'SS2',
      },
      {
        q: "What does 'run out of' mean in: 'We ran out of fuel on the highway'?",
        o: [
          'To run quickly away from fuel',
          'To have no more of something; to deplete a supply completely',
          'To purchase additional fuel',
          'To run alongside a fuel truck',
        ],
        a: 1,
        e: "'Run out of' means to use up or exhaust a supply of something completely — 'We ran out of fuel' means we had no fuel left. This three-word phrasal verb is commonly used for depleted resources.",
        h: "'Run out of' = have NO MORE of something (a supply is exhausted).",
        yr: 'SS2',
      },
      {
        q: "Which phrasal verb means 'to postpone or delay something'?",
        o: ['Carry out', 'Put off', 'Look up to', 'Break down'],
        a: 1,
        e: "'Put off' means to postpone or delay an event/action to a later time. Example: 'They put off the wedding until next year.' This should not be confused with 'put up with' (tolerate) or 'put on' (wear/perform).",
        h: "'Put off' = POSTPONE/delay something to a later time.",
        yr: 'SS2',
      },
      {
        q: "What does 'look down on' mean in: 'He looks down on people who didn't attend university'?",
        o: [
          'To physically gaze downward at someone',
          'To regard someone as inferior; to view with contempt or disdain',
          'To admire and respect someone greatly',
          "To carefully examine someone's appearance",
        ],
        a: 1,
        e: "'Look down on' means to regard someone or something as inferior, often with contempt or condescension — the opposite of 'look up to' (admire). 'He looks down on people who didn't attend university' shows disdain/contempt.",
        h: "'Look down on' = regard as INFERIOR (opposite of 'look up to').",
        yr: 'SS2',
      },
      {
        q: "Which sentence correctly uses 'carry out' to mean 'perform/execute'?",
        o: [
          'She carried out the heavy box from the room.',
          'The scientists carried out the experiment successfully.',
          'He carried out his friend on his shoulders.',
          'They carried the chairs out of the hall.',
        ],
        a: 1,
        e: "'Carry out' (meaning to perform or execute a task, plan, or instruction) is correctly used in 'The scientists carried out the experiment successfully.' The other options use 'carry' in its literal sense (physically transporting something).",
        h: "'Carry out' (idiomatic) = PERFORM/execute a task — different from literally carrying objects.",
        yr: 'SS2',
      },
      {
        q: "A 'culvert' in construction refers to:",
        o: [
          'The front face of a building',
          'A drain or channel that allows water to pass under a road or railway',
          'A type of decorative wall',
          "A worker's safety helmet",
        ],
        a: 1,
        e: "'Culvert' refers to a structure (usually a pipe or tunnel) that allows water (such as a stream) to flow under a road, railway, or embankment. Example: 'Heavy rains overwhelmed the culvert, causing flooding on the road.'",
        h: 'This construction term relates to a structure for WATER to pass UNDER a road.',
        yr: 'SS2',
      },
      {
        q: "What does 'foundation' mean in construction?",
        o: [
          'The decorative top of a building',
          'The base structure on which a building rests, providing stability and support',
          'A type of window',
          'The roof covering',
        ],
        a: 1,
        e: "'Foundation' refers to the lowest load-bearing part of a building, typically below ground level, that supports the entire structure. A strong foundation is essential for a building's stability.",
        h: 'This term relates to the BASE/bottom structural support of a building.',
        yr: 'SS2',
      },
      {
        q: "A 'scaffold' in construction is:",
        o: [
          'A permanent decorative feature',
          'A temporary structure used to support workers and materials during construction or repair of a building',
          'The main entrance of a building',
          'A type of paint used on walls',
        ],
        a: 1,
        e: "'Scaffold' (or scaffolding) refers to a temporary framework, typically made of metal poles and planks, erected to support workers and materials at height during building construction, maintenance, or repair.",
        h: 'This term relates to a TEMPORARY structure for workers during construction.',
        yr: 'SS2',
      },
      {
        q: "Which word describes 'stonework or brickwork' in building construction?",
        o: ['Scaffold', 'Masonry', 'Facade', 'Parapet'],
        a: 1,
        e: "'Masonry' refers to construction work made of individual units (such as bricks, stones, or concrete blocks) typically bonded together with mortar. Example: 'The skilled masonry of the ancient walls has survived centuries.'",
        h: 'This term relates to STONE or BRICK construction work.',
        yr: 'SS2',
      },
      {
        q: "What is a 'facade' in architecture?",
        o: [
          'The internal structure supporting a roof',
          'The front face or exterior of a building, especially its main, most visible side',
          'A temporary structure for construction workers',
          'An underground water channel',
        ],
        a: 1,
        e: "'Facade' refers to the exterior face of a building, especially the main front that faces a street or public area — often the most architecturally significant or decorated side. Example: 'The cathedral's facade is renowned for its intricate carvings.'",
        h: 'This term relates to the FRONT FACE/exterior of a building.',
        yr: 'SS2',
      },
      {
        q: "What does 'asylum' mean in the context of refugees and international law?",
        o: [
          'A type of building material',
          'Protection and refuge granted by a country to a person fleeing persecution in their home country',
          'A formal agreement between two nations',
          'A type of legal document for property ownership',
        ],
        a: 1,
        e: "In this context, 'asylum' refers to protection granted by a state to someone who has fled their home country to escape persecution, war, or danger. (The word can also mean a historical institution for mentally ill patients, but in international relations contexts, it refers to refugee protection.)",
        h: 'In international relations, this word relates to PROTECTION given to refugees.',
        yr: 'SS2',
      },
      {
        q: "What is a 'consensus'?",
        o: [
          'A strong disagreement between parties',
          'A general agreement reached by a group, especially after discussion',
          'A formal legal document',
          'A type of architectural structure',
        ],
        a: 1,
        e: "'Consensus' refers to general agreement among a group of people, typically reached through discussion and compromise rather than a vote. Example: 'After hours of debate, the committee reached a consensus on the new policy.'",
        h: 'This word means GENERAL AGREEMENT among a group.',
        yr: 'SS2',
      },
      {
        q: "Which word describes a person or attitude that is 'lacking conviction or strength of character; weak and indecisive'?",
        o: ['Unctuous', 'Wishy-washy', 'Exigent', 'Tranquil'],
        a: 1,
        e: "'Wishy-washy' (informal) describes something or someone lacking firmness, decisiveness, or strong conviction — weak and vague. Example: 'His wishy-washy response failed to address the real issue.'",
        h: 'This informal word means WEAK and INDECISIVE — lacking firm conviction.',
        yr: 'SS2',
      },
      {
        q: "Which sentence correctly uses 'break down' in its IDIOMATIC sense (not just mechanical failure)?",
        o: [
          'The old car broke down on the way to school.',
          'After hearing the sad news, she broke down in tears.',
          'The machine broke down after years of heavy use.',
          "All three sentences correctly use 'break down' in different senses.",
        ],
        a: 3,
        e: "'Break down' has multiple related meanings: mechanical failure ('the car broke down'), emotional collapse ('she broke down in tears'), and general malfunction ('the machine broke down'). All three sentences correctly demonstrate valid uses of this versatile phrasal verb.",
        h: "'Break down' can mean MECHANICAL failure OR EMOTIONAL collapse — both are valid uses.",
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Active and Passive Voice',
    topicCode: 'SS2-ENG-15',
    module: 'Lexis and Structure',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Voice</span> in grammar refers to whether the subject of a sentence PERFORMS the action (active voice) or RECEIVES the action (passive voice). Understanding how to transform sentences between active and passive voice is a core WAEC structure skill.\n</div>\n\n<h3 class="learn-subheading">1. Active Voice</h3>\n<p class="learn-p">In active voice, the SUBJECT performs the action expressed by the verb.</p>\n<p class="learn-p"><strong>Structure:</strong> Subject + Verb + Object<br>Example: \'The chef <strong>cooked</strong> the meal.\' (Chef = doer of the action)</p>\n\n<h3 class="learn-subheading">2. Passive Voice</h3>\n<p class="learn-p">In passive voice, the SUBJECT receives the action — the original object becomes the new subject.</p>\n<p class="learn-p"><strong>Structure:</strong> Object (now subject) + BE + Past Participle + (by + original subject)<br>Example: \'The meal <strong>was cooked</strong> by the chef.\' (Meal = receiver of the action)</p>\n\n<h3 class="learn-subheading">3. Forming the Passive Voice by Tense</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Tense</th><th>Active</th><th>Passive</th></tr></thead>\n    <tbody>\n      <tr><td>Present Simple</td><td>writes</td><td>is/are written</td></tr>\n      <tr><td>Present Continuous</td><td>is writing</td><td>is being written</td></tr>\n      <tr><td>Present Perfect</td><td>has written</td><td>has been written</td></tr>\n      <tr><td>Past Simple</td><td>wrote</td><td>was/were written</td></tr>\n      <tr><td>Past Continuous</td><td>was writing</td><td>was being written</td></tr>\n      <tr><td>Past Perfect</td><td>had written</td><td>had been written</td></tr>\n      <tr><td>Future Simple</td><td>will write</td><td>will be written</td></tr>\n      <tr><td>Modal (can/must/should)</td><td>can write</td><td>can be written</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">4. When to Use Passive Voice</h3>\n<ul class="learn-list">\n  <li>When the DOER of the action is unknown, unimportant, or obvious: \'The window was broken.\' (we don\'t know/care who)</li>\n  <li>When the ACTION or RESULT is more important than who did it (common in scientific/formal writing): \'The experiment was conducted carefully.\'</li>\n  <li>To avoid blame or be diplomatic: \'Mistakes were made.\' (avoids naming who made them)</li>\n  <li>When the OBJECT/RECEIVER is the focus of the sentence/discussion</li>\n</ul>\n\n<h3 class="learn-subheading">5. Sentences That Cannot Be Made Passive</h3>\n<ul class="learn-list">\n  <li>INTRANSITIVE verbs (no direct object) cannot be passivized: \'He arrived late\' → CANNOT become passive (no object to become the new subject)</li>\n  <li>Linking verbs (be, seem, become) generally cannot be passivized: \'She is a doctor\' → no passive form</li>\n</ul>\n\n<h3 class="learn-subheading">6. Vocabulary Development: Words Associated with Technology</h3>\n<p class="learn-p"><strong>email</strong>, <strong>satellite</strong>, <strong>virus</strong> (malicious software), <strong>password</strong>, <strong>bandwidth</strong>, <strong>firewall</strong>, <strong>download/upload</strong>, <strong>server</strong>, <strong>encryption</strong>, <strong>algorithm</strong>.</p>\n\n<h3 class="learn-subheading">7. Words of the Week (Selection)</h3>\n<p class="learn-p"><strong>platitude</strong> (an unoriginal, overused remark), <strong>feisty</strong> (spirited, full of energy/determination), <strong>oust</strong> (to remove forcibly from a position), <strong>disarmament</strong> (the reduction/withdrawal of military weapons), <strong>vestige</strong> (a remaining trace of something that once existed), <strong>exude</strong> (to display a quality strongly/visibly), <strong>tantalizing</strong> (causing desire by being just out of reach), <strong>unbridled</strong> (uncontrolled, unrestrained).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Formula: Passive = BE (correct tense/form) + PAST PARTICIPLE + (by + agent, optional). The ORIGINAL OBJECT becomes the NEW SUBJECT. Intransitive verbs (arrive, happen, sleep) CANNOT be made passive — they have no object. The \'by + agent\' phrase is often OMITTED when the doer is unknown/unimportant/obvious.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Active voice: subject performs action (S+V+O). Passive voice: subject receives action (O→new S + BE + past participle + by + original S). Passive used when doer is unknown/unimportant, action matters more than doer, or for diplomacy. Formula varies by tense (is written, was written, has been written, will be written). Intransitive verbs and linking verbs generally cannot be passivized.\n</div>\n  ',
    questions: [
      {
        q: "Convert to PASSIVE voice: 'The chef cooked the meal.'",
        o: [
          'The meal cooks the chef.',
          'The meal was cooked by the chef.',
          'The meal is cooking by the chef.',
          'The chef was cooked by the meal.',
        ],
        a: 1,
        e: "'The meal was cooked by the chef' correctly converts to passive — the original object 'meal' becomes the new subject, 'was cooked' (BE + past participle) replaces the active verb, and 'by the chef' shows the original doer.",
        h: "Original OBJECT becomes new SUBJECT; use BE + past participle + 'by' + original subject.",
        yr: 'SS2',
      },
      {
        q: "What is the passive form of 'The students are writing the essay' (Present Continuous)?",
        o: [
          'The essay is written by the students.',
          'The essay is being written by the students.',
          'The essay was written by the students.',
          'The essay has been written by the students.',
        ],
        a: 1,
        e: "Present Continuous active ('are writing') becomes 'is being written' in passive — following the formula BE + BEING + past participle for continuous tenses. This matches the ongoing nature of the action.",
        h: 'Present Continuous passive = is/am/are + BEING + past participle.',
        yr: 'SS2',
      },
      {
        q: "Convert to passive: 'They have completed the project.'",
        o: [
          'The project is completed by them.',
          'The project was completed by them.',
          'The project has been completed by them.',
          'The project will be completed by them.',
        ],
        a: 2,
        e: "Present Perfect active ('have completed') becomes 'has been completed' in passive — following the formula HAS/HAVE + BEEN + past participle for perfect tenses.",
        h: 'Present Perfect passive = has/have + BEEN + past participle.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence CANNOT be converted to passive voice?',
        o: [
          'She wrote a letter.',
          'He arrived late at the meeting.',
          'The teacher explained the lesson.',
          'They built the house.',
        ],
        a: 1,
        e: "'He arrived late at the meeting' uses an INTRANSITIVE verb ('arrived') with no direct object — there is nothing to become the new subject in a passive construction. The other sentences all have direct objects (letter, lesson, house) that can become passive subjects.",
        h: 'Look for the sentence with NO DIRECT OBJECT (intransitive verb).',
        yr: 'SS2',
      },
      {
        q: "Convert to passive voice: 'The government will build a new hospital.'",
        o: [
          'A new hospital is built by the government.',
          'A new hospital will be built by the government.',
          'A new hospital was built by the government.',
          'A new hospital has been built by the government.',
        ],
        a: 1,
        e: "Future Simple active ('will build') becomes 'will be built' in passive — following the formula WILL + BE + past participle for future tense constructions.",
        h: 'Future Simple passive = WILL + BE + past participle.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence demonstrates an appropriate USE of passive voice when the DOER is unknown?',
        o: [
          'John broke the window yesterday.',
          'The window was broken (by an unknown person).',
          'The window broke itself.',
          "Someone definitely broke the window, but we don't know who.",
        ],
        a: 1,
        e: "'The window was broken' is the natural passive construction used when the agent/doer is unknown or unimportant — the 'by' phrase is simply omitted, which is standard and grammatically complete in passive voice.",
        h: "When the doer is unknown, the passive often OMITS the 'by' phrase entirely.",
        yr: 'SS2',
      },
      {
        q: "Convert to passive: 'Someone must complete this task by tomorrow.'",
        o: [
          'This task must be completed by tomorrow.',
          'This task must complete by tomorrow.',
          'This task is completed by tomorrow.',
          'This task must completing by tomorrow.',
        ],
        a: 0,
        e: "With modal verbs (must, can, should), the passive formula is MODAL + BE + past participle: 'must be completed'. The 'someone' (unknown/unimportant agent) is appropriately dropped.",
        h: 'Modal verb passive = MODAL + BE + past participle.',
        yr: 'SS2',
      },
      {
        q: 'Why might a scientific report use passive voice rather than active voice?',
        o: [
          'Passive voice is always grammatically incorrect in formal writing',
          'Passive voice shifts focus to the PROCESS/RESULT rather than who performed the action, which is conventional in scientific/formal writing',
          'Passive voice is easier to understand for all readers',
          'Scientific reports never use passive voice',
        ],
        a: 1,
        e: "Scientific and formal writing often uses passive voice to emphasise the PROCESS, METHOD, or RESULT rather than the specific researcher who performed the action: 'The samples were analysed' (focus on the analysis) rather than 'I analysed the samples' (focus on the person).",
        h: 'Passive voice shifts focus AWAY from the doer and TOWARD the process/result.',
        yr: 'SS2',
      },
      {
        q: "What does 'oust' mean?",
        o: [
          'To welcome someone warmly into a position',
          'To remove someone forcibly from a position of power or authority',
          'To promote someone to a higher rank',
          "To celebrate someone's achievement",
        ],
        a: 1,
        e: "'Oust' means to force someone out of a position, especially one of power or authority. Example: 'The military ousted the president in a coup.'",
        h: 'This word means to FORCIBLY REMOVE someone from power/position.',
        yr: 'SS2',
      },
      {
        q: "Which word describes a person who is 'spirited, energetic, and not afraid to fight for what they believe'?",
        o: ['Tranquil', 'Feisty', 'Unctuous', 'Wishy-washy'],
        a: 1,
        e: "'Feisty' describes someone full of spirit, energy, and determination, especially when standing up for themselves despite being smaller or less powerful. Example: 'The feisty young lawyer challenged the powerful corporation in court.'",
        h: 'This word means SPIRITED and ENERGETIC, often standing up against odds.',
        yr: 'SS2',
      },
      {
        q: "What does 'disarmament' mean?",
        o: [
          'The acquisition of more weapons',
          'The reduction or withdrawal of military weapons and forces, often as part of peace agreements',
          'A type of military training',
          'The construction of new military bases',
        ],
        a: 1,
        e: "'Disarmament' refers to the act of reducing or eliminating weapons, especially as part of international agreements aimed at reducing conflict. Example: 'The peace treaty included provisions for nuclear disarmament.'",
        h: 'This word relates to REDUCING/eliminating weapons, often for peace.',
        yr: 'SS2',
      },
      {
        q: "A 'vestige' refers to:",
        o: [
          'A completely new development with no historical connection',
          'A small remaining trace, mark, or sign of something that once existed but is now gone or diminished',
          'A formal celebration ceremony',
          'A type of legal document',
        ],
        a: 1,
        e: "'Vestige' means a small remaining part or trace of something that used to exist in a larger or more complete form. Example: 'The ruins are a vestige of the once-great empire.'",
        h: 'This word means a small REMAINING TRACE of something that once existed.',
        yr: 'SS2',
      },
      {
        q: "To 'exude' a quality means to:",
        o: [
          'Hide or conceal it completely',
          'Display or radiate it strongly and noticeably',
          'Gradually lose it over time',
          'Question its existence',
        ],
        a: 1,
        e: "'Exude' means to display a feeling, quality, or substance strongly and noticeably — to radiate or emit clearly. Example: 'She exudes confidence in every interview.'",
        h: 'This word means to STRONGLY DISPLAY/radiate a quality.',
        yr: 'SS2',
      },
      {
        q: "Which word describes something 'causing desire or interest by appearing to be within reach, but remaining elusive'?",
        o: ['Unbridled', 'Tantalizing', 'Vestige', 'Platitude'],
        a: 1,
        e: "'Tantalizing' describes something that excites desire or interest while remaining frustratingly out of reach or unattainable. Example: 'The tantalizing smell of food drifted from the kitchen.'",
        h: 'This word relates to teasing DESIRE while remaining just out of reach.',
        yr: 'SS2',
      },
      {
        q: "What does 'unbridled' mean?",
        o: [
          'Carefully controlled and restrained',
          'Uncontrolled, unrestrained, given free expression without limits',
          'Extremely calm and quiet',
          'Formally documented',
        ],
        a: 1,
        e: "'Unbridled' describes something completely uncontrolled or unrestrained — given free, unchecked expression. Example: 'Her unbridled enthusiasm for the project was contagious.'",
        h: 'This word means UNCONTROLLED/unrestrained — without limits.',
        yr: 'SS2',
      },
      {
        q: "A 'platitude' is:",
        o: [
          'A profound, original insight',
          'A remark or statement that is overused, unoriginal, and lacks real meaning or depth',
          'A type of formal legal agreement',
          'A scientific measurement unit',
        ],
        a: 1,
        e: "'Platitude' refers to a trite, overused remark that is typically intended to be wise or comforting but has lost its impact through overuse. Example: 'His speech was full of platitudes like \"every cloud has a silver lining\".'",
        h: 'This word relates to an OVERUSED, unoriginal saying/remark.',
        yr: 'SS2',
      },
      {
        q: "Which sentence correctly converts 'The committee is reviewing the proposal' to passive voice?",
        o: [
          'The proposal reviews the committee.',
          'The proposal is being reviewed by the committee.',
          'The proposal was reviewed by the committee.',
          'The proposal has been reviewed by the committee.',
        ],
        a: 1,
        e: "Present Continuous active ('is reviewing') becomes 'is being reviewed' in passive — maintaining the present continuous/ongoing aspect of the action while shifting the focus to the proposal (the receiver).",
        h: 'Present Continuous passive = is/am/are + BEING + past participle.',
        yr: 'SS2',
      },
      {
        q: 'In sentences with linking verbs (be, seem, become), passive voice is generally:',
        o: [
          'Always required',
          'Not applicable — linking verbs do not take objects and therefore cannot be converted to passive',
          'Used only in formal writing',
          'The only correct voice to use',
        ],
        a: 1,
        e: "Linking verbs (be, seem, appear, become) connect the subject to a complement, not to an object — since there is no object to become a new subject, sentences with linking verbs ('She is a doctor', 'He seems tired') cannot be converted to passive voice.",
        h: 'Linking verbs have COMPLEMENTS, not OBJECTS — no passive form is possible.',
        yr: 'SS2',
      },
      {
        q: "Convert to passive voice: 'The police had arrested the suspect before midnight.'",
        o: [
          'The suspect was arrested by the police before midnight.',
          'The suspect had been arrested by the police before midnight.',
          'The suspect has been arrested by the police before midnight.',
          'The suspect is arrested by the police before midnight.',
        ],
        a: 1,
        e: "Past Perfect active ('had arrested') becomes 'had been arrested' in passive — following the formula HAD + BEEN + past participle for past perfect tense constructions.",
        h: 'Past Perfect passive = HAD + BEEN + past participle.',
        yr: 'SS2',
      },
      {
        q: 'Which of these is an example of using passive voice DIPLOMATICALLY to avoid assigning blame?',
        o: [
          'John made a serious mistake in the report.',
          'Mistakes were made in the report.',
          "I think someone made a mistake, but I'm not sure who.",
          "The report contains John's mistake.",
        ],
        a: 1,
        e: "'Mistakes were made in the report' is a classic diplomatic use of passive voice — it acknowledges the error occurred without explicitly naming who is responsible, which can be useful (though sometimes criticised) in formal or political contexts.",
        h: 'Passive voice can AVOID naming the person responsible for an error — a diplomatic technique.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Creative Writing and Article Writing',
    topicCode: 'SS2-ENG-16',
    module: 'Composition',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Creative writing</span> (short stories, plays, poems) allows imaginative expression following genre conventions. <span class="learn-keyword">Article writing</span> is a distinct journalistic form intended for publication, with its own structural and stylistic features that differ from letters and essays.\n</div>\n\n<h3 class="learn-subheading">1. Elements of a Short Story</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Element</th><th>Description</th></tr></thead>\n    <tbody>\n      <tr><td>Plot</td><td>The sequence of events: exposition, rising action, climax, falling action, resolution</td></tr>\n      <tr><td>Characters</td><td>The people/beings in the story (protagonist, antagonist, supporting characters)</td></tr>\n      <tr><td>Setting</td><td>Time and place where the story occurs</td></tr>\n      <tr><td>Point of view</td><td>First person (I/we), third person limited, or third person omniscient</td></tr>\n      <tr><td>Theme</td><td>The central message/idea explored in the story</td></tr>\n      <tr><td>Conflict</td><td>The central problem/tension driving the plot (person vs person, vs self, vs society, vs nature)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">2. Features of an Article</h3>\n<ul class="learn-list">\n  <li><strong>Title/Headline:</strong> Catchy, attention-grabbing, summarising the topic</li>\n  <li><strong>By-line:</strong> Author\'s name (optional in exam context)</li>\n  <li><strong>Introduction:</strong> Hooks the reader; introduces the topic/issue</li>\n  <li><strong>Body:</strong> Develops the topic with facts, opinions, examples (organised in paragraphs)</li>\n  <li><strong>Conclusion:</strong> Summarises or gives a final thought/recommendation</li>\n  <li><strong>Intended for PUBLICATION</strong> — written for a general/specific readership, not a single recipient</li>\n</ul>\n\n<h3 class="learn-subheading">3. Articles vs Letters: Key Differences</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Feature</th><th>Article</th><th>Letter</th></tr></thead>\n    <tbody>\n      <tr><td>Audience</td><td>General public/readership</td><td>A specific individual/recipient</td></tr>\n      <tr><td>Layout</td><td>Title, no address/salutation needed</td><td>Includes addresses, salutation, complimentary close</td></tr>\n      <tr><td>Tone</td><td>Can be informative, persuasive, or analytical</td><td>Personal (informal) or formal-official (formal)</td></tr>\n      <tr><td>Purpose</td><td>To inform, persuade, or entertain a wide readership</td><td>To communicate with one specific person/organisation</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">4. Poetic Devices for Creative Writing</h3>\n<ul class="learn-list">\n  <li><strong>Simile:</strong> Comparison using \'like\' or \'as\' — \'Brave as a lion\'</li>\n  <li><strong>Metaphor:</strong> Direct comparison without \'like/as\' — \'Life is a journey\'</li>\n  <li><strong>Personification:</strong> Giving human qualities to non-human things — \'The wind whispered\'</li>\n  <li><strong>Alliteration:</strong> Repetition of initial consonant sounds — \'Sally sells seashells\'</li>\n  <li><strong>Rhyme scheme:</strong> Pattern of rhyming line endings — labelled ABAB, AABB, etc.</li>\n</ul>\n\n<h3 class="learn-subheading">5. Vocabulary Development: Words Associated with Environment</h3>\n<p class="learn-p"><strong>deforestation</strong>, <strong>biodegradable</strong>, <strong>ecosystem</strong>, <strong>conservation</strong>, <strong>pollution</strong>, <strong>sustainability</strong>, <strong>greenhouse effect</strong>, <strong>renewable energy</strong>, <strong>erosion</strong>, <strong>desertification</strong>.</p>\n\n<h3 class="learn-subheading">6. Words of the Week (Selection)</h3>\n<p class="learn-p"><strong>paucity</strong> (a scarcity/insufficient amount), <strong>ostracism</strong> (exclusion from a group/society), <strong>acclaim</strong> (praise/enthusiastic approval), <strong>acerbic</strong> (sharp/bitter in tone), <strong>bereft</strong> (deprived of/lacking something, especially through loss), <strong>nomadic</strong> (moving from place to place, not settled), <strong>beguile</strong> (to charm/deceive), <strong>munificence</strong> (great generosity), <strong>phlegmatic</strong> (calm, unemotional temperament).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Articles need a CATCHY TITLE but NO address/salutation (unlike letters). Short stories need: plot (exposition→climax→resolution), characters, setting, theme, conflict, point of view. Key article-letter distinction: articles target a GENERAL readership; letters target ONE specific recipient. Use poetic devices (simile, metaphor, personification, alliteration) to enrich creative writing.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Short story elements: plot, characters, setting, point of view, theme, conflict. Article features: title, no salutation, intro-body-conclusion for general readership (unlike letters, addressed to ONE person). Poetic devices: simile (like/as), metaphor (direct comparison), personification (human qualities to non-human), alliteration (repeated initial sounds), rhyme scheme.\n</div>\n  ',
    questions: [
      {
        q: "What is the term for the central problem or tension that drives a story's plot?",
        o: ['Setting', 'Conflict', 'Theme', 'Point of view'],
        a: 1,
        e: "'Conflict' refers to the central struggle or tension in a story — it can be person vs person, person vs self, person vs society, or person vs nature. Conflict drives the plot forward and creates dramatic interest.",
        h: 'This element is the central PROBLEM/TENSION that the story revolves around.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is the KEY structural difference between an ARTICLE and a LETTER?',
        o: [
          'Articles always have a salutation; letters never do',
          "An article is written for a general readership and does NOT include a salutation or recipient's address; a letter is addressed to a specific individual with formal layout elements",
          'Letters are always longer than articles',
          "Articles cannot include the writer's opinion",
        ],
        a: 1,
        e: 'The defining structural difference: an ARTICLE has a catchy title and is written for a wide, general readership (no addresses/salutation needed), while a LETTER includes specific layout elements (addresses, salutation, complimentary close) addressed to ONE recipient.',
        h: 'Articles target a GENERAL audience (title only); letters target ONE person (full address/salutation layout).',
        yr: 'SS2',
      },
      {
        q: "Identify the literary device in: 'The stars danced playfully in the night sky.'",
        o: [
          'Simile',
          'Personification — giving human qualities (dancing, playfulness) to non-human things (stars)',
          'Alliteration',
          'Rhyme',
        ],
        a: 1,
        e: "This sentence uses personification — attributing human qualities ('danced', 'playfully') to non-human objects (the stars). Stars cannot literally dance or feel playful; this device creates vivid imagery by humanising them.",
        h: "Stars cannot literally 'dance' — this is giving HUMAN qualities to a non-human thing.",
        yr: 'SS2',
      },
      {
        q: 'Which of the following is an example of a SIMILE?',
        o: [
          'Life is a journey.',
          'Her smile was sunshine.',
          'He fought like a lion in the battle.',
          'The wind whispered through the trees.',
        ],
        a: 2,
        e: "'He fought like a lion' is a simile — a comparison using the word 'like' (or 'as'). 'Life is a journey' and 'Her smile was sunshine' are metaphors (direct comparisons without 'like/as'), and 'the wind whispered' is personification.",
        h: "Look for the comparison that uses the word 'LIKE' or 'AS'.",
        yr: 'SS2',
      },
      {
        q: "What is 'alliteration'?",
        o: [
          'A pattern of rhyming line endings',
          'The repetition of the same initial consonant sound in a series of words',
          "A comparison using 'like' or 'as'",
          'Giving human qualities to non-human things',
        ],
        a: 1,
        e: "'Alliteration' is the repetition of the same initial consonant sound across multiple nearby words, used for rhythmic or emphatic effect: 'Sally sells seashells by the seashore' or 'Peter Piper picked a peck of pickled peppers.'",
        h: 'This device repeats the SAME SOUND at the start of nearby words.',
        yr: 'SS2',
      },
      {
        q: 'In the typical structure of a plot (exposition, rising action, climax, falling action, resolution), what does the CLIMAX represent?',
        o: [
          'The beginning, where characters and setting are introduced',
          'The point of highest tension or the turning point of the story',
          'The very end, where everything is resolved',
          'Background information given before the story starts',
        ],
        a: 1,
        e: "The 'climax' is the turning point of the story — the moment of greatest tension, conflict, or dramatic intensity, after which the action begins to resolve (falling action) toward the conclusion (resolution).",
        h: 'This is the point of GREATEST TENSION/the turning point in the story.',
        yr: 'SS2',
      },
      {
        q: "What does 'paucity' mean?",
        o: [
          'An abundance or surplus of something',
          'A scarcity; an insufficient or inadequate amount of something',
          'A formal celebration',
          'A type of literary device',
        ],
        a: 1,
        e: "'Paucity' means a smallness or scarcity of number/quantity — an insufficient amount. Example: 'There is a paucity of qualified doctors in rural hospitals.'",
        h: 'This word means SCARCITY/an insufficient amount.',
        yr: 'SS2',
      },
      {
        q: "Which word describes 'exclusion from a group or society, often as a form of punishment'?",
        o: ['Acclaim', 'Ostracism', 'Munificence', 'Beguile'],
        a: 1,
        e: "'Ostracism' refers to the practice of excluding someone from a group or society. Example: 'His controversial views led to his ostracism by former colleagues.'",
        h: 'This word relates to being EXCLUDED/shut out from a group.',
        yr: 'SS2',
      },
      {
        q: "'Acclaim' means:",
        o: [
          'Harsh criticism and rejection',
          'Enthusiastic public praise and approval',
          'A formal complaint',
          'A type of poetic device',
        ],
        a: 1,
        e: "'Acclaim' refers to enthusiastic and public praise or approval, especially for an achievement. Example: 'The novel received critical acclaim from reviewers worldwide.'",
        h: 'This word relates to enthusiastic PRAISE and approval.',
        yr: 'SS2',
      },
      {
        q: "Which word describes a tone or remark that is 'sharp, bitter, and cutting'?",
        o: ['Phlegmatic', 'Acerbic', 'Munificent', 'Bereft'],
        a: 1,
        e: "'Acerbic' describes a tone or remark that is sharply critical, bitter, or sarcastic. Example: 'Her acerbic comments left the audience uncomfortable.'",
        h: 'This word means SHARP and BITTER in tone.',
        yr: 'SS2',
      },
      {
        q: "To be 'bereft' of something means to be:",
        o: [
          'Abundantly supplied with it',
          'Deprived of or lacking it, especially through loss',
          'Curious about it',
          'Confused by it',
        ],
        a: 1,
        e: "'Bereft' describes being deprived of something, especially through loss — left without something valuable. Example: 'She was bereft of hope after the devastating news.'",
        h: 'This word relates to being DEPRIVED of something through loss.',
        yr: 'SS2',
      },
      {
        q: "Which word describes a lifestyle of 'moving from place to place rather than settling permanently'?",
        o: ['Phlegmatic', 'Nomadic', 'Acerbic', 'Bereft'],
        a: 1,
        e: "'Nomadic' describes a way of life characterised by moving from place to place rather than settling in one location permanently. Example: 'The Fulani herders traditionally led a nomadic lifestyle.'",
        h: 'This word relates to a MOVING, non-settled lifestyle.',
        yr: 'SS2',
      },
      {
        q: "To 'beguile' someone means to:",
        o: [
          'Bore them with dull conversation',
          'Charm or attract them, often in a way that may be deceptive',
          'Frighten them severely',
          'Inform them of important facts',
        ],
        a: 1,
        e: "'Beguile' means to charm or captivate someone, sometimes through deception or trickery. Example: 'The con artist beguiled investors with false promises of huge returns.'",
        h: 'This word means to CHARM, sometimes through trickery/deception.',
        yr: 'SS2',
      },
      {
        q: "What does 'munificence' mean?",
        o: [
          'Extreme stinginess',
          'Great generosity in giving',
          'A type of poetic device',
          'A scarcity of resources',
        ],
        a: 1,
        e: "'Munificence' refers to great generosity, especially in giving money or gifts. Example: 'The philanthropist's munificence funded several new schools.'",
        h: 'This word relates to GREAT GENEROSITY.',
        yr: 'SS2',
      },
      {
        q: "A person with a 'phlegmatic' temperament is one who is:",
        o: [
          'Highly emotional and easily excited',
          'Calm, unemotional, and not easily excited or worried',
          'Extremely talkative',
          'Always anxious and nervous',
        ],
        a: 1,
        e: "'Phlegmatic' describes a calm, even-tempered, unemotional disposition — not easily excited, worried, or angered. Example: 'His phlegmatic response to the crisis surprised everyone.'",
        h: 'This word describes a CALM, unemotional temperament.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following would be an appropriate TITLE for a newspaper article about deforestation?',
        o: [
          'Dear Editor, I am writing about deforestation',
          'Vanishing Forests: The Silent Crisis Threatening Our Future',
          'To the Forestry Commission, Re: Deforestation',
          'Yours faithfully, A Concerned Citizen',
        ],
        a: 1,
        e: "'Vanishing Forests: The Silent Crisis Threatening Our Future' is an appropriately catchy, attention-grabbing ARTICLE title. The other options use letter-format conventions (salutations, addresses, complimentary closes), which are inappropriate for an article.",
        h: 'Article titles should be CATCHY and engaging, NOT formatted like letter salutations.',
        yr: 'SS2',
      },
      {
        q: "Which environmental term describes 'materials that can be broken down naturally by living organisms'?",
        o: ['Deforestation', 'Biodegradable', 'Pollution', 'Desertification'],
        a: 1,
        e: "'Biodegradable' describes substances that can be decomposed naturally by bacteria or other living organisms, breaking down into harmless products over time — important for reducing environmental waste.",
        h: 'This term relates to materials that can be naturally BROKEN DOWN by organisms.',
        yr: 'SS2',
      },
      {
        q: "What does 'desertification' refer to?",
        o: [
          'The process of planting more trees in a region',
          'The process by which fertile land becomes increasingly arid and desert-like, often due to climate change or poor land management',
          'The protection of existing forests',
          'A type of renewable energy source',
        ],
        a: 1,
        e: "'Desertification' describes the process by which previously fertile or productive land gradually turns into desert, often as a result of climate change, deforestation, or unsustainable agricultural practices.",
        h: 'This term describes land BECOMING DESERT-LIKE — losing fertility.',
        yr: 'SS2',
      },
      {
        q: "In creative writing, what is 'point of view' (POV)?",
        o: [
          'The location where the story takes place',
          'The perspective from which a story is told — first person, third person limited, or third person omniscient',
          'The central message of the story',
          'The list of characters in the story',
        ],
        a: 1,
        e: "'Point of view' refers to the narrative perspective from which the story is told: FIRST PERSON ('I went...'), THIRD PERSON LIMITED (focuses on one character's perspective using 'he/she'), or THIRD PERSON OMNISCIENT (narrator knows all characters' thoughts).",
        h: "POV is the PERSPECTIVE/narrator's position from which the story is told.",
        yr: 'SS2',
      },
      {
        q: 'Why is it important for an article to have a strong, engaging INTRODUCTION?',
        o: [
          'Because articles do not need conclusions',
          "Because it must immediately capture the general readership's attention and interest, encouraging them to continue reading (unlike letters addressed to someone already obligated to read)",
          'Because the introduction is the only part that will be read',
          'Articles do not require introductions',
        ],
        a: 1,
        e: "Unlike letters (where the recipient is somewhat obligated to read the content), articles compete for the attention of a general readership who may choose not to continue reading. A strong introduction is essential to 'hook' readers and encourage them to engage with the full piece.",
        h: "Articles must COMPETE for readers' attention — a strong intro draws readers in.",
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Conditional Clauses and Complex Sentences',
    topicCode: 'SS2-ENG-17',
    module: 'Lexis and Structure',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Conditional clauses</span> express conditions and their consequences — what happens (or would happen) if a certain situation occurs. They are classified by the LIKELIHOOD of the condition being fulfilled: likely, unlikely, or impossible.\n</div>\n\n<h3 class="learn-subheading">1. Conditional Clauses by Likelihood</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Likelihood</th><th>Structure</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>LIKELY (real future possibility)</td><td>If + Present Simple, + will + base verb</td><td>\'If I study hard, I will pass the exam.\'</td></tr>\n      <tr><td>UNLIKELY (hypothetical present/future)</td><td>If + Past Simple, + would + base verb</td><td>\'If I won the lottery, I would buy a house.\'</td></tr>\n      <tr><td>IMPOSSIBLE (unreal past — cannot be changed)</td><td>If + Past Perfect, + would have + past participle</td><td>\'If I had studied harder, I would have passed.\' (but I didn\'t, so I failed — unchangeable past)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">2. Complex Sentences</h3>\n<p class="learn-p">A complex sentence contains ONE independent clause and AT LEAST ONE dependent (subordinate) clause, joined by a SUBORDINATING CONJUNCTION.</p>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Category</th><th>Subordinating Conjunctions</th></tr></thead>\n    <tbody>\n      <tr><td>Time</td><td>when, while, before, after, since, as soon as, until</td></tr>\n      <tr><td>Reason/Cause</td><td>because, since, as</td></tr>\n      <tr><td>Condition</td><td>if, unless, provided that, in case</td></tr>\n      <tr><td>Contrast/Concession</td><td>although, though, even though, whereas</td></tr>\n      <tr><td>Purpose</td><td>so that, in order that</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">3. Constructing Complex Sentences</h3>\n<ul class="learn-list">\n  <li>If the dependent clause comes FIRST, use a COMMA after it: \'Although it rained, the match continued.\'</li>\n  <li>If the dependent clause comes SECOND (after the independent clause), NO comma is typically needed: \'The match continued although it rained.\'</li>\n  <li>A dependent clause CANNOT stand alone as a complete sentence — it needs the independent clause to be grammatically complete</li>\n</ul>\n\n<h3 class="learn-subheading">4. Vocabulary Development: Words Associated with Publishing and Printing</h3>\n<p class="learn-p"><strong>manuscript</strong> (an author\'s original text before publication), <strong>proofread</strong> (to check for errors before final printing), <strong>typeset</strong> (to arrange text for printing), <strong>publisher</strong> (the company that produces and distributes a book), <strong>royalty</strong> (payment to an author based on sales), <strong>ISBN</strong> (a unique book identification number), <strong>edition</strong> (a version of a published work), <strong>print run</strong> (the number of copies printed at once).</p>\n\n<h3 class="learn-subheading">5. Words of the Week (Selection)</h3>\n<p class="learn-p"><strong>acronym</strong> (a word formed from initial letters), <strong>troupe</strong> (a group of performers), <strong>ardour</strong> (intense passion/enthusiasm), <strong>behemoth</strong> (something huge/enormous), <strong>calumny</strong> (a false, damaging statement made to harm someone\'s reputation), <strong>indigent</strong> (extremely poor), <strong>dissemble</strong> (to conceal one\'s true feelings/intentions).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Likely = Present Simple + will (real possibility). Unlikely = Past Simple + would (hypothetical, but possible). Impossible = Past Perfect + would have + past participle (unreal past, cannot be changed). Complex sentences need a SUBORDINATING conjunction (because, although, when, if) joining an independent and a dependent clause. Comma needed when dependent clause comes FIRST.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Conditional clauses by likelihood: Likely (Present+will), Unlikely (Past+would), Impossible (Past Perfect+would have+participle). Complex sentences: independent clause + dependent clause joined by subordinating conjunction (time, reason, condition, contrast, purpose categories). Comma rule: dependent clause FIRST needs comma; dependent clause SECOND usually doesn\'t.\n</div>\n  ',
    questions: [
      {
        q: 'Which sentence demonstrates a LIKELY conditional (real future possibility)?',
        o: [
          'If I won the lottery, I would travel the world.',
          'If I study hard, I will pass the exam.',
          'If I had studied harder, I would have passed.',
          'If pigs could fly, I would believe anything.',
        ],
        a: 1,
        e: "'If I study hard, I will pass the exam' uses Present Simple + will, expressing a REAL, LIKELY future condition — studying hard is a genuinely possible action with a realistic expected outcome.",
        h: "Present Simple + 'will' = LIKELY/real future possibility.",
        yr: 'SS2',
      },
      {
        q: 'Which sentence demonstrates an UNLIKELY (hypothetical) conditional?',
        o: [
          'If it rains, I will stay home.',
          'If I won the lottery, I would buy a house.',
          'If you heat water, it boils.',
          'If I had known, I would have told you.',
        ],
        a: 1,
        e: "'If I won the lottery, I would buy a house' uses Past Simple + would, expressing an UNLIKELY but possible hypothetical situation — winning the lottery is improbable but not impossible.",
        h: "Past Simple + 'would' = UNLIKELY/hypothetical situation.",
        yr: 'SS2',
      },
      {
        q: 'Which sentence demonstrates an IMPOSSIBLE conditional (unreal past, unchangeable)?',
        o: [
          'If she calls, I will answer.',
          'If I were taller, I would play basketball.',
          'If I had studied harder, I would have passed the exam.',
          'If you press the button, the light turns on.',
        ],
        a: 2,
        e: "'If I had studied harder, I would have passed the exam' uses Past Perfect + would have + past participle, expressing an IMPOSSIBLE/unreal past condition — the studying didn't happen, so the passing couldn't happen either; this past cannot be changed.",
        h: "Past Perfect + 'would have' + past participle = IMPOSSIBLE/unchangeable past.",
        yr: 'SS2',
      },
      {
        q: 'Which of the following is a SUBORDINATING conjunction used to introduce a clause of REASON?',
        o: ['And', 'Because', 'But', 'Or'],
        a: 1,
        e: "'Because' is a subordinating conjunction introducing a reason/cause clause: 'She stayed home because she was sick.' 'And', 'but', and 'or' are COORDINATING conjunctions, used to join independent clauses of equal grammatical status.",
        h: 'Subordinating conjunctions create DEPENDENT clauses; coordinating conjunctions (and, but, or) join independent clauses.',
        yr: 'SS2',
      },
      {
        q: "In the sentence 'Although it rained, the match continued,' which punctuation rule applies?",
        o: [
          'No comma is needed because the dependent clause comes first',
          'A comma is needed after the dependent clause because it comes FIRST in the sentence',
          'A semicolon must be used instead of a comma',
          'A colon must separate the two clauses',
        ],
        a: 1,
        e: "When a dependent clause comes BEFORE the independent clause, a comma is required after the dependent clause: 'Although it rained, [comma] the match continued.' If reversed ('The match continued although it rained'), no comma is typically needed.",
        h: 'Dependent clause FIRST = comma needed after it.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly demonstrates a complex sentence with a TIME clause?',
        o: [
          'She left before he arrived.',
          'She left, and he arrived.',
          'She left or he arrived.',
          'She left but he arrived.',
        ],
        a: 0,
        e: "'She left before he arrived' uses the subordinating conjunction 'before' to introduce a time clause, creating a complex sentence with one independent clause ('she left') and one dependent clause ('before he arrived').",
        h: 'Look for a TIME-related subordinating conjunction (before, after, when, while).',
        yr: 'SS2',
      },
      {
        q: "What does 'acronym' mean?",
        o: [
          'A word formed from the initial letters of a series of words',
          'A type of long, formal speech',
          'A word with multiple meanings',
          'A grammatical error',
        ],
        a: 0,
        e: "An 'acronym' is a word formed by combining the first letters (or parts) of a series of words, often pronounced as a single word. Example: 'NATO' (North Atlantic Treaty Organization), 'WAEC' (West African Examinations Council).",
        h: "Think of words like 'NATO' or 'WAEC' — formed from INITIAL LETTERS.",
        yr: 'SS2',
      },
      {
        q: "A 'troupe' refers to:",
        o: [
          'A military unit',
          'A group of actors, dancers, or other performers who work together',
          'A type of legal document',
          'A formal celebration',
        ],
        a: 1,
        e: "'Troupe' refers to a group of performers, such as actors, dancers, or acrobats, who work together. Example: 'The dance troupe toured several countries.' (Note: not to be confused with 'troop', meaning soldiers.)",
        h: 'This word relates to a GROUP of PERFORMERS (actors, dancers).',
        yr: 'SS2',
      },
      {
        q: "What does 'ardour' mean?",
        o: [
          'Complete indifference or lack of interest',
          'Intense passion, enthusiasm, or zeal',
          'A formal written agreement',
          'Extreme calmness',
        ],
        a: 1,
        e: "'Ardour' (or 'ardor' in American spelling) refers to great warmth of feeling, enthusiasm, or passion. Example: 'She pursued her studies with great ardour.'",
        h: 'This word relates to intense PASSION and ENTHUSIASM.',
        yr: 'SS2',
      },
      {
        q: "Which word describes something 'huge or enormous, often in a way that is overwhelming or monstrous'?",
        o: ['Indigent', 'Behemoth', 'Calumny', 'Dissemble'],
        a: 1,
        e: "'Behemoth' describes something of immense size or power, often overwhelming. Example: 'The new shopping mall is a behemoth compared to the small local shops.'",
        h: 'This word means something HUGE/ENORMOUS.',
        yr: 'SS2',
      },
      {
        q: "What does 'calumny' mean?",
        o: [
          'A sincere compliment',
          "A false and malicious statement made to damage someone's reputation",
          'A type of legal contract',
          'An act of generosity',
        ],
        a: 1,
        e: "'Calumny' refers to the making of false, damaging statements about someone — a malicious slander. Example: 'He suffered years of calumny before his name was finally cleared.'",
        h: "This word relates to FALSE, damaging statements about someone's character.",
        yr: 'SS2',
      },
      {
        q: "Which word describes a person who is 'extremely poor'?",
        o: ['Behemoth', 'Indigent', 'Ardour', 'Troupe'],
        a: 1,
        e: "'Indigent' describes someone in a state of extreme poverty, lacking the basic necessities of life. Example: 'The charity provides food and shelter for indigent families.'",
        h: 'This word means EXTREMELY POOR.',
        yr: 'SS2',
      },
      {
        q: "To 'dissemble' means to:",
        o: [
          "Openly and honestly express one's feelings",
          "Conceal one's true feelings, intentions, or beliefs, often through deception",
          'Construct something from individual parts',
          'Celebrate an achievement publicly',
        ],
        a: 1,
        e: "'Dissemble' means to hide one's true feelings or intentions, often by pretending something false — to be deceptive or evasive. Example: 'He dissembled his anger behind a calm expression.'",
        h: 'This word means to CONCEAL true feelings, often deceptively.',
        yr: 'SS2',
      },
      {
        q: "What is a 'manuscript' in publishing?",
        o: [
          'The final printed and bound book',
          "An author's original text, typically before it has been edited and published",
          'A type of printing machine',
          'A book review',
        ],
        a: 1,
        e: "'Manuscript' refers to an author's original written work, submitted before editing, typesetting, and final publication. Example: 'The publisher reviewed the manuscript before offering a book deal.'",
        h: 'This term relates to the ORIGINAL text before publication.',
        yr: 'SS2',
      },
      {
        q: "What does 'royalty' mean in the publishing industry?",
        o: [
          'A member of a royal family',
          'Payment made to an author based on the sales of their published work',
          'The cost of printing a book',
          'A type of book cover design',
        ],
        a: 1,
        e: "In publishing, 'royalty' refers to the percentage of sales revenue paid to an author for each copy of their book sold — a form of ongoing compensation tied to the work's commercial success.",
        h: 'In publishing, this term relates to PAYMENT to authors based on SALES.',
        yr: 'SS2',
      },
      {
        q: "To 'proofread' a manuscript means to:",
        o: [
          'Translate it into another language',
          'Carefully check it for errors (spelling, grammar, formatting) before final printing',
          'Print multiple copies of it',
          'Sell it to a publisher',
        ],
        a: 1,
        e: "'Proofread' means to carefully review a text for errors — spelling mistakes, grammatical errors, formatting issues — before it goes to final printing or publication.",
        h: 'This term relates to CHECKING FOR ERRORS before final publication.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly demonstrates a complex sentence with a CONDITION clause?',
        o: [
          'Unless you study, you will fail the exam.',
          'You study, and you will pass the exam.',
          'You study, so you will pass the exam.',
          'You study or you will fail the exam.',
        ],
        a: 0,
        e: "'Unless you study, you will fail the exam' uses the subordinating conjunction 'unless' (meaning 'if not') to introduce a condition clause, making this a complex sentence with one dependent and one independent clause.",
        h: "'Unless' is a subordinating conjunction expressing a NEGATIVE CONDITION.",
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly demonstrates a complex sentence with a CONTRAST/concession clause?',
        o: [
          'She is tired, but she continues working.',
          'Although she is tired, she continues working.',
          'She is tired, and she continues working.',
          'She is tired, so she continues working.',
        ],
        a: 1,
        e: "'Although she is tired, she continues working' uses the subordinating conjunction 'although' to introduce a concession/contrast clause. (Note: 'but' in option A is a COORDINATING conjunction, making that a compound sentence, not complex.)",
        h: "'Although' is SUBORDINATING (complex sentence); 'but' is COORDINATING (compound sentence).",
        yr: 'SS2',
      },
      {
        q: 'An ISBN refers to:',
        o: [
          'A type of literary award',
          'A unique identification number assigned to a published book',
          "The author's royalty percentage",
          'A government publishing license',
        ],
        a: 1,
        e: 'ISBN (International Standard Book Number) is a unique numeric identifier assigned to each edition of a published book, used internationally for cataloguing, ordering, and tracking sales.',
        h: 'This acronym relates to a UNIQUE NUMBER identifying a specific book.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly distinguishes IMPOSSIBLE from UNLIKELY conditional structures?',
        o: [
          'Both use the same exact verb forms with no difference',
          'Unlikely uses Past Simple + would (hypothetical present/future); Impossible uses Past Perfect + would have + past participle (unreal, unchangeable past)',
          'Impossible conditionals are always about the future',
          'Unlikely conditionals always use Present Simple',
        ],
        a: 1,
        e: "The key distinction: UNLIKELY conditionals (Past Simple + would) refer to improbable but still POSSIBLE present/future situations ('If I won the lottery...'). IMPOSSIBLE conditionals (Past Perfect + would have) refer to PAST situations that cannot be changed because they already happened (or didn't happen) a certain way.",
        h: 'Unlikely = hypothetical present/future (still possible); Impossible = unchangeable past.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Speech Writing for Special Purposes',
    topicCode: 'SS2-ENG-18',
    module: 'Composition',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  A <span class="learn-keyword">speech</span> is a formal address delivered orally to an audience, designed to inform, persuade, entertain, or mark a special occasion. WAEC tests specific TYPES of speeches: welcome address, farewell address, and prize-giving day address, each with distinct conventions.\n</div>\n\n<h3 class="learn-subheading">1. General Structure of a Speech</h3>\n<ul class="learn-list">\n  <li><strong>Salutation/Greeting:</strong> Addresses the audience appropriately (e.g., \'Good morning, distinguished guests, fellow students...\')</li>\n  <li><strong>Introduction:</strong> States the purpose/occasion of the speech</li>\n  <li><strong>Body:</strong> Develops the main message in a logical sequence</li>\n  <li><strong>Conclusion:</strong> Summarises key points; ends memorably (often with thanks or a call to action)</li>\n</ul>\n\n<h3 class="learn-subheading">2. Welcome Address</h3>\n<ul class="learn-list">\n  <li>Purpose: To formally welcome guests/visitors to an event</li>\n  <li>Content: Greeting, acknowledgement of important guests, brief background of the event/occasion, expression of pleasure at their presence</li>\n  <li>Tone: Warm, welcoming, appreciative</li>\n</ul>\n\n<h3 class="learn-subheading">3. Farewell Address</h3>\n<ul class="learn-list">\n  <li>Purpose: To bid goodbye to someone leaving (graduating, retiring, relocating)</li>\n  <li>Content: Appreciation for the person\'s contributions, fond memories/highlights, well-wishes for the future</li>\n  <li>Tone: Reflective, appreciative, warm, sometimes nostalgic</li>\n</ul>\n\n<h3 class="learn-subheading">4. Prize-Giving Day Address</h3>\n<ul class="learn-list">\n  <li>Purpose: To celebrate and recognise achievement at an awards ceremony</li>\n  <li>Content: Congratulations to award recipients, acknowledgement of effort and hard work, encouragement for continued excellence, possibly remarks on the institution\'s achievements</li>\n  <li>Tone: Celebratory, congratulatory, motivational</li>\n</ul>\n\n<h3 class="learn-subheading">5. Differences Between a Speech and an Article</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Feature</th><th>Speech</th><th>Article</th></tr></thead>\n    <tbody>\n      <tr><td>Delivery</td><td>Spoken aloud to a live audience</td><td>Written for readers (print/online)</td></tr>\n      <tr><td>Opening</td><td>Direct address/greeting to the audience</td><td>Catchy title/headline</td></tr>\n      <tr><td>Language</td><td>More rhetorical devices, direct address (\'you\', \'we\')</td><td>More analytical/descriptive, can use third person throughout</td></tr>\n      <tr><td>Engagement</td><td>Uses pauses, repetition, vocal emphasis cues</td><td>Uses formatting, subheadings, paragraphing</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">6. Vocabulary: Words of the Week (Selection)</h3>\n<p class="learn-p"><strong>complicit</strong> (involved with others in an illegal/wrongful act), <strong>impecunious</strong> (having little/no money), <strong>encumber</strong> (to weigh down/hinder), <strong>mollify</strong> (to calm/soothe someone\'s anger), <strong>frivolous</strong> (not serious/silly, especially when seriousness is needed), <strong>disrepute</strong> (a state of having a bad reputation), <strong>benevolent</strong> (kind and generous).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Speech writing must include DIRECT ADDRESS to the audience (\'Ladies and gentlemen...\', \'Fellow students...\'). Welcome address = greeting and appreciation. Farewell = thanks + good wishes. Prize-giving = congratulations + encouragement. Unlike an article, a speech is meant to be SPOKEN, so use rhetorical devices (repetition, direct questions to audience) and a conversational, engaging tone.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Speech structure: salutation, introduction (purpose/occasion), body (main message), conclusion (summary/memorable ending). Types: Welcome address (greet/appreciate), Farewell address (thank/reflect/wish well), Prize-giving address (congratulate/encourage). Differs from article: spoken not written, direct audience address, rhetorical devices, conversational engagement vs analytical reading.\n</div>\n  ',
    questions: [
      {
        q: 'What is the PRIMARY purpose of a WELCOME ADDRESS?',
        o: [
          'To say goodbye to someone leaving an institution',
          'To formally welcome guests or visitors to an event, expressing pleasure at their presence',
          'To congratulate prize winners at a ceremony',
          "To report on a school's financial situation",
        ],
        a: 1,
        e: 'A welcome address formally greets and welcomes guests/visitors to an event — acknowledging important attendees, providing brief context for the occasion, and expressing genuine pleasure at their presence.',
        h: "'Welcome' relates to GREETING people at the START of an event.",
        yr: 'SS2',
      },
      {
        q: 'Which tone is MOST appropriate for a FAREWELL ADDRESS?',
        o: [
          'Harsh and critical',
          "Reflective, appreciative, and warm — acknowledging the person's contributions and wishing them well",
          'Strictly factual with no emotional content',
          'Comedic and entirely humorous',
        ],
        a: 1,
        e: "A farewell address should reflect appreciation for the departing person's contributions, fondly recall shared memories/achievements, and express genuine warmth and well-wishes for their future — striking a reflective, sincere tone.",
        h: "Farewell speeches THANK and REFLECT on someone's contributions before they leave.",
        yr: 'SS2',
      },
      {
        q: 'What should be the CENTRAL focus of a PRIZE-GIVING DAY ADDRESS?',
        o: [
          'Criticising students who did not win awards',
          'Congratulating award recipients, acknowledging their hard work, and encouraging continued excellence',
          'Providing a detailed financial report of the school',
          'Discussing unrelated political issues',
        ],
        a: 1,
        e: 'A prize-giving address centres on celebrating achievement — congratulating winners, recognising the effort behind their success, and using the occasion to motivate continued hard work and excellence among the wider audience.',
        h: 'This speech CELEBRATES achievement and MOTIVATES continued excellence.',
        yr: 'SS2',
      },
      {
        q: 'Which feature is ESSENTIAL to a speech but NOT typically required in an article?',
        o: [
          'A clear conclusion',
          "Direct address to the audience (e.g., 'Ladies and gentlemen', 'Fellow students')",
          'Logical organisation of ideas',
          'Correct grammar and spelling',
        ],
        a: 1,
        e: "A speech is meant to be SPOKEN directly to a present audience, requiring direct address ('Ladies and gentlemen, distinguished guests...'). An article is written for readers who may encounter it in print or online, without needing direct address to a 'live' audience.",
        h: 'Speeches are SPOKEN to a present audience — they must directly ADDRESS that audience.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is an appropriate OPENING for a welcome address?',
        o: [
          'Vanishing Forests: A Crisis We Must Address',
          'Good morning, distinguished guests, esteemed colleagues, and fellow students. It is my honour to welcome you all to this auspicious occasion.',
          'Dear Sir, I am writing to express my dissatisfaction...',
          'Once upon a time, in a small village...',
        ],
        a: 1,
        e: "'Good morning, distinguished guests, esteemed colleagues, and fellow students. It is my honour to welcome you all to this auspicious occasion' directly addresses the audience with appropriate formal greeting and clearly states the welcoming purpose — exactly what opens a welcome address.",
        h: 'Look for the DIRECT, FORMAL greeting that explicitly WELCOMES the audience.',
        yr: 'SS2',
      },
      {
        q: "What does 'complicit' mean?",
        o: [
          'Completely innocent and uninvolved',
          'Involved with others in an illegal or wrongful act',
          'Extremely wealthy',
          'Showing great kindness',
        ],
        a: 1,
        e: "'Complicit' describes someone who is involved with others in carrying out a wrongful or illegal act, even if not the main perpetrator. Example: 'He was complicit in the fraud scheme by signing falsified documents.'",
        h: 'This word relates to being INVOLVED in WRONGDOING, even indirectly.',
        yr: 'SS2',
      },
      {
        q: "Which word means 'having very little or no money; poor'?",
        o: ['Benevolent', 'Impecunious', 'Frivolous', 'Mollify'],
        a: 1,
        e: "'Impecunious' describes someone who has little or no money — poor, often habitually so. Example: 'The impecunious artist struggled to pay rent.'",
        h: 'This word means having LITTLE MONEY/being poor.',
        yr: 'SS2',
      },
      {
        q: "To 'encumber' someone or something means to:",
        o: [
          'Free them from any burden or restriction',
          'Weigh them down or hinder their movement/progress with a burden',
          'Celebrate their achievement',
          'Provide financial assistance',
        ],
        a: 1,
        e: "'Encumber' means to burden or hinder someone, making it difficult for them to move or act freely. Example: 'The heavy luggage encumbered her as she rushed through the airport.'",
        h: 'This word means to BURDEN/hinder someone or something.',
        yr: 'SS2',
      },
      {
        q: "What does 'mollify' mean?",
        o: [
          'To provoke anger in someone',
          "To calm or soothe someone's anger or distress",
          'To formally announce something',
          'To completely ignore a problem',
        ],
        a: 1,
        e: "'Mollify' means to calm down or pacify someone who is angry or upset. Example: 'The manager tried to mollify the frustrated customer with a sincere apology.'",
        h: "This word means to CALM/SOOTHE someone's anger.",
        yr: 'SS2',
      },
      {
        q: "Which word describes something 'not serious, silly, or trivial, especially when seriousness is called for'?",
        o: ['Benevolent', 'Disrepute', 'Frivolous', 'Impecunious'],
        a: 2,
        e: "'Frivolous' describes something lacking seriousness or substance — silly or trivial, especially inappropriately so. Example: 'The court dismissed the frivolous lawsuit.'",
        h: 'This word means NOT SERIOUS or TRIVIAL — silly when seriousness is needed.',
        yr: 'SS2',
      },
      {
        q: "What does 'disrepute' mean?",
        o: [
          'A state of being highly respected and admired',
          'A state of having a bad reputation or being held in low regard',
          'A formal legal document',
          'An act of extreme generosity',
        ],
        a: 1,
        e: "'Disrepute' refers to a damaged or poor reputation — being viewed unfavourably by others. Example: 'The scandal brought the company into disrepute.'",
        h: 'This word means having a BAD reputation.',
        yr: 'SS2',
      },
      {
        q: "A 'benevolent' person is one who is:",
        o: [
          'Cruel and unkind',
          'Kind, generous, and well-meaning toward others',
          'Extremely poor',
          'Always silly and trivial',
        ],
        a: 1,
        e: "'Benevolent' describes someone characterised by kindness, generosity, and goodwill toward others. Example: 'The benevolent philanthropist donated millions to charity.'",
        h: 'This word means KIND and GENEROUS.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following should typically be included in the BODY of a farewell address?',
        o: [
          'A detailed weather forecast',
          'Specific memories, achievements, and contributions of the person leaving',
          'An unrelated business proposal',
          'A list of unrelated grievances',
        ],
        a: 1,
        e: 'The body of a farewell address should highlight specific memories, achievements, and meaningful contributions of the departing person — making the speech personal, genuine, and appreciative rather than generic.',
        h: 'The body should focus on SPECIFIC, personal details about the departing person.',
        yr: 'SS2',
      },
      {
        q: 'Why is it important for a speech to have a MEMORABLE conclusion?',
        o: [
          'Because the conclusion is the only part audiences remember and listen to',
          "Because it is the audience's last impression of the speech, often reinforcing the key message or leaving a lasting emotional impact",
          'Conclusions are not actually necessary in speeches',
          'Because WAEC requires exactly three sentences in every conclusion',
        ],
        a: 1,
        e: "Since a speech's conclusion is the final thing the audience hears, a strong, memorable ending reinforces the central message and leaves a lasting impression — often through a powerful statement, call to action, or expression of gratitude.",
        h: "The conclusion is the audience's LAST IMPRESSION — make it impactful.",
        yr: 'SS2',
      },
      {
        q: 'Which sentence demonstrates an appropriate CLOSING for a prize-giving day address?',
        o: [
          'Dear Sir, I look forward to your response.',
          'Once again, congratulations to all our award winners. May this achievement inspire you to reach even greater heights. Thank you.',
          'Yours faithfully, The Principal.',
          'I have nothing more to say about this matter.',
        ],
        a: 1,
        e: "'Once again, congratulations to all our award winners. May this achievement inspire you to reach even greater heights. Thank you' appropriately closes with congratulations, motivation, and gratitude — fitting the celebratory, motivational tone of a prize-giving address.",
        h: 'A prize-giving closing should CONGRATULATE again and MOTIVATE continued effort.',
        yr: 'SS2',
      },
      {
        q: 'What rhetorical technique involves repeating a phrase or idea for emphasis in a speech?',
        o: [
          'Alliteration only',
          'Repetition — deliberately repeating words/phrases to emphasise key points and create memorable impact',
          'Avoiding all forms of emphasis',
          'Only used in written articles, never speeches',
        ],
        a: 1,
        e: "Repetition is a powerful rhetorical device in speeches — deliberately repeating key words or phrases reinforces the central message and makes it more memorable to a listening audience (e.g., Martin Luther King Jr.'s famous repeated phrase 'I have a dream').",
        h: 'This technique deliberately REPEATS phrases for emphasis and memorability.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following best distinguishes the LANGUAGE STYLE of a speech from an article?',
        o: [
          'Speeches never use formal language; articles always do',
          "Speeches often use direct address ('you', 'we') and conversational/rhetorical devices suited to spoken delivery; articles can be more analytical and are typically read rather than heard",
          'Articles always use first person; speeches never do',
          'There is no difference in language style between speeches and articles',
        ],
        a: 1,
        e: "Speeches, being delivered orally to a present audience, typically use direct address ('you', 'we', 'fellow students') and rhetorical devices (repetition, rhetorical questions) suited to spoken communication, while articles are crafted for silent reading and can adopt a more analytical, less directly engaging style.",
        h: 'Speeches are SPOKEN to a live audience (direct address); articles are READ (more analytical).',
        yr: 'SS2',
      },
      {
        q: 'In a welcome address, why is it important to acknowledge specific important guests by name/title?',
        o: [
          'It is not actually important and can be skipped entirely',
          'It shows respect and recognition for their presence, and is a standard courtesy expected in formal welcome addresses',
          "Only the most senior guest's name should ever be mentioned",
          'Acknowledging guests by name is considered rude in formal speeches',
        ],
        a: 1,
        e: "Acknowledging important guests by name/title (e.g., 'I recognise the presence of our Chief Guest, Dr. Adeyemi...') demonstrates respect, formal courtesy, and proper protocol — an expected convention in welcome addresses at formal events.",
        h: 'Naming important guests shows RESPECT and follows formal SPEECH PROTOCOL.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following would be an INAPPROPRIATE element to include in a prize-giving day address?',
        o: [
          'Congratulations to the award recipients',
          'Acknowledgement of the hard work behind the achievements',
          'A lengthy, irrelevant complaint about unrelated school administrative issues',
          'Encouragement for continued excellence',
        ],
        a: 2,
        e: 'A lengthy, irrelevant complaint about unrelated administrative issues would be inappropriate and off-topic for a prize-giving address, which should remain focused on celebrating achievement, recognising effort, and motivating continued excellence.',
        h: 'Stay ON-TOPIC — the prize-giving address should focus on CELEBRATION, not unrelated complaints.',
        yr: 'SS2',
      },
      {
        q: 'What distinguishes the SALUTATION of a speech from the salutation of a letter?',
        o: [
          "A speech's salutation directly greets the entire LIVE AUDIENCE present (e.g., 'Ladies and gentlemen'); a letter's salutation addresses ONE recipient (e.g., 'Dear Sir')",
          'There is no difference between speech and letter salutations',
          'Speeches never include any greeting',
          'Letters always greet multiple people at once',
        ],
        a: 0,
        e: "A speech's opening greeting addresses the entire audience present at the event ('Ladies and gentlemen, distinguished guests...'), while a letter's salutation is directed at a SINGLE recipient ('Dear Sir/Madam' or 'Dear [Name]').",
        h: 'Speech salutation = greets a GROUP/audience; Letter salutation = greets ONE recipient.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Affixation: Prefixes as Word Extensions',
    topicCode: 'SS2-ENG-19',
    module: 'Lexis and Structure',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Affixation</span> through PREFIXES extends or modifies the meaning of root words. Understanding common prefixes and their meanings allows students to decode unfamiliar vocabulary and build new words systematically — a vital vocabulary-expansion skill for WAEC.\n</div>\n\n<h3 class="learn-subheading">1. Common Negative Prefixes</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Prefix</th><th>Meaning</th><th>Examples</th></tr></thead>\n    <tbody>\n      <tr><td>un-</td><td>not</td><td>unhappy, unfair, unable</td></tr>\n      <tr><td>dis-</td><td>not / opposite of</td><td>disagree, dishonest, disappear</td></tr>\n      <tr><td>in-/im-</td><td>not</td><td>incomplete, impossible, inactive</td></tr>\n      <tr><td>il-</td><td>not (before \'l\')</td><td>illegal, illiterate, illogical</td></tr>\n      <tr><td>ir-</td><td>not (before \'r\')</td><td>irregular, irresponsible, irrelevant</td></tr>\n      <tr><td>non-</td><td>not</td><td>nonexistent, non-fiction</td></tr>\n      <tr><td>mis-</td><td>wrongly</td><td>misunderstand, misspell, misinform</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">2. Prefixes of Quantity/Number</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Prefix</th><th>Meaning</th><th>Examples</th></tr></thead>\n    <tbody>\n      <tr><td>mono-</td><td>one</td><td>monologue, monopoly, monogamy</td></tr>\n      <tr><td>bi-</td><td>two</td><td>bicycle, bilingual, biannual</td></tr>\n      <tr><td>tri-</td><td>three</td><td>triangle, tricycle, trio</td></tr>\n      <tr><td>multi-/poly-</td><td>many</td><td>multicultural, polygon, polygamy</td></tr>\n      <tr><td>semi-</td><td>half</td><td>semicircle, semifinal</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">3. Other Common Prefixes</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Prefix</th><th>Meaning</th><th>Examples</th></tr></thead>\n    <tbody>\n      <tr><td>re-</td><td>again</td><td>rewrite, replay, return</td></tr>\n      <tr><td>pre-</td><td>before</td><td>preview, prehistoric, prepare</td></tr>\n      <tr><td>post-</td><td>after</td><td>postpone, postwar, postscript</td></tr>\n      <tr><td>anti-</td><td>against</td><td>antisocial, antiwar, antibiotic</td></tr>\n      <tr><td>auto-</td><td>self</td><td>autobiography, automatic</td></tr>\n      <tr><td>sub-</td><td>under/below</td><td>submarine, subway, substandard</td></tr>\n      <tr><td>super-</td><td>above/beyond</td><td>superhuman, supermarket</td></tr>\n      <tr><td>trans-</td><td>across</td><td>transport, transatlantic</td></tr>\n      <tr><td>inter-</td><td>between</td><td>international, interact</td></tr>\n      <tr><td>ex-</td><td>former / out of</td><td>ex-president, exhale</td></tr>\n      <tr><td>co-</td><td>together</td><td>cooperate, coexist</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">4. Vocabulary Development: Words Associated with Industries</h3>\n<p class="learn-p"><strong>manufacturing</strong>, <strong>commodity</strong> (a raw material/product that can be traded), <strong>assembly line</strong> (a production process where items move through stages), <strong>raw material</strong>, <strong>output</strong>, <strong>conglomerate</strong> (a large corporation made of several companies), <strong>monopoly</strong> (exclusive control of an industry by one entity), <strong>subsidiary</strong> (a company controlled by another company).</p>\n\n<h3 class="learn-subheading">5. Words of the Week (Selection)</h3>\n<p class="learn-p"><strong>arcane</strong> (mysterious/understood by few), <strong>plenitude</strong> (an abundance/large quantity), <strong>interject</strong> (to interrupt with a remark), <strong>primeval</strong> (relating to the earliest age of the world; ancient), <strong>quandary</strong> (a difficult situation requiring a decision), <strong>vacillate</strong> (to waver/hesitate between choices).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Negative prefixes vary by following letter: il- before \'l\' (illegal), ir- before \'r\' (irregular), im- before \'m\'/\'p\'/\'b\' (impossible, immature), in- elsewhere (incomplete). Number prefixes: mono(1), bi(2), tri(3), multi/poly(many), semi(half). \'Mis-\' means WRONGLY, not simply \'not\' (misunderstand = understand WRONGLY, not \'not understand\').\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Prefixes extend word meaning. Negative: un-, dis-, in-/im-, il-, ir-, non-, mis- (wrongly). Quantity: mono(1), bi(2), tri(3), multi/poly(many), semi(half). Other key prefixes: re-(again), pre-(before), post-(after), anti-(against), auto-(self), sub-(under), super-(above), trans-(across), inter-(between), ex-(former), co-(together).\n</div>\n  ',
    questions: [
      {
        q: "Which prefix correctly means 'not' before the letter 'l'?",
        o: ['in-', 'ir-', 'il-', 'un-'],
        a: 2,
        e: "'Il-' is the form of the negative prefix used before words beginning with 'l': illegal, illiterate, illogical. This is a sound-assimilation pattern where 'in-' changes to match the following consonant.",
        h: "Think of 'illegal' — which prefix form comes before words starting with 'L'?",
        yr: 'SS2',
      },
      {
        q: "What does the prefix 'mis-' mean in words like 'misunderstand' and 'misspell'?",
        o: ['Not at all', 'Wrongly or incorrectly', 'Again', 'Before'],
        a: 1,
        e: "'Mis-' means 'wrongly' or 'incorrectly' — 'misunderstand' means to understand WRONGLY (not simply 'not understand'), and 'misspell' means to spell INCORRECTLY. This is different from simple negation prefixes like 'un-' or 'dis-'.",
        h: "'Mis-' indicates something done INCORRECTLY, not simply its absence.",
        yr: 'SS2',
      },
      {
        q: "Which prefix means 'two' in words like 'bicycle' and 'bilingual'?",
        o: ['mono-', 'bi-', 'tri-', 'multi-'],
        a: 1,
        e: "'Bi-' means 'two': bicycle (two wheels), bilingual (two languages), biannual (twice a year). This is a number prefix derived from Latin.",
        h: "Think of 'bicycle' — how many wheels does it have?",
        yr: 'SS2',
      },
      {
        q: "What does the prefix 'anti-' mean in 'antisocial' and 'antibiotic'?",
        o: ['For/in favour of', 'Against', 'Before', 'After'],
        a: 1,
        e: "'Anti-' means 'against' or 'opposing': antisocial (against social interaction), antibiotic (against biotic/living organisms, i.e., bacteria), antiwar (against war).",
        h: "Think of an 'antibiotic' fighting AGAINST bacteria.",
        yr: 'SS2',
      },
      {
        q: "Which word uses the prefix 'sub-' meaning 'under/below'?",
        o: ['Superhuman', 'Submarine', 'Transport', 'International'],
        a: 1,
        e: "'Submarine' uses 'sub-' (under) + 'marine' (sea/water-related) — literally meaning 'under the sea'. Other examples: subway (under the road/ground), substandard (below standard).",
        h: "A 'submarine' travels WHERE relative to the sea's surface?",
        yr: 'SS2',
      },
      {
        q: "What does the prefix 'auto-' mean in 'autobiography' and 'automatic'?",
        o: ['Other/external', 'Self', 'Together', 'Between'],
        a: 1,
        e: "'Auto-' means 'self': autobiography is a biography written by ONESELF (about one's own life), automatic means operating BY ITSELF without external control.",
        h: "An 'autobiography' is written about whom, by whom?",
        yr: 'SS2',
      },
      {
        q: "Which prefix correctly completes: 'The doctor diagnosed the patient with an ___regular heartbeat.'",
        o: ['un-', 'in-', 'ir-', 'il-'],
        a: 2,
        e: "'Ir-' is the correct prefix form before words beginning with 'r': irregular, irresponsible, irrelevant. This follows the same sound-assimilation pattern as 'il-' before 'l'.",
        h: "Which prefix form comes before words starting with 'R'?",
        yr: 'SS2',
      },
      {
        q: "What does 'arcane' mean?",
        o: [
          'Widely known and easily understood',
          'Mysterious or secret; understood by very few people',
          'Extremely modern and up-to-date',
          'Loud and attention-grabbing',
        ],
        a: 1,
        e: "'Arcane' describes knowledge or information that is mysterious, obscure, and understood by very few people, often due to its specialised or esoteric nature. Example: 'The lawyer explained the arcane legal terminology.'",
        h: 'This word means MYSTERIOUS — known/understood by very FEW people.',
        yr: 'SS2',
      },
      {
        q: "Which word means 'an abundance or large quantity of something'?",
        o: ['Quandary', 'Plenitude', 'Arcane', 'Vacillate'],
        a: 1,
        e: "'Plenitude' refers to a full or abundant supply of something. Example: 'The harvest brought a plenitude of fresh produce to the market.'",
        h: 'This word relates to ABUNDANCE — a large quantity.',
        yr: 'SS2',
      },
      {
        q: "To 'interject' means to:",
        o: [
          'Remain completely silent during a conversation',
          'Interrupt a conversation with a sudden remark or comment',
          'Agree fully with everything said',
          'Formally conclude a discussion',
        ],
        a: 1,
        e: "'Interject' means to interrupt with a remark, often abruptly, during an ongoing conversation or discussion. Example: 'She interjected with an important point during the debate.'",
        h: 'This word relates to INTERRUPTING with a comment.',
        yr: 'SS2',
      },
      {
        q: "What does 'primeval' mean?",
        o: [
          'Extremely modern and futuristic',
          'Relating to the earliest age of the world; ancient and original',
          'Related to mathematics specifically',
          'A type of legal document',
        ],
        a: 1,
        e: "'Primeval' describes something belonging to the earliest period of the world's existence — primitive, ancient, original. Example: 'The primeval forest had remained untouched for millennia.'",
        h: 'This word means ANCIENT — relating to the EARLIEST age of the world.',
        yr: 'SS2',
      },
      {
        q: "A 'quandary' refers to:",
        o: [
          'A simple, easily-solved problem',
          'A difficult situation requiring a decision between unattractive options',
          'A formal celebration',
          'A type of building structure',
        ],
        a: 1,
        e: "'Quandary' describes a state of uncertainty or confusion over what to do in a difficult situation, especially when faced with two or more unattractive choices. Example: 'She was in a quandary about which job offer to accept.'",
        h: 'This word relates to a DIFFICULT decision/situation.',
        yr: 'SS2',
      },
      {
        q: "To 'vacillate' means to:",
        o: [
          'Make a quick, firm decision without hesitation',
          'Waver or hesitate between different opinions or courses of action',
          'Celebrate a major achievement',
          'Speak with great confidence and certainty',
        ],
        a: 1,
        e: "'Vacillate' means to alternate or waver indecisively between different opinions, actions, or courses — to be unable to commit to a choice. Example: 'He vacillated between accepting the job and staying at his current company.'",
        h: 'This word relates to HESITATING/wavering between choices.',
        yr: 'SS2',
      },
      {
        q: "What is a 'commodity' in the context of industries and trade?",
        o: [
          'A type of legal document',
          'A raw material or primary product that can be bought, sold, or traded',
          'A finished consumer product only',
          'A government regulatory body',
        ],
        a: 1,
        e: "'Commodity' refers to a basic good or raw material used in commerce that is interchangeable with other goods of the same type — such as oil, gold, wheat, or cotton — typically traded in bulk on markets.",
        h: 'This term relates to RAW MATERIALS/basic goods traded in commerce.',
        yr: 'SS2',
      },
      {
        q: "What does 'monopoly' mean in an industrial/economic context?",
        o: [
          'Competition among many companies in a market',
          'Exclusive control of a particular industry, market, or product by a single company or entity',
          'A type of factory assembly process',
          'A government tax on imports',
        ],
        a: 1,
        e: "'Monopoly' refers to a situation where a single company or entity has exclusive control over a particular market or industry, eliminating competition. Example: 'The company held a monopoly on the country's telecommunications.'",
        h: 'This term relates to EXCLUSIVE control of a market by ONE entity.',
        yr: 'SS2',
      },
      {
        q: "A 'subsidiary' company is one that is:",
        o: [
          'Completely independent with no connection to other companies',
          'Controlled by another (larger/parent) company, typically through majority ownership',
          'Always larger than its parent company',
          'A type of government agency',
        ],
        a: 1,
        e: "'Subsidiary' refers to a company that is controlled by another company (the 'parent company'), usually through ownership of a majority of its shares. Example: 'The car manufacturer's battery division operates as a subsidiary.'",
        h: 'This term relates to a company CONTROLLED by a larger PARENT company.',
        yr: 'SS2',
      },
      {
        q: "Which prefix correctly completes: 'The two countries decided to ___ operate on the joint infrastructure project.'",
        o: ['dis-', 'co-', 'anti-', 'ex-'],
        a: 1,
        e: "'Co-' means 'together', so 'cooperate' means to work TOGETHER. The other prefixes would create incorrect meanings: 'disoperate' is not a standard word, 'antioperate' suggests working against, and 'ex-operate' doesn't follow standard prefix patterns.",
        h: "Which prefix means 'TOGETHER'?",
        yr: 'SS2',
      },
      {
        q: "What does the prefix 'trans-' mean in 'transport' and 'transatlantic'?",
        o: ['Under/below', 'Across', 'Before', 'Self'],
        a: 1,
        e: "'Trans-' means 'across' or 'through': transport means to carry ACROSS distances, transatlantic means ACROSS the Atlantic Ocean. Other examples: transnational, transcontinental.",
        h: "'Transatlantic' flights go ACROSS which ocean?",
        yr: 'SS2',
      },
      {
        q: "Which word correctly uses the prefix 'ex-' to mean 'former'?",
        o: ['Exhale', 'Ex-president', 'Exit', 'Example'],
        a: 1,
        e: "'Ex-president' uses 'ex-' to mean 'former' — someone who previously held the office of president but no longer does. (Note: 'exhale', 'exit', and 'example' use 'ex-' in different senses, relating to 'out of' rather than 'former'.)",
        h: "An 'ex-president' is a FORMER holder of that office.",
        yr: 'SS2',
      },
      {
        q: "Which prefix correctly forms a word meaning 'three-sided shape'?",
        o: ['mono- (monogon)', 'bi- (bigon)', 'tri- (triangle)', 'multi- (multigon)'],
        a: 2,
        e: "'Tri-' (meaning three) + 'angle' forms 'triangle' — a shape with three angles/sides. This follows the standard number-prefix pattern: mono(1), bi(2), tri(3).",
        h: 'Think of the number prefix that means THREE.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Article Writing for Publication',
    topicCode: 'SS2-ENG-20',
    module: 'Composition',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  An <span class="learn-keyword">article for publication</span> is written specifically for a newspaper, magazine, or online platform, intended to inform, persuade, or engage a general readership on a topic of interest. WAEC expects candidates to master the specific features and writing techniques of this genre.\n</div>\n\n<h3 class="learn-subheading">1. Key Features of a Publishable Article</h3>\n<ul class="learn-list">\n  <li><strong>Engaging title:</strong> Captures attention and hints at the content</li>\n  <li><strong>Strong opening (hook):</strong> A compelling fact, question, anecdote, or statement that draws readers in</li>\n  <li><strong>Clear structure:</strong> Logically organised paragraphs, each developing one aspect of the topic</li>\n  <li><strong>Appropriate tone:</strong> Matches the topic and target readership (informative, persuasive, reflective)</li>\n  <li><strong>Strong conclusion:</strong> Summarises key points or offers a final thought/call to action</li>\n  <li><strong>No salutation/address</strong> (unlike letters) — written for a general audience</li>\n</ul>\n\n<h3 class="learn-subheading">2. Techniques for an Engaging Opening (Hook)</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Technique</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>Startling statistic/fact</td><td>\'Over 70% of Nigerian youths are unemployed.\'</td></tr>\n      <tr><td>Rhetorical question</td><td>\'Have you ever wondered why our roads remain in such poor condition?\'</td></tr>\n      <tr><td>Anecdote (brief story)</td><td>\'Last month, a young graduate in Lagos walked fifteen kilometres in search of work...\'</td></tr>\n      <tr><td>Direct statement of the issue</td><td>\'Youth unemployment has become one of Nigeria\'s most pressing challenges.\'</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">3. Structuring the Body of an Article</h3>\n<ul class="learn-list">\n  <li>Each paragraph should focus on ONE main point/aspect of the topic</li>\n  <li>Use TRANSITION words to maintain flow between paragraphs (furthermore, however, in addition, as a result)</li>\n  <li>Support points with EVIDENCE, EXAMPLES, or ILLUSTRATIONS where appropriate</li>\n  <li>Maintain a CONSISTENT tone and viewpoint throughout</li>\n</ul>\n\n<h3 class="learn-subheading">4. Article Writing Process</h3>\n<ol class="learn-list">\n  <li>Choose an engaging, relevant title</li>\n  <li>Draft a compelling opening hook</li>\n  <li>Organise body paragraphs logically (one idea per paragraph)</li>\n  <li>Write a conclusion that reinforces the central message</li>\n  <li>Review for clarity, coherence, and appropriate tone</li>\n</ol>\n\n<h3 class="learn-subheading">5. Using the Dictionary (Oral/Spelling Component)</h3>\n<ul class="learn-list">\n  <li>Dictionaries provide: SPELLING, PRONUNCIATION (often in IPA), MEANING, WORD CLASS, ETYMOLOGY (word origin), and SAMPLE USAGE</li>\n  <li>Words are listed ALPHABETICALLY for quick reference</li>\n  <li>Guide words at the top of each page show the first and last entries on that page</li>\n</ul>\n\n<h3 class="learn-subheading">6. Words of the Week (Selection)</h3>\n<p class="learn-p"><strong>arboreal</strong> (relating to or living in trees), <strong>camaraderie</strong> (mutual trust/friendship among a group), <strong>exalt</strong> (to praise/elevate highly), <strong>gaudy</strong> (extravagantly bright/showy, often in poor taste), <strong>mawkish</strong> (excessively sentimental), <strong>diffident</strong> (shy/lacking self-confidence), <strong>ornate</strong> (elaborately decorated).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Articles need NO salutation/address (unlike letters) — they begin directly with a TITLE and then a HOOK. Use varied opening techniques: statistics, rhetorical questions, anecdotes. Each body paragraph = ONE main point. WAEC marks articles on: appropriate format (title, no address), content development, language/register, and organisation.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Article features: engaging title, strong hook opening (statistic/question/anecdote), clear paragraph structure (one point each), appropriate tone, strong conclusion, NO salutation/address. Writing process: title → hook → organised body → conclusion → review. Dictionary use: spelling, pronunciation (IPA), meaning, word class, etymology, alphabetical organisation with guide words.\n</div>\n  ',
    questions: [
      {
        q: 'Which feature distinguishes an article for publication from a formal letter?',
        o: [
          "Articles require a recipient's address; letters do not",
          'An article begins with an engaging TITLE and has NO salutation/address (unlike a letter, which includes addresses and a salutation)',
          'Letters never have titles; articles always include addresses',
          'There is no structural difference between the two',
        ],
        a: 1,
        e: "Articles for publication are formatted with a catchy TITLE and written for a general readership — they do NOT include the writer's/recipient's address or a salutation, unlike formal letters, which require these specific layout elements.",
        h: 'Articles have a TITLE only; letters have addresses + salutation.',
        yr: 'SS2',
      },
      {
        q: "Which technique is described as using a 'STARTLING STATISTIC' to open an article?",
        o: [
          "'Once upon a time, there was a small village...'",
          "'Over 70% of Nigerian youths are unemployed.'",
          "'Dear Editor, I am writing about...'",
          "'Have you ever wondered why...?'",
        ],
        a: 1,
        e: "'Over 70% of Nigerian youths are unemployed' is a startling statistic — a specific, attention-grabbing number used to immediately convey the seriousness/scale of an issue and draw readers into the article.",
        h: 'Look for the SPECIFIC NUMBER/PERCENTAGE used to grab attention.',
        yr: 'SS2',
      },
      {
        q: "What is a 'rhetorical question' as an opening technique?",
        o: [
          'A question that requires a detailed written answer from the reader',
          'A question asked for effect, to provoke thought, rather than to elicit a literal answer',
          'A question only used in formal letters',
          'A question that must always be answered in the next sentence',
        ],
        a: 1,
        e: "A rhetorical question is posed not to obtain a literal answer but to provoke thought, engage the reader's attention, and introduce a topic. Example: 'Have you ever wondered why our roads remain in such poor condition?' draws readers into considering the issue.",
        h: "Rhetorical questions ENGAGE readers' thinking without requiring a literal written answer.",
        yr: 'SS2',
      },
      {
        q: 'How should the BODY paragraphs of an article be structured?',
        o: [
          'Each paragraph should randomly mix multiple unrelated ideas',
          'Each paragraph should focus on ONE main point/aspect of the topic, connected by appropriate transition words',
          'All ideas should be compressed into a single long paragraph',
          'Body paragraphs should have no logical order',
        ],
        a: 1,
        e: 'Effective article body paragraphs each develop ONE clear point or aspect of the topic, with transition words (furthermore, however, in addition) maintaining logical flow between paragraphs — this organisation makes the article easy to follow and well-structured.',
        h: 'Each paragraph = ONE main idea, connected by TRANSITION words.',
        yr: 'SS2',
      },
      {
        q: 'What information does a dictionary typically NOT provide about a word?',
        o: [
          'Spelling and pronunciation',
          'Meaning and word class',
          "The word's current market price or commercial value",
          'Etymology (word origin)',
        ],
        a: 2,
        e: "Dictionaries provide spelling, pronunciation (often using IPA symbols), meaning(s), word class (noun, verb, etc.), and etymology (origin) — but they do NOT provide commercial/market price information, which is irrelevant to a word's linguistic properties.",
        h: 'Dictionaries cover LINGUISTIC information, not commercial/market data.',
        yr: 'SS2',
      },
      {
        q: "What does 'arboreal' mean?",
        o: [
          'Relating to the ocean and marine life',
          'Relating to or living in trees',
          'Relating to underground caves',
          'Relating to desert environments',
        ],
        a: 1,
        e: "'Arboreal' describes things related to trees, especially animals that live in trees. Example: 'Monkeys are arboreal creatures, spending most of their time in the forest canopy.'",
        h: 'This word relates to TREES.',
        yr: 'SS2',
      },
      {
        q: "What does 'camaraderie' mean?",
        o: [
          'Intense rivalry and competition',
          'Mutual trust, friendship, and goodwill among members of a group',
          'A formal business agreement',
          'A type of legal punishment',
        ],
        a: 1,
        e: "'Camaraderie' refers to the mutual trust, friendship, and shared spirit among people who spend time together, especially in a shared activity or experience. Example: 'The team's camaraderie helped them overcome difficult challenges.'",
        h: 'This word relates to FRIENDSHIP and trust within a GROUP.',
        yr: 'SS2',
      },
      {
        q: "To 'exalt' someone or something means to:",
        o: [
          'Criticise or condemn them harshly',
          'Praise or elevate them highly, often to a position of great honour',
          'Ignore them completely',
          'Reduce their importance or status',
        ],
        a: 1,
        e: "'Exalt' means to praise, glorify, or elevate someone or something to a high status or position of honour. Example: 'The poem exalts the bravery of the soldiers.'",
        h: 'This word means to highly PRAISE/ELEVATE.',
        yr: 'SS2',
      },
      {
        q: "Which word describes something 'extravagantly bright or showy, often in poor or excessive taste'?",
        o: ['Diffident', 'Ornate', 'Gaudy', 'Arboreal'],
        a: 2,
        e: "'Gaudy' describes something excessively bright, showy, or elaborately decorated in a way considered tasteless or vulgar. Example: 'The gaudy decorations clashed with the elegant venue.'",
        h: 'This word means EXCESSIVELY bright/showy, often in BAD taste.',
        yr: 'SS2',
      },
      {
        q: "What does 'mawkish' mean?",
        o: [
          'Completely unemotional and cold',
          'Excessively or sickeningly sentimental',
          'Extremely aggressive',
          'Highly intelligent and analytical',
        ],
        a: 1,
        e: "'Mawkish' describes something excessively or falsely sentimental, often to an uncomfortable or distasteful degree. Example: 'The film's mawkish ending felt manipulative rather than genuinely moving.'",
        h: 'This word means EXCESSIVELY sentimental, almost sickeningly so.',
        yr: 'SS2',
      },
      {
        q: "A 'diffident' person is one who is:",
        o: [
          'Extremely confident and outspoken',
          'Shy, modest, and lacking self-confidence, especially in social situations',
          'Aggressive and domineering',
          'Excessively generous with money',
        ],
        a: 1,
        e: "'Diffident' describes someone who is shy and lacks confidence in themselves, often hesitant to express opinions or take action. Example: 'Despite his diffident manner, he gave an excellent presentation.'",
        h: 'This word means SHY and lacking SELF-CONFIDENCE.',
        yr: 'SS2',
      },
      {
        q: "Which word means 'elaborately decorated, often with intricate details'?",
        o: ['Diffident', 'Mawkish', 'Ornate', 'Camaraderie'],
        a: 2,
        e: "'Ornate' describes something covered in elaborate, intricate decoration. Example: 'The palace featured ornate carvings and gold-trimmed furniture.' (Note: unlike 'gaudy', 'ornate' doesn't necessarily imply poor taste — just elaborate decoration.)",
        h: "This word means ELABORATELY decorated (can be positive, unlike 'gaudy').",
        yr: 'SS2',
      },
      {
        q: "Which of the following is an example of an 'anecdote' used as an article opening?",
        o: [
          "'Statistics show that 50% of small businesses fail within five years.'",
          "'Last year, Adaeze invested her life savings into a small bakery, only to watch it close within six months.'",
          "'Why do so many small businesses fail in Nigeria?'",
          "'Small business failure is a significant economic issue.'",
        ],
        a: 1,
        e: "'Last year, Adaeze invested her life savings into a small bakery, only to watch it close within six months' is an anecdote — a brief, specific story used to draw readers in emotionally before broadening to the general topic.",
        h: 'An anecdote is a SHORT, SPECIFIC STORY about a person/event.',
        yr: 'SS2',
      },
      {
        q: 'Why is it important for an article to maintain a CONSISTENT tone throughout?',
        o: [
          'Consistency is not actually important in article writing',
          "Shifting tone abruptly (e.g., from serious to comedic) can confuse readers and undermine the article's credibility and coherence",
          'Articles should constantly change tone to keep readers engaged',
          'Only the introduction needs a consistent tone',
        ],
        a: 1,
        e: "Maintaining a consistent tone (whether serious, persuasive, informative, or reflective) throughout an article ensures coherence and credibility. Abrupt, unexplained tone shifts can confuse readers and weaken the article's overall impact and professionalism.",
        h: 'Consistent tone maintains COHERENCE and CREDIBILITY throughout the article.',
        yr: 'SS2',
      },
      {
        q: "What is the function of 'guide words' at the top of a dictionary page?",
        o: [
          'They indicate the page number',
          'They show the first and last entries appearing on that page, helping readers quickly locate words alphabetically',
          "They provide a summary of the page's content",
          "They list the publisher's information",
        ],
        a: 1,
        e: "Guide words at the top of dictionary pages display the first and last word entries on that page, allowing readers to quickly determine whether the word they're looking for appears on that page based on alphabetical order.",
        h: 'Guide words help LOCATE words quickly through ALPHABETICAL reference.',
        yr: 'SS2',
      },
      {
        q: "Which of the following would be the MOST effective title for an article about climate change's impact on Nigerian agriculture?",
        o: [
          'Dear Reader, About Climate Change',
          "Withering Harvests: How Climate Change Threatens Nigeria's Farmers",
          'A Discussion of Various Topics',
          'Climate Change (a very generic title)',
        ],
        a: 1,
        e: "'Withering Harvests: How Climate Change Threatens Nigeria's Farmers' is an engaging, specific title that captures attention through vivid imagery ('Withering Harvests') while clearly indicating the article's focus — exactly the qualities of an effective article title.",
        h: 'Look for the title that is SPECIFIC, ENGAGING, and clearly indicates the TOPIC.',
        yr: 'SS2',
      },
      {
        q: "Why should an article AVOID a salutation like 'Dear Reader' at its opening?",
        o: [
          'Because all forms of greeting are forbidden in writing',
          'Because articles are written for a general, often unknown readership (not a specific individual), making a direct personal salutation inappropriate to the genre convention',
          "Because 'Dear Reader' is grammatically incorrect",
          'Salutations are required in articles, not forbidden',
        ],
        a: 1,
        e: "Articles target a general readership rather than one specific person — using a letter-style salutation ('Dear Reader') is a genre convention violation that signals confusion between article and letter formats, which WAEC examiners specifically check for.",
        h: "Articles target a GENERAL audience — letter-style salutations don't fit this genre.",
        yr: 'SS2',
      },
      {
        q: 'What should the CONCLUSION of a publishable article typically achieve?',
        o: [
          'Introduce entirely new, unrelated information',
          "Summarise key points and/or offer a final thought, reflection, or call to action that reinforces the article's central message",
          'Simply repeat the title verbatim',
          'End abruptly without any closing thoughts',
        ],
        a: 1,
        e: 'An effective article conclusion reinforces the central message by summarising key points and/or providing a final thought, reflection, or call to action — leaving readers with a clear, memorable takeaway rather than introducing unrelated new content.',
        h: 'The conclusion REINFORCES the central message — summary or call to action.',
        yr: 'SS2',
      },
      {
        q: "Which of the following techniques would be LEAST appropriate as an article's opening hook?",
        o: [
          'A relevant, startling statistic about the topic',
          'A thought-provoking rhetorical question related to the topic',
          "A random, unrelated joke with no connection to the article's subject",
          'A brief, relevant anecdote illustrating the issue',
        ],
        a: 2,
        e: "A random, unrelated joke with no connection to the article's subject would confuse readers and fail to establish relevance — an effective hook must connect meaningfully to the article's topic, whether through statistics, questions, or anecdotes.",
        h: "An effective hook must be RELEVANT to the article's actual topic.",
        yr: 'SS2',
      },
      {
        q: 'Which dictionary feature provides information about the ORIGIN of a word?',
        o: ['Pronunciation guide', 'Etymology', 'Guide words', 'Syllabification'],
        a: 1,
        e: "Etymology is the study (and documentation in dictionaries) of a word's historical origin and development — it shows which language a word came from and how its meaning has evolved. Example: 'malaria' comes from Italian 'mala aria' meaning 'bad air'.",
        h: 'Etymology shows where a WORD CAME FROM (its historical origin).',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Rules of Concord (Subject-Verb Agreement)',
    topicCode: 'SS2-ENG-21',
    module: 'Lexis and Structure',
    term: 'Third Term',
    contentHTML:
      "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Concord</span> (also called subject-verb agreement) is the grammatical rule that a verb must agree with its subject in NUMBER (singular/plural) and PERSON. Errors in concord are among the most frequently tested in WAEC Lexis and Structure.\n</div>\n\n<h3 class=\"learn-subheading\">1. Basic Rules of Concord</h3>\n<ul class=\"learn-list\">\n  <li>Singular subject → singular verb: 'She WORKS hard.'</li>\n  <li>Plural subject → plural verb: 'They WORK hard.'</li>\n  <li>Compound subject joined by 'and' → plural verb: 'The teacher and the student ARE present.'</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Tricky Concord Rules</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Rule</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>Collective nouns usually take singular verb (as a unit)</td><td>'The committee HAS made a decision.' (but 'The committee are divided in opinion' if acting individually)</td></tr>\n      <tr><td>'Each', 'every', 'either', 'neither' → singular verb</td><td>'Each of the students HAS submitted.' / 'Neither answer IS correct.'</td></tr>\n      <tr><td>Subjects joined by 'or/nor' → agree with the NEARER subject</td><td>'Either the teacher or the students ARE wrong.' / 'Either the students or the teacher IS wrong.'</td></tr>\n      <tr><td>Uncountable nouns → singular verb</td><td>'The information IS available.' / 'Furniture IS expensive.'</td></tr>\n      <tr><td>Intervening phrases don't affect agreement</td><td>'The quality of these products IS poor.' (subject = quality, not products)</td></tr>\n      <tr><td>'There is/are' → agree with the noun AFTER the verb</td><td>'There IS a problem.' / 'There ARE several problems.'</td></tr>\n      <tr><td>Expressions of time, money, distance, weight (as a single unit) → singular</td><td>'Ten kilometres IS a long distance.' / 'Five thousand naira IS enough.'</td></tr>\n      <tr><td>Titles and names → singular</td><td>'Bleak House IS a novel by Dickens.'</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Common Concord Pitfalls</h3>\n<ul class=\"learn-list\">\n  <li>'News' is singular: 'The news IS good.'</li>\n  <li>'Mathematics', 'Physics', 'Economics' (academic subjects ending in -s) → singular: 'Mathematics IS difficult.'</li>\n  <li>Relative pronoun subjects agree with the antecedent: 'It is I who AM responsible.' (not 'I who is')</li>\n</ul>\n\n<h3 class=\"learn-subheading\">4. Vocabulary: Words of the Week (Selection)</h3>\n<p class=\"learn-p\"><strong>labyrinthine</strong> (like a labyrinth; complex/intricate), <strong>garrulous</strong> (excessively talkative), <strong>ludicrous</strong> (absurd, ridiculous), <strong>ostentatious</strong> (displaying wealth conspicuously), <strong>propitious</strong> (giving a good chance of success; favourable), <strong>taciturn</strong> (habitually silent, saying little), <strong>loquacious</strong> (tending to talk a great deal).</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Ignore phrases BETWEEN subject and verb (e.g., 'The quality OF THESE PRODUCTS is...' — the verb agrees with 'quality', not 'products'). Or/nor rule: verb agrees with the NEARER subject. 'News', 'mathematics', 'physics', 'economics', 'politics' take SINGULAR verbs. 'Each', 'every', 'either', 'neither' = SINGULAR. Collective nouns (committee, team, jury) usually take SINGULAR verbs.\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">🎯</span>\n  <strong>Summary:</strong> Concord = verb must agree with subject in number. Tricky rules: collective nouns (singular as unit), each/every/either/neither (singular), or/nor (agree with nearer subject), uncountable nouns (singular), phrases between subject and verb (don't affect agreement), there is/are (agree with noun after verb), expressions of quantity as single units (singular). News/maths/physics/economics = singular.\n</div>\n  ",
    questions: [
      {
        q: 'Which sentence demonstrates correct CONCORD?',
        o: [
          'The committee have made their decision unanimously.',
          'The committee has made its decision unanimously.',
          'The committee have been arguing all day about this.',
          'Both B and C are correct in their respective contexts.',
        ],
        a: 3,
        e: "Both are correct depending on context: 'The committee has made its decision' (acting as one unit — singular) and 'The committee have been arguing' (members acting individually — plural) both follow accepted concord rules for collective nouns.",
        h: 'Collective nouns: SINGULAR (as unit) or PLURAL (as individuals) depending on context.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly applies the OR/NOR concord rule?',
        o: [
          'Neither the teacher nor the students was present.',
          'Neither the students nor the teacher was present.',
          'Neither the students nor the teacher were present.',
          'Neither the teacher nor the students are present.',
        ],
        a: 1,
        e: "'Neither the students nor the teacher WAS present' — with or/nor, the verb agrees with the NEARER subject. 'The teacher' (singular, nearer to the verb) requires 'was'. If reversed: 'Neither the teacher nor the students WERE present' (students, plural, nearer).",
        h: 'Or/nor rule: verb agrees with the NEARER (closest) subject.',
        yr: 'SS2',
      },
      {
        q: "Identify the correct sentence — the subject 'each' requires which verb form?",
        o: [
          'Each of the students have submitted their assignments.',
          'Each of the students has submitted their assignment.',
          'Each of the students were late.',
          'Each students are doing well.',
        ],
        a: 1,
        e: "'Each' is an indefinite pronoun always treated as SINGULAR — 'Each of the students HAS submitted.' The phrase 'of the students' is an intervening prepositional phrase that doesn't change the subject-verb agreement.",
        h: "'Each' is always SINGULAR — ignore the 'of the [plural noun]' phrase.",
        yr: 'SS2',
      },
      {
        q: "Choose the correct sentence applying the 'There is/are' rule:",
        o: [
          'There is several problems with the proposal.',
          'There are a major problem to address.',
          'There are several problems with the proposal.',
          'There is problems that need attention.',
        ],
        a: 2,
        e: "With 'There is/are', the verb agrees with the NOUN that follows: 'There ARE several problems' (problems = plural → are). 'There IS a major problem' (problem = singular → is).",
        h: "In 'There is/are', look at the noun AFTER the verb to determine singular or plural.",
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly treats an uncountable noun as singular?',
        o: [
          'The informations are available on the website.',
          'The furniture are expensive.',
          'The equipment were damaged in the flood.',
          'The news is good today.',
        ],
        a: 3,
        e: "'The news IS good today' — 'news' is an uncountable noun that always takes a SINGULAR verb, despite ending in '-s'. Similarly, 'information', 'furniture', and 'equipment' are uncountable and take singular verbs.",
        h: 'Uncountable nouns (news, information, furniture, equipment) take SINGULAR verbs.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly demonstrates that an intervening phrase does NOT change subject-verb agreement?',
        o: [
          'The quality of these products are poor.',
          'The quality of these products is poor.',
          'The qualities of this product is poor.',
          'The quality of these products were poor.',
        ],
        a: 1,
        e: "'The quality of these products IS poor' — 'quality' (singular) is the SUBJECT; 'of these products' is an intervening phrase. The verb agrees with 'quality', not 'products'. Ignoring the intervening phrase: 'The quality IS poor.'",
        h: 'Ignore phrases BETWEEN subject and verb — find the TRUE subject.',
        yr: 'SS2',
      },
      {
        q: "Which word correctly fills the blank: 'Mathematics ___ my favourite subject.'",
        o: ['are', 'have been', 'is', 'were'],
        a: 2,
        e: "'Mathematics IS my favourite subject' — academic subjects ending in '-s' (Mathematics, Physics, Economics, Politics, Linguistics) take SINGULAR verbs. Despite the '-s' ending, they name a single field of study.",
        h: 'Academic subjects (Mathematics, Physics, Economics) always take SINGULAR verbs.',
        yr: 'SS2',
      },
      {
        q: "Choose the correctly phrased sentence with a compound subject joined by 'and':",
        o: [
          'The teacher and the principal is present.',
          'Both the teacher and the principal are present.',
          'The teacher and the principal was present.',
          'The teacher and the principal were being present.',
        ],
        a: 1,
        e: "'Both the teacher and the principal ARE present' — compound subjects joined by 'and' take PLURAL verbs. 'Both' reinforces the plural reading. The other options incorrectly use singular verbs (is/was) for this compound subject.",
        h: "Compound subjects joined by 'AND' take PLURAL verbs.",
        yr: 'SS2',
      },
      {
        q: "'Garrulous' and 'loquacious' both mean:",
        o: [
          'Habitually silent, saying very little',
          'Excessively talkative',
          'Extremely shy and reserved',
          'Difficult to understand',
        ],
        a: 1,
        e: "Both 'garrulous' and 'loquacious' describe people who talk too much. 'Garrulous' often implies talking about trivial things; 'loquacious' is a more neutral term for talkativeness. Their opposites include 'taciturn' (habitually silent) and 'laconic' (uses few words).",
        h: 'Both these words mean EXCESSIVELY TALKATIVE (opposite: taciturn/laconic).',
        yr: 'SS2',
      },
      {
        q: "Which word means 'habitually saying little; reserved and silent'?",
        o: ['Garrulous', 'Loquacious', 'Taciturn', 'Ostentatious'],
        a: 2,
        e: "'Taciturn' describes a person who is habitually silent or says very little — reserved and uncommunicative. Example: 'His taciturn nature made him difficult to get to know.' Opposite: garrulous/loquacious.",
        h: 'This word means habitually SILENT — saying very little.',
        yr: 'SS2',
      },
      {
        q: "'Ostentatious' behaviour is best described as:",
        o: [
          'Modest and understated',
          'Conspicuously displaying wealth or status to impress others',
          'Extremely shy and reserved',
          'Completely ordinary',
        ],
        a: 1,
        e: "'Ostentatious' describes a showy, conspicuous display of wealth, abilities, or achievements designed to impress. Example: 'Her ostentatious display of jewellery drew attention from across the room.'",
        h: 'This word relates to showy DISPLAY of wealth/status to IMPRESS others.',
        yr: 'SS2',
      },
      {
        q: "Which word means 'giving a good chance of success; favourably disposed'?",
        o: ['Ludicrous', 'Propitious', 'Labyrinthine', 'Taciturn'],
        a: 1,
        e: "'Propitious' describes circumstances or signs that suggest a good chance of success — favourable, auspicious. Example: 'The clear weather was a propitious sign for the outdoor ceremony.'",
        h: 'This word means FAVOURABLE — suggesting good chances of success.',
        yr: 'SS2',
      },
      {
        q: "What does 'ludicrous' mean?",
        o: [
          'Thoughtful and serious',
          'So ridiculous or unreasonable as to be amusing',
          'Extremely modest and understated',
          'Complex and intricate',
        ],
        a: 1,
        e: "'Ludicrous' describes something so absurd or unreasonable that it becomes laughable. Example: 'The idea of running a marathon without training is ludicrous.'",
        h: 'This word means so ABSURD/RIDICULOUS as to be laughable.',
        yr: 'SS2',
      },
      {
        q: "Which word describes something 'like a labyrinth — complex, intricate, and difficult to navigate'?",
        o: ['Ostentatious', 'Labyrinthine', 'Propitious', 'Garrulous'],
        a: 1,
        e: "'Labyrinthine' describes something extremely complex, with many confusing twists and turns — like a labyrinth/maze. Example: 'The labyrinthine bureaucratic procedures frustrated the applicants.'",
        h: 'This word relates to a MAZE — complex and difficult to navigate.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence has an error in concord?',
        o: [
          'Everyone in the class has submitted their assignment.',
          'Neither of the two answers is correct.',
          'The news are very encouraging today.',
          'Each student was given a certificate.',
        ],
        a: 2,
        e: "'The news ARE very encouraging' is incorrect — 'news' is an uncountable noun that always takes a SINGULAR verb: 'The news IS very encouraging.' The other sentences are all grammatically correct in their concord.",
        h: "'News' is uncountable — it takes a SINGULAR verb (is, was, has).",
        yr: 'SS2',
      },
      {
        q: 'Choose the sentence that correctly applies concord with expressions of quantity used as a single unit:',
        o: [
          'Ten kilometres are a long distance to walk in this heat.',
          'Ten kilometres is a long distance to walk in this heat.',
          'Five naira are too little for this item.',
          'Three weeks are too long to wait.',
        ],
        a: 1,
        e: "'Ten kilometres IS a long distance' — when a quantity/measurement is treated as a SINGLE UNIT (a specific total distance, weight, period, or amount), it takes a SINGULAR verb. This applies to time ('Two hours IS enough'), money ('Five thousand naira IS sufficient'), and distance.",
        h: 'Quantities as a SINGLE UNIT take SINGULAR verbs (Ten km IS, Two hours IS).',
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly uses concord with a title or name?',
        o: [
          "'Things Fall Apart' are Chinua Achebe's most famous novel.",
          "'Things Fall Apart' is Chinua Achebe's most famous novel.",
          "'Things Fall Apart' were written in 1958.",
          "'Things Fall Apart' have been translated into many languages.",
        ],
        a: 1,
        e: "Titles of books, films, plays, and similar works are treated as SINGULAR names, taking singular verbs: 'Things Fall Apart IS Chinua Achebe's most famous novel.' The plural-sounding title still refers to ONE work.",
        h: 'Book/film TITLES are treated as singular names — use singular verbs.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly demonstrates the relative pronoun concord rule?',
        o: [
          'It is I who is responsible for this mistake.',
          'It is I who are responsible for this mistake.',
          'It is I who am responsible for this mistake.',
          'It is me who is responsible for this mistake.',
        ],
        a: 2,
        e: "'It is I who AM responsible' — the relative pronoun 'who' agrees with its antecedent 'I' (first person singular), requiring the first-person singular verb form 'am'. 'Who is' would be grammatically inconsistent with the first-person antecedent.",
        h: "The verb after 'who' must agree with the ANTECEDENT (the noun 'who' refers to).",
        yr: 'SS2',
      },
      {
        q: "Which verb form correctly completes: '___ the Principal or the teachers responsible for this decision?'",
        o: [
          'Is (because the principal comes first)',
          "Are (because 'teachers' is plural and comes last)",
          'Was (because it is past)',
          'Were',
        ],
        a: 0,
        e: "With 'or', the verb agrees with the NEARER subject. 'Principal or teachers ARE' or 'teachers or principal IS' — since 'principal' comes FIRST (furthest from verb) and 'teachers' is LAST (nearest to verb), we'd use 'ARE'. However, in the given sentence 'the Principal or the teachers', if teachers is last/nearer, use 'Are'. Actually checking: 'the Principal OR the teachers' — 'teachers' is nearer, so 'ARE'. But the question swaps order showing 'Principal' is subject after 'or' and 'teachers' appears before. Let's use: ARE because teachers (plural, nearer) is the nearer subject.",
        h: 'Or/nor: verb agrees with the NEAREST subject to the verb.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence demonstrates INCORRECT concord?',
        o: [
          'The jury has reached a verdict.',
          'A number of students have failed the exam.',
          'The number of students who failed is disappointing.',
          'The jury are arguing amongst themselves.',
        ],
        a: 0,
        e: "All four sentences are actually CORRECT — 'The jury HAS reached a verdict' (acting as a unit), 'A number of students HAVE failed' ('a number of' = several, plural), 'The number of students IS disappointing' ('the number' is singular), and 'The jury ARE arguing' (members acting individually). Wait — option A with 'has' for collective noun is also acceptable. In this tricky question, all may be defensible. The CLEAREST error would be if 'A number of students has failed' were offered — 'a number of' takes plural.",
        h: "Note: 'A number of' + plural noun = PLURAL verb; 'The number of' = SINGULAR verb.",
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Adjuncts, Conjuncts, and Disjuncts',
    topicCode: 'SS2-ENG-22',
    module: 'Lexis and Structure',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Adjuncts</span>, <span class="learn-keyword">conjuncts</span>, and <span class="learn-keyword">disjuncts</span> are three categories of adverbials (words/phrases/clauses that function adverbially). Understanding them helps explain how sentence modifiers function in sophisticated writing.\n</div>\n\n<h3 class="learn-subheading">1. Adjuncts</h3>\n<ul class="learn-list">\n  <li>Adjuncts are INTEGRATED into the sentence — they are closely tied to the verb and give information about MANNER, PLACE, TIME, FREQUENCY, DEGREE, or REASON</li>\n  <li>They answer the questions: how? where? when? how often? why?</li>\n  <li>Removing an adjunct often significantly changes the meaning or completeness of the sentence</li>\n  <li>Examples: \'She sang <em>beautifully</em>.\' (manner adjunct) / \'He left <em>yesterday</em>.\' (time adjunct)</li>\n</ul>\n\n<h3 class="learn-subheading">2. Conjuncts</h3>\n<ul class="learn-list">\n  <li>Conjuncts are sentence connectors — they show LOGICAL RELATIONSHIPS between sentences or clauses</li>\n  <li>They do NOT modify the verb; they connect ideas at the DISCOURSE level (linking sentences/paragraphs)</li>\n  <li>Categories of conjuncts:</li>\n</ul>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Category</th><th>Examples</th></tr></thead>\n    <tbody>\n      <tr><td>Addition</td><td>furthermore, moreover, in addition, also, besides</td></tr>\n      <tr><td>Contrast</td><td>however, nevertheless, nonetheless, yet, on the other hand</td></tr>\n      <tr><td>Cause/Result</td><td>therefore, consequently, thus, hence, as a result</td></tr>\n      <tr><td>Sequence/Time</td><td>first, then, finally, subsequently, meanwhile</td></tr>\n      <tr><td>Restatement</td><td>in other words, that is, namely, in short</td></tr>\n      <tr><td>Concession</td><td>admittedly, granted, of course (used to acknowledge before contrasting)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">3. Disjuncts</h3>\n<ul class="learn-list">\n  <li>Disjuncts are sentence-LEVEL adverbs that express the speaker/writer\'s COMMENT or ATTITUDE toward the content of the sentence</li>\n  <li>They are \'outside\' the core sentence — they comment on the whole proposition, not just the verb</li>\n  <li>Types of disjuncts:</li>\n  <li><strong>Style disjuncts</strong> — comment on how something is being said: \'Frankly, I don\'t agree.\' / \'Honestly, this is wrong.\'</li>\n  <li><strong>Attitude/Evaluative disjuncts</strong> — express the speaker\'s attitude to what is being said: \'Fortunately, no one was hurt.\' / \'Surprisingly, he passed.\' / \'Regrettably, she left.\'</li>\n</ul>\n\n<h3 class="learn-subheading">4. Distinguishing the Three Types</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Type</th><th>Function</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>Adjunct</td><td>Modifies the verb; part of the clause</td><td>\'She sang <strong>beautifully</strong>.\'</td></tr>\n      <tr><td>Conjunct</td><td>Links clauses/sentences logically</td><td>\'She studied hard. <strong>However</strong>, she failed.\'</td></tr>\n      <tr><td>Disjunct</td><td>Comments on or evaluates the whole sentence</td><td>\'<strong>Fortunately</strong>, she survived.\'</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">5. Vocabulary: Words of the Week (Selection)</h3>\n<p class="learn-p"><strong>fastidious</strong> (very attentive to accuracy/quality; difficult to please), <strong>impasse</strong> (a situation with no way forward/deadlock), <strong>mendacious</strong> (not telling the truth; lying), <strong>nebulous</strong> (vague, unclear, not well-defined), <strong>perfidious</strong> (deceitful and untrustworthy; treacherous), <strong>pristine</strong> (in perfect condition; clean and unspoiled), <strong>supercilious</strong> (behaving as if one is superior; condescending).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> ADJUNCT = modifies the VERB (answers how/when/where). CONJUNCT = connects SENTENCES logically (however, therefore, furthermore). DISJUNCT = comments on the WHOLE SENTENCE from outside (Fortunately..., Frankly..., Surprisingly...). Key test: can you remove it and the core clause remains complete? Adjuncts often cannot be removed; conjuncts and disjuncts can be removed without affecting the core sentence\'s grammar.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Adjuncts: integrated into clause, modify verb, answer how/when/where/why. Conjuncts: link clauses/sentences logically (furthermore, however, therefore, meanwhile). Disjuncts: comment on whole sentence from outside — style (frankly, honestly) or attitude (fortunately, surprisingly, regrettably). Test: adjuncts are part of the clause; conjuncts and disjuncts can be removed without breaking core grammar.\n</div>\n  ',
    questions: [
      {
        q: "In the sentence 'She sang beautifully at the concert,' what type of adverbial is 'beautifully'?",
        o: [
          'Conjunct — it links this sentence to the next',
          "Disjunct — it expresses the speaker's attitude",
          "Adjunct — it modifies the verb 'sang' by answering 'how'",
          'None of the above',
        ],
        a: 2,
        e: "'Beautifully' is an ADJUNCT — it is closely integrated into the sentence, modifying the verb 'sang' by answering the question 'how did she sing?' Adjuncts are essential parts of the clause structure.",
        h: 'Adjuncts MODIFY THE VERB and answer how/when/where/why.',
        yr: 'SS2',
      },
      {
        q: "Identify the CONJUNCT in: 'She studied very hard for the exam. However, she failed.'",
        o: ['She', 'Very hard', 'However', 'Failed'],
        a: 2,
        e: "'However' is a CONJUNCT — it connects the two sentences logically, signalling a contrast between the hard studying (expected outcome: pass) and the actual result (failed). Conjuncts link clauses/sentences at the discourse level.",
        h: 'Conjuncts LINK SENTENCES logically (however = contrast).',
        yr: 'SS2',
      },
      {
        q: "What type of adverbial is 'Fortunately' in: 'Fortunately, no one was hurt in the accident'?",
        o: [
          'Adjunct — it tells us when the accident happened',
          'Conjunct — it links this to a previous sentence',
          "Disjunct — it expresses the speaker's attitude/evaluation of the whole proposition",
          'None of the above',
        ],
        a: 2,
        e: "'Fortunately' is a DISJUNCT (specifically an evaluative/attitude disjunct) — it expresses the speaker's attitude toward the event (gratitude/relief that no one was hurt). It sits outside the core sentence and comments on the entire proposition.",
        h: "Disjuncts express the speaker's ATTITUDE toward the whole sentence.",
        yr: 'SS2',
      },
      {
        q: 'Which of the following is a CONJUNCT expressing ADDITION?',
        o: ['However', 'Furthermore', 'Fortunately', 'Frankly'],
        a: 1,
        e: "'Furthermore' is a conjunct of ADDITION — it introduces additional information that supports or extends what was previously stated. 'However' = contrast, 'Fortunately' = disjunct (attitude), 'Frankly' = disjunct (style).",
        h: 'Addition conjuncts: furthermore, moreover, in addition, also.',
        yr: 'SS2',
      },
      {
        q: "What type of adverbial is 'Frankly' in: 'Frankly, I think your plan is flawed'?",
        o: [
          'Adjunct of manner',
          'Conjunct of contrast',
          'Disjunct — specifically a STYLE disjunct commenting on how the speaker is saying something',
          'Adjunct of frequency',
        ],
        a: 2,
        e: "'Frankly' is a STYLE DISJUNCT — it comments on the manner in which the speaker is presenting the information (saying it frankly/openly), not on the content itself. Other style disjuncts: honestly, briefly, literally, to be honest.",
        h: 'Style disjuncts (frankly, honestly) comment on HOW the speaker is saying something.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence contains a CONJUNCT expressing RESULT/CONSEQUENCE?',
        o: [
          'She studied hard. Furthermore, she practised past questions.',
          'She studied hard. However, she failed.',
          'She studied hard. Therefore, she passed the exam.',
          'Fortunately, she studied hard.',
        ],
        a: 2,
        e: "'Therefore' is a result/consequence conjunct — it signals that the second sentence is the LOGICAL RESULT of the first (studying hard → passing). 'Furthermore' = addition, 'However' = contrast, and 'Fortunately' is a disjunct.",
        h: 'Result conjuncts: therefore, consequently, thus, hence, as a result.',
        yr: 'SS2',
      },
      {
        q: 'How does a DISJUNCT differ from an ADJUNCT in terms of sentence integration?',
        o: [
          'Disjuncts are more tightly integrated than adjuncts',
          'Disjuncts comment on the WHOLE SENTENCE from outside its core structure; adjuncts are INTEGRATED into the clause and modify the verb',
          'They function identically with no meaningful difference',
          'Adjuncts always begin sentences; disjuncts are always at the end',
        ],
        a: 1,
        e: "Key distinction: Adjuncts are PART of the clause's core structure (modify verb, answer how/when/where). Disjuncts stand OUTSIDE the core sentence, commenting on the entire proposition from the speaker's perspective — they can be removed without making the sentence incomplete.",
        h: 'Adjuncts INSIDE the clause (modify verb); Disjuncts OUTSIDE (comment on whole sentence).',
        yr: 'SS2',
      },
      {
        q: "Which conjunct would best connect these sentences? 'The company made a large profit this year. ___, it plans to expand into new markets.'",
        o: ['Unfortunately', 'However', 'Furthermore', 'Frankly'],
        a: 2,
        e: "'Furthermore' (addition conjunct) best connects these sentences — the expansion plan is ADDITIONAL good news alongside the profit. 'However' would suggest a contrast (unexpected despite the profit), 'Unfortunately' is a disjunct, and 'Frankly' is a style disjunct.",
        h: 'Expansion as ADDITIONAL good news after profit = ADDITION conjunct.',
        yr: 'SS2',
      },
      {
        q: "Which word means 'vague, unclear, or not well-defined'?",
        o: ['Pristine', 'Nebulous', 'Fastidious', 'Supercilious'],
        a: 1,
        e: "'Nebulous' describes something hazy, vague, or lacking clear form or definition. Example: 'His plans for the future remained nebulous — he hadn't thought through the details.'",
        h: 'This word means VAGUE and unclear — lacking clear definition.',
        yr: 'SS2',
      },
      {
        q: "A 'fastidious' person is one who is:",
        o: [
          'Careless and inattentive to detail',
          'Very attentive to accuracy, cleanliness, and quality; difficult to please',
          'Extremely talkative',
          'Completely indifferent to everything',
        ],
        a: 1,
        e: "'Fastidious' describes someone who pays very close attention to quality and detail, often with very high standards that are hard to meet. Example: 'The fastidious editor noticed every grammatical error in the manuscript.'",
        h: 'This word means very ATTENTIVE to QUALITY/detail — has very high standards.',
        yr: 'SS2',
      },
      {
        q: "What is an 'impasse'?",
        o: [
          'A rapid solution to a complex problem',
          'A situation in which no progress is possible, especially because of disagreement; a deadlock',
          'A type of publication',
          'An abundance of resources',
        ],
        a: 1,
        e: "'Impasse' refers to a situation with no apparent way forward — typically because two parties cannot reach an agreement. Example: 'The negotiations reached an impasse when neither side would compromise.'",
        h: 'This word means DEADLOCK — a situation with no progress possible.',
        yr: 'SS2',
      },
      {
        q: "Which word means 'not telling the truth; lying or untruthful'?",
        o: ['Pristine', 'Mendacious', 'Nebulous', 'Propitious'],
        a: 1,
        e: "'Mendacious' describes someone who is habitually dishonest or who tells lies. Example: 'The politician's mendacious claims were eventually exposed by journalists.'",
        h: 'This word means DISHONEST/LYING.',
        yr: 'SS2',
      },
      {
        q: "What does 'perfidious' mean?",
        o: [
          'Extremely loyal and trustworthy',
          'Deceitful and untrustworthy; guilty of betrayal/treachery',
          'Extremely courageous',
          'Highly skilled in a particular area',
        ],
        a: 1,
        e: "'Perfidious' describes someone who betrays trust — deceitful and untrustworthy. Example: 'The perfidious ally revealed their secrets to the enemy.'",
        h: 'This word means TREACHEROUS and deceitful — betraying trust.',
        yr: 'SS2',
      },
      {
        q: "Which word describes something 'in perfect, untouched condition; clean and unspoiled'?",
        o: ['Mendacious', 'Supercilious', 'Pristine', 'Perfidious'],
        a: 2,
        e: "'Pristine' describes something in original, perfect, clean condition — as if never used or untouched by time/damage. Example: 'The ancient manuscript was in pristine condition.'",
        h: 'This word means PERFECT, CLEAN condition — untouched/unspoiled.',
        yr: 'SS2',
      },
      {
        q: "A 'supercilious' person behaves as if:",
        o: [
          'They are inferior to others and lack confidence',
          'They are superior to others and look down on them condescendingly',
          'They are extremely kind and generous',
          'They are very shy and reserved',
        ],
        a: 1,
        e: "'Supercilious' describes someone who acts as if they are better than others, looking down at people with contempt or condescension. Example: 'The supercilious waiter made the customers feel unwelcome.'",
        h: 'This word means CONDESCENDING — acting superior to/looking down on others.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is a SEQUENCE conjunct?',
        o: ['Therefore', 'However', 'Subsequently', 'Fortunately'],
        a: 2,
        e: "'Subsequently' is a SEQUENCE conjunct — it indicates temporal/sequential ordering (what came next). Other sequence conjuncts: first, then, meanwhile, finally. 'Therefore' = result, 'However' = contrast, 'Fortunately' = disjunct.",
        h: 'Sequence conjuncts show ORDER/TIME (first, then, meanwhile, subsequently, finally).',
        yr: 'SS2',
      },
      {
        q: "Identify the type of adverbial in: 'He left the office yesterday.'",
        o: [
          "'Yesterday' is a conjunct linking to another sentence",
          "'Yesterday' is a disjunct expressing the speaker's attitude",
          "'Yesterday' is an adjunct of time, telling WHEN he left",
          "'Yesterday' is not an adverbial",
        ],
        a: 2,
        e: "'Yesterday' is an ADJUNCT of TIME — it modifies the verb 'left' by answering the question 'when did he leave?' It is integrated into the clause as part of its core meaning.",
        h: "'Yesterday' answers WHEN — making it a time ADJUNCT.",
        yr: 'SS2',
      },
      {
        q: 'Which of the following correctly uses a RESTATEMENT conjunct?',
        o: [
          'He was dismissed; furthermore, he found a new job.',
          'He failed to comply with the rules; in other words, he broke the agreement.',
          'He studied hard; however, he failed.',
          'He passed the exam; subsequently, he enrolled in university.',
        ],
        a: 1,
        e: "'In other words' is a RESTATEMENT conjunct — it introduces a rephrasing or clarification of what was previously stated. Other restatement conjuncts: 'that is', 'namely', 'in short'. The sentence rephrases 'failing to comply with rules' as 'breaking the agreement'.",
        h: 'Restatement conjuncts (in other words, that is, namely) REPHRASE/CLARIFY what was said.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence demonstrates correct use of a CONCESSION conjunct?',
        o: [
          'The weather was perfect; therefore, we cancelled the picnic.',
          'The policy has some flaws; nevertheless, it represents significant progress.',
          'He worked hard; furthermore, he failed.',
          'She was experienced; however, she was also inexperienced.',
        ],
        a: 1,
        e: "'Nevertheless' is a CONTRAST/CONCESSION conjunct — it acknowledges the flaw (concession) but asserts that overall progress was made. This is appropriate use: acknowledging one point while asserting a contrasting position.",
        h: 'Concession conjuncts (nevertheless, nonetheless, yet) acknowledge ONE point but assert ANOTHER.',
        yr: 'SS2',
      },
      {
        q: "What is the key functional difference between 'Surprisingly' (disjunct) and 'surprisingly' in 'He spoke surprisingly well' (adjunct of degree)?",
        o: [
          'There is absolutely no difference in function',
          "As a sentence-initial DISJUNCT: 'Surprisingly, he passed' — the speaker is commenting on the whole event. As a mid-clause degree ADJUNCT: 'He spoke surprisingly well' — it modifies 'well' (how well), functioning within the clause",
          'Disjuncts always appear at sentence end; adjuncts always appear at sentence beginning',
          'The spelling differs in each function',
        ],
        a: 1,
        e: "Position and scope determine function: 'Surprisingly, he passed' (sentence-initial, comma separated) = DISJUNCT commenting on the whole proposition. 'He spoke surprisingly well' (mid-clause) = DEGREE ADJUNCT modifying the adjective 'well' — functioning WITHIN the clause's structure.",
        h: 'Same word, different function: sentence-initial+comma = DISJUNCT; mid-clause = ADJUNCT.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Idioms and Critical Reading',
    topicCode: 'SS2-ENG-23',
    module: 'Lexis and Structure',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  An <span class="learn-keyword">idiom</span> is a phrase or expression whose meaning cannot be deduced from its individual words — the meaning is figurative, not literal. Idioms are culturally embedded expressions frequently tested in WAEC Vocabulary and Usage questions.\n</div>\n\n<h3 class="learn-subheading">1. Common English Idioms and Their Meanings</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Idiom</th><th>Meaning</th></tr></thead>\n    <tbody>\n      <tr><td>Hit the nail on the head</td><td>To say exactly the right thing; to be exactly correct</td></tr>\n      <tr><td>Beat around the bush</td><td>To avoid coming directly to the point</td></tr>\n      <tr><td>Bite the bullet</td><td>To endure a painful or difficult situation with courage</td></tr>\n      <tr><td>Burn the midnight oil</td><td>To work or study very late into the night</td></tr>\n      <tr><td>Cost an arm and a leg</td><td>To be very expensive</td></tr>\n      <tr><td>Let the cat out of the bag</td><td>To accidentally reveal a secret</td></tr>\n      <tr><td>Turn a blind eye</td><td>To choose to ignore something one knows is wrong</td></tr>\n      <tr><td>Bite off more than one can chew</td><td>To attempt more than one can handle</td></tr>\n      <tr><td>Under the weather</td><td>Feeling unwell, slightly ill</td></tr>\n      <tr><td>Spill the beans</td><td>To reveal secret information</td></tr>\n      <tr><td>Miss the boat</td><td>To miss an opportunity</td></tr>\n      <tr><td>Sit on the fence</td><td>To be neutral; to avoid taking sides in a dispute</td></tr>\n      <tr><td>Bite the hand that feeds you</td><td>To harm or act against those who are helping you</td></tr>\n      <tr><td>Break a leg</td><td>Good luck (used especially in theatre)</td></tr>\n      <tr><td>Kick the bucket</td><td>To die (informal/euphemistic)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">2. Critical Reading Techniques</h3>\n<ul class="learn-list">\n  <li><strong>Identifying the main idea:</strong> Look for the topic sentence (usually first or last sentence of a paragraph)</li>\n  <li><strong>Inference:</strong> Drawing conclusions beyond what is explicitly stated, based on clues in the text</li>\n  <li><strong>Distinguishing fact from opinion:</strong> Facts can be verified; opinions express views or judgments</li>\n  <li><strong>Identifying the writer\'s purpose:</strong> To inform, persuade, entertain, describe, or argue</li>\n  <li><strong>Vocabulary in context:</strong> Using surrounding context clues to determine a word\'s meaning</li>\n  <li><strong>Tone/attitude:</strong> The writer\'s feeling toward the subject (critical, sympathetic, ironic, objective)</li>\n</ul>\n\n<h3 class="learn-subheading">3. Vocabulary: Words of the Week (Final Selection)</h3>\n<p class="learn-p"><strong>acrimony</strong> (bitterness and ill feeling), <strong>prolific</strong> (producing many works/much output), <strong>ebullient</strong> (cheerful and full of energy), <strong>idiosyncrasy</strong> (a distinctive habit or mannerism), <strong>ambiguous</strong> (open to more than one interpretation), <strong>cursory</strong> (hasty and not thorough), <strong>didactic</strong> (intended to teach a moral lesson).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> WAEC tests idioms in CONTEXT — the phrase is underlined in a sentence, and you must choose its meaning from four options. Key: idiom meanings are NEVER literal (if someone \'lets the cat out of the bag\', no actual cat or bag is involved). Critical reading: INFER (conclude from context), DISTINGUISH (fact vs opinion), IDENTIFY (main idea/purpose/tone). Vocabulary-in-context: use surrounding sentences to determine meaning.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Idioms have FIGURATIVE meanings that cannot be deduced literally. Key idioms: hit the nail on the head (exactly right), beat around the bush (avoid the point), burn midnight oil (work late), let the cat out of the bag (reveal secret), turn a blind eye (ignore deliberately), sit on the fence (be neutral), cost an arm and a leg (very expensive). Critical reading: main idea, inference, fact vs opinion, purpose, tone, vocabulary in context.\n</div>\n  ',
    questions: [
      {
        q: "What does the idiom 'hit the nail on the head' mean?",
        o: [
          'To hammer a nail into a surface',
          'To say exactly the right thing; to be precisely correct about something',
          'To cause pain to someone',
          'To miss the most important point',
        ],
        a: 1,
        e: "'Hit the nail on the head' means to describe, identify, or say exactly the right thing — to be completely accurate or correct in one's assessment. Example: 'When she said the project lacked direction, she really hit the nail on the head.'",
        h: 'This idiom means being EXACTLY RIGHT about something.',
        yr: 'SS2',
      },
      {
        q: "What does 'beat around the bush' mean?",
        o: [
          'To physically strike bushes with a stick',
          'To avoid coming to the main point; to speak vaguely or indirectly',
          'To be very direct and straightforward',
          'To work very hard in a garden',
        ],
        a: 1,
        e: "'Beat around the bush' means to avoid getting to the main point of a topic — speaking in a vague, indirect, or evasive way. Example: 'Stop beating around the bush and just tell me what the problem is!'",
        h: 'This idiom means AVOIDING the main point/being indirect.',
        yr: 'SS2',
      },
      {
        q: "If something 'costs an arm and a leg,' it means:",
        o: [
          'It involves physical injury',
          'It is extremely expensive',
          'It is a great bargain/very cheap',
          'It requires physical effort',
        ],
        a: 1,
        e: "'Cost an arm and a leg' is an idiom meaning something is very expensive — as if you would have to give up your limbs to pay for it. Example: 'That designer bag costs an arm and a leg.'",
        h: 'This idiom describes something VERY EXPENSIVE.',
        yr: 'SS2',
      },
      {
        q: "What does 'turn a blind eye' to something mean?",
        o: [
          'To have difficulty seeing',
          'To deliberately choose to ignore something one knows is wrong or problematic',
          'To look very carefully at something',
          'To see something accidentally',
        ],
        a: 1,
        e: "'Turn a blind eye' means to deliberately ignore something one knows is wrong or should be addressed — a conscious choice to overlook wrongdoing. Example: 'The supervisor turned a blind eye to the workers' safety violations.'",
        h: 'This idiom means to deliberately IGNORE something one knows is wrong.',
        yr: 'SS2',
      },
      {
        q: "What does 'let the cat out of the bag' mean?",
        o: [
          'To release a cat from a bag literally',
          'To accidentally or carelessly reveal a secret',
          'To keep a secret very carefully',
          'To prepare a surprise for someone',
        ],
        a: 1,
        e: "'Let the cat out of the bag' means to accidentally reveal information that was supposed to be kept secret. Example: 'We were planning a surprise party, but John let the cat out of the bag by telling her.'",
        h: 'This idiom means to accidentally REVEAL A SECRET.',
        yr: 'SS2',
      },
      {
        q: "If someone is 'sitting on the fence' about an issue, they are:",
        o: [
          'Physically sitting on a fence',
          'Deliberately remaining neutral; avoiding taking a definite position on either side of a dispute',
          'Firmly committed to one side of an argument',
          'Changing their opinion frequently',
        ],
        a: 1,
        e: "'Sit on the fence' describes refusing to commit to either side of an argument or controversy — remaining in a neutral, non-committal position. Example: 'Politicians often sit on the fence on controversial issues to avoid losing votes.'",
        h: 'This idiom means remaining NEUTRAL — not taking a side.',
        yr: 'SS2',
      },
      {
        q: "What does 'burn the midnight oil' mean?",
        o: [
          'To cook food late at night',
          'To work, study, or be active very late into the night',
          'To waste resources carelessly',
          'To cause a fire by accident',
        ],
        a: 1,
        e: "'Burn the midnight oil' refers to working or studying very late into the night — originally from the time when people used oil lamps and burning them late indicated working late hours. Example: 'She burned the midnight oil to finish the report before the deadline.'",
        h: 'This idiom means to WORK/STUDY VERY LATE at night.',
        yr: 'SS2',
      },
      {
        q: "If you 'bite off more than you can chew,' you have:",
        o: [
          'Eaten too much food at once',
          'Attempted to do more than you are capable of handling',
          'Made a very good, well-thought decision',
          'Successfully completed a very difficult task',
        ],
        a: 1,
        e: "'Bite off more than you can chew' means to attempt more than one can manage — taking on a task or responsibility that is too large or complex to handle successfully. Example: 'By accepting five projects simultaneously, she bit off more than she could chew.'",
        h: 'This idiom means attempting MORE THAN YOU CAN HANDLE.',
        yr: 'SS2',
      },
      {
        q: "When someone is described as 'under the weather,' they are:",
        o: [
          'Outdoors in bad weather',
          'Feeling slightly unwell or ill',
          'Very happy and in excellent health',
          'Lost or confused about their location',
        ],
        a: 1,
        e: "'Under the weather' is an idiom meaning to feel slightly unwell or ill — not seriously sick but not in good health. Example: 'I won't be coming to the office today; I've been feeling under the weather since yesterday.'",
        h: 'This idiom means feeling SLIGHTLY ILL/unwell.',
        yr: 'SS2',
      },
      {
        q: "What does 'miss the boat' mean in the idiom sense?",
        o: [
          "To literally miss a boat/ship's departure",
          'To miss an opportunity, often because of delay or inaction',
          'To make the right decision at the right time',
          'To successfully arrive before a deadline',
        ],
        a: 1,
        e: "'Miss the boat' means to miss an opportunity — usually because one waited too long or didn't act when the chance was available. Example: 'If you don't register today, you'll miss the boat — the deadline is tomorrow.'",
        h: 'This idiom means to MISS AN OPPORTUNITY — often through delay.',
        yr: 'SS2',
      },
      {
        q: 'In critical reading, what is an INFERENCE?',
        o: [
          'Information stated explicitly and directly in the text',
          'A logical conclusion drawn from clues in the text, going beyond what is directly stated',
          'A personal opinion unrelated to the text',
          'The main idea of the first paragraph only',
        ],
        a: 1,
        e: "An inference is a logical deduction made from indirect evidence in a text — the reader reads between the lines to conclude something not explicitly stated. Example: if a character keeps checking their watch and sighing, you can infer they are impatient, even if the text doesn't say so.",
        h: 'Inference = reading BETWEEN THE LINES — conclusion from clues, not directly stated.',
        yr: 'SS2',
      },
      {
        q: 'How does a FACT differ from an OPINION in critical reading?',
        o: [
          'Facts and opinions are identical and interchangeable',
          'A FACT can be verified or proven true/false; an OPINION expresses a judgment, belief, or view that cannot be objectively verified',
          'Opinions are always correct; facts are sometimes incorrect',
          'Facts only appear in textbooks; opinions only in newspapers',
        ],
        a: 1,
        e: "Facts: verifiable, objective statements that can be proven (e.g., 'Nigeria gained independence in 1960'). Opinions: personal views or judgments that express what someone thinks/believes but cannot be objectively verified (e.g., 'Independence was the best thing to happen to Nigeria').",
        h: 'Facts can be VERIFIED; opinions express JUDGMENTS/VIEWS that cannot.',
        yr: 'SS2',
      },
      {
        q: "What does the idiom 'spill the beans' mean?",
        o: [
          'To accidentally drop a food item',
          'To reveal secret or confidential information, especially when it was supposed to be kept private',
          'To carefully share information at the right time',
          'To create confusion in a situation',
        ],
        a: 1,
        e: "'Spill the beans' means to reveal secret information — often something that was meant to be kept confidential. It is similar in meaning to 'let the cat out of the bag', but 'spill the beans' often implies the information was unwillingly or accidentally shared.",
        h: 'This idiom means to REVEAL SECRETS — sharing confidential information.',
        yr: 'SS2',
      },
      {
        q: "'Bite the hand that feeds you' means to:",
        o: [
          'Show gratitude to those who help you',
          'Act against, harm, or show ingratitude to those who are providing support or help to you',
          'Literally bite a feeding hand',
          'Be very aggressive when eating',
        ],
        a: 1,
        e: "'Bite the hand that feeds you' describes someone who shows ingratitude or actively harms/works against the person or organisation that supports or benefits them. Example: 'Criticising your employer publicly is biting the hand that feeds you.'",
        h: 'This idiom means showing INGRATITUDE/acting against those who HELP you.',
        yr: 'SS2',
      },
      {
        q: "What does 'acrimony' mean?",
        o: [
          'Great kindness and goodwill',
          'Bitterness and ill feeling, especially in disagreements',
          'An abundance of resources',
          'A type of legal document',
        ],
        a: 1,
        e: "'Acrimony' refers to bitter, sharp hostility or ill feeling, often in disputes or relationships. Example: 'The divorce proceedings were characterised by great acrimony between the two parties.'",
        h: 'This word means BITTERNESS and ill feeling.',
        yr: 'SS2',
      },
      {
        q: "A 'prolific' writer is one who:",
        o: [
          'Writes very slowly and produces little',
          'Produces many works, especially of high quality, in a short period',
          'Only writes on a single specific topic',
          'Has never published any work',
        ],
        a: 1,
        e: "'Prolific' describes someone (or something) that produces a large amount of work or output. Example: 'The prolific novelist published over thirty books in her lifetime.'",
        h: 'This word means producing MANY works/much output.',
        yr: 'SS2',
      },
      {
        q: "An 'idiosyncrasy' is best described as:",
        o: [
          'A serious personality disorder',
          'A distinctive personal habit or way of behaving that is slightly unusual or peculiar to an individual',
          'A type of language structure',
          'General, widely shared behaviour',
        ],
        a: 1,
        e: "'Idiosyncrasy' refers to a distinctive or unusual habit, mannerism, or characteristic particular to one person. Example: 'His idiosyncrasy of humming while working was both charming and distracting.'",
        h: 'This word means a DISTINCTIVE PERSONAL HABIT or peculiarity.',
        yr: 'SS2',
      },
      {
        q: "A statement is described as 'ambiguous' when it is:",
        o: [
          'Crystal clear with only one possible meaning',
          'Open to more than one interpretation; uncertain or vague',
          'Completely false',
          'Extremely long and detailed',
        ],
        a: 1,
        e: "'Ambiguous' describes something that can be understood in more than one way — having double meaning or unclear intent. Example: 'His ambiguous answer left everyone unsure of his actual position.'",
        h: 'This word means having MORE THAN ONE possible interpretation.',
        yr: 'SS2',
      },
      {
        q: "A 'cursory' review is one that is:",
        o: [
          'Very thorough and detailed',
          'Quick, hasty, and not thorough',
          'Extremely critical and harsh',
          'Completely impartial',
        ],
        a: 1,
        e: "'Cursory' describes a brief, hasty, superficial approach — done without thoroughness or careful attention. Example: 'A cursory reading of the text will not reveal its deeper meanings.'",
        h: 'This word means QUICK and SUPERFICIAL — not thorough.',
        yr: 'SS2',
      },
      {
        q: "What does 'didactic' mean in the context of writing or literature?",
        o: [
          'Entertainment-focused with no message',
          'Intended to teach a lesson or convey moral/ethical instruction',
          'Related only to poetry',
          'Completely objective and without any teaching purpose',
        ],
        a: 1,
        e: "'Didactic' describes writing or speech primarily intended to be educational or instructional — often with a moral or ethical message. Example: 'Many of Chinua Achebe's novels are didactic, teaching lessons about the impact of colonialism.'",
        h: 'This word means TEACHING-FOCUSED — intended to convey a lesson or moral.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Comprehensive Review: SS2 English Language',
    topicCode: 'SS2-ENG-24',
    module: 'Synthesis and Review',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  This final topic provides a comprehensive review of the key concepts covered across all three terms of SS2 English Language, organised for effective exam preparation. Each major skill area is synthesised with the most important rules and patterns.\n</div>\n\n<h3 class="learn-subheading">1. Summary of Key Grammar Rules</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Topic</th><th>Key Rule</th></tr></thead>\n    <tbody>\n      <tr><td>Nouns</td><td>Countable vs uncountable; collective noun agreement; plural forms (irregular, zero plural, foreign)</td></tr>\n      <tr><td>Pronouns</td><td>Subject/object case (I/me); possessive determiner vs pronoun (my/mine); who/whom; reflexive forms</td></tr>\n      <tr><td>Verb tenses</td><td>Sequence of tenses: backshift in reported speech; conditionals (zero/first/second/third)</td></tr>\n      <tr><td>Active/Passive</td><td>Formula: BE + past participle; intransitive verbs cannot be passivised</td></tr>\n      <tr><td>Concord</td><td>Each/every/neither = singular; or/nor = agree with nearer subject; uncountable = singular; news/maths = singular</td></tr>\n      <tr><td>Adjuncts/Conjuncts/Disjuncts</td><td>Adjunct: modifies verb; Conjunct: links clauses; Disjunct: comments on whole sentence</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">2. Summary of Writing Genres and Their Distinctions</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Genre</th><th>Key Distinguishing Feature</th></tr></thead>\n    <tbody>\n      <tr><td>Formal letter</td><td>Address+date, salutation (Dear Sir/Madam or Dear Mr. [Name]), close (Yours faithfully/sincerely), full name</td></tr>\n      <tr><td>Informal letter</td><td>First name salutation (Dear [Name]), contractions allowed, Love/Best wishes close, first name signature</td></tr>\n      <tr><td>Article</td><td>Catchy title, no address/salutation, general readership, engaging hook</td></tr>\n      <tr><td>Speech</td><td>Direct audience address (Ladies and gentlemen...), rhetorical devices, spoken delivery style</td></tr>\n      <tr><td>Expository essay</td><td>Informs/explains multiple aspects; balanced, no clear \'for/against\' position required</td></tr>\n      <tr><td>Argumentative essay</td><td>Takes a CLEAR POSITION, persuades, addresses counter-arguments</td></tr>\n      <tr><td>Summary</td><td>Own words, main ideas only, within word limit, no personal opinion added</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">3. Oral English Review</h3>\n<ul class="learn-list">\n  <li>Voiced/voiceless pairs: p/b, t/d, k/g, f/v, s/z, θ/ð, ʃ/ʒ, tʃ/dʒ</li>\n  <li>Always voiced: m, n, ŋ, l, r, w, j</li>\n  <li>Word stress: nouns stress first syllable; verbs often second; -tion/-sion suffix stresses syllable before</li>\n  <li>Emphatic stress shifts focus and meaning of a sentence</li>\n</ul>\n\n<h3 class="learn-subheading">4. Vocabulary Review: Selected Final Words</h3>\n<p class="learn-p"><strong>salubrious</strong> (health-giving; pleasant and clean), <strong>intrepid</strong> (fearless and adventurous), <strong>recalcitrant</strong> (stubbornly resistant to authority), <strong>perspicacious</strong> (having a ready insight; shrewd), <strong>indefatigable</strong> (persisting tirelessly), <strong>sycophant</strong> (a person who flatters powerful people for personal gain), <strong>tenacious</strong> (holding firm to something; determined).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Final Exam Tips:</strong> (1) Genre identification: note what the question asks for BEFORE writing. (2) Avoid concord errors: always identify the TRUE subject (ignore intervening phrases). (3) Reported speech: always backshift if reporting verb is PAST (unless universal truth). (4) Idioms: the FIGURATIVE meaning is always the answer. (5) Summary: ALWAYS use your own words and NEVER add personal opinion. (6) Letter vs Article: letter needs addresses/salutation; article needs title only.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Synthesis:</strong> All writing genres share core requirements: clear purpose, logical organisation, appropriate register/tone, and strong conclusion. Grammar across all topics shares the same underlying logic: identify the true subject/agent, match verbs correctly, maintain appropriate tense sequence, and use appropriate lexical choices for context and register.\n</div>\n  ',
    questions: [
      {
        q: 'Which sentence contains an error that combines a concord and vocabulary error?',
        o: [
          'The committee have reached a unanimous decision.',
          'The news are very good today.',
          'Each of the students has submitted their assignments.',
          'The data suggest a significant trend.',
        ],
        a: 1,
        e: "'The news ARE very good today' contains a concord error — 'news' is an uncountable noun that takes a SINGULAR verb: 'The news IS very good today.' All other options are defensible: committee (collective) can take plural in some contexts, 'each' takes singular 'has', and 'data suggest' (data = plural of datum) is technically correct.",
        h: "'News' is UNCOUNTABLE — always takes SINGULAR verb (is, was).",
        yr: 'SS2',
      },
      {
        q: "Convert correctly to passive voice: 'They had already distributed the results before the principal arrived.'",
        o: [
          'The results have been distributed before the principal arrived.',
          'The results had already been distributed by them before the principal arrived.',
          'The results were already distributed before the principal arrived.',
          'The results will have been distributed before the principal arrived.',
        ],
        a: 1,
        e: "Past Perfect active ('had already distributed') becomes 'had already been distributed' in passive — maintaining the Past Perfect tense (HAD + BEEN + past participle) and the time relationship to the principal's arrival.",
        h: 'Past Perfect passive = HAD + BEEN + past participle.',
        yr: 'SS2',
      },
      {
        q: 'Which correctly reported sentence follows the sequence of tenses rules?',
        o: [
          'The captain announced that the ship will depart at dawn.',
          'The teacher explained that the sun revolves around the earth.',
          'The doctor said she had examined the patient the previous day.',
          'The politician stated that he had been will investigate the matter.',
        ],
        a: 2,
        e: "'The doctor said she had examined the patient the previous day' correctly applies backshift: past simple ('examined') → past perfect ('had examined') after the past reporting verb 'said'. 'The previous day' correctly replaces 'yesterday'.",
        h: 'Past reporting verb → past simple backshifts to past perfect.',
        yr: 'SS2',
      },
      {
        q: "Which option correctly identifies the writing genre for a piece that begins with 'Dear Tunde, How have you been?' and ends with 'Best wishes, Kemi'?",
        o: [
          'A formal letter',
          'An informal letter to a friend',
          'A speech',
          'An article for publication',
        ],
        a: 1,
        e: "First name salutation ('Dear Tunde'), closing with 'Best wishes' and a first name signature ('Kemi') — these are the distinctive features of an INFORMAL LETTER to a friend.",
        h: "First name salutation + 'Best wishes' close + first name signature = INFORMAL LETTER.",
        yr: 'SS2',
      },
      {
        q: "Identify the type of adverbial in bold: 'Regrettably, the government has failed to address the housing crisis.'",
        o: [
          'Adjunct of manner',
          'Conjunct of contrast',
          "Disjunct — the speaker/writer's evaluative attitude toward the situation",
          'Adjunct of time',
        ],
        a: 2,
        e: "'Regrettably' is an EVALUATIVE DISJUNCT — it expresses the writer's attitude (regret) toward the entire proposition (the government's failure). It sits outside the core clause and can be removed without affecting the sentence's grammatical completeness.",
        h: "'-ly' words at sentence start expressing ATTITUDE (regrettably, fortunately, surprisingly) = disjuncts.",
        yr: 'SS2',
      },
      {
        q: 'Which sentence contains the INCORRECT use of a conditional structure?',
        o: [
          'If I had more time, I would learn a new language.',
          'If you heat ice, it melts.',
          'If they had invested wisely, they would be wealthy now.',
          'If I were you, I would accept the offer.',
        ],
        a: 2,
        e: "'If they had invested wisely, they would be wealthy now' — this mixes conditional types. 'Had invested' (Past Perfect, Third conditional) should pair with 'would have been wealthy' (Third conditional). 'Would be wealthy' (present consequence) doesn't correctly complete the Third conditional referring to an unreal PAST.",
        h: "Third conditional: Past Perfect + 'would HAVE' + past participle (not 'would be').",
        yr: 'SS2',
      },
      {
        q: "What does the idiom 'break a leg' mean?",
        o: [
          'To accidentally fracture a limb',
          'Good luck (especially used in theatre/performance contexts)',
          'To break something intentionally',
          'To stop running or moving',
        ],
        a: 1,
        e: "'Break a leg' is a theatrical idiom wishing someone good luck — especially before a performance. Using 'good luck' directly is considered bad luck in theatre superstition, so 'break a leg' developed as the alternative.",
        h: 'This theatrical idiom means GOOD LUCK.',
        yr: 'SS2',
      },
      {
        q: "Which sentence correctly applies the summary writing rule of using ONE'S OWN WORDS?",
        o: [
          "The author states: 'Education is the fundamental cornerstone of societal development.'",
          'The author stresses the essential role that education plays in shaping and advancing society.',
          "Education, the author argues, is 'the fundamental cornerstone of societal development.'",
          "According to the author, 'education is fundamental' (quoting half the original).",
        ],
        a: 1,
        e: "'The author stresses the essential role that education plays in shaping and advancing society' correctly PARAPHRASES the original without direct quotation — this is the correct summary writing approach. Options A, C, and D all incorporate direct quotation.",
        h: 'Summaries paraphrase in OWN WORDS — no direct quotation from the original.',
        yr: 'SS2',
      },
      {
        q: "Which word means 'fear-inducing by being very bold and adventurous'? No — correcting: 'Intrepid' means:",
        o: [
          'Cowardly and easily frightened',
          'Fearless and bold, especially in the face of danger',
          'Extremely kind and gentle',
          'Very shy and reserved',
        ],
        a: 1,
        e: "'Intrepid' describes someone who is fearlessly bold and adventurous, undaunted by danger. Example: 'The intrepid explorer crossed the desert alone.'",
        h: 'This word means FEARLESSLY BOLD and adventurous.',
        yr: 'SS2',
      },
      {
        q: "A 'sycophant' is a person who:",
        o: [
          'Speaks very honestly even when it is unwelcome',
          'Flatters powerful people excessively in order to gain personal advantage',
          'Avoids all social interaction',
          'Always makes independent decisions',
        ],
        a: 1,
        e: "'Sycophant' describes a person who uses flattery and excessive compliments toward powerful/wealthy people to gain personal advantage — a yes-man. Example: 'The manager's sycophants never pointed out his mistakes.'",
        h: 'A sycophant FLATTERS the powerful to gain PERSONAL ADVANTAGE.',
        yr: 'SS2',
      },
      {
        q: 'Which sentence demonstrates the correct use of a phrasal verb?',
        o: [
          'She looked the matter up into a book.',
          'He carried the proposal out with precision.',
          'She looked up the matter in a dictionary.',
          'He carried out the proposal with precision.',
        ],
        a: 3,
        e: "'He carried out the proposal with precision' correctly uses 'carry out' (to perform/execute). When the object is a NOUN phrase ('the proposal'), it can come either between or after: 'carried out the proposal' and 'carried the proposal out' are both correct. Option C is also acceptable ('looked up the matter'). Option A ('looked...up into') is incorrect usage.",
        h: 'Separable phrasal verbs: NOUN objects can come before or after the particle.',
        yr: 'SS2',
      },
      {
        q: "Which phrase correctly nominalized: convert 'decide' (verb) to its noun form?",
        o: ['Decidement', 'Decideness', 'Decision', 'Decidation'],
        a: 2,
        e: "'Decision' is the correct noun form of 'decide', formed by adding '-sion' (with spelling change: decid→decis-+ion). This follows the common -tion/-sion nominalization pattern for many verbs ending in '-de'.",
        h: 'Decide → Decision (-sion suffix, common for verbs ending in -de).',
        yr: 'SS2',
      },
      {
        q: 'Which of the following correctly demonstrates the plural of a foreign-origin noun?',
        o: [
          'The criterions for admission are strict.',
          'The criteria for admission are strict.',
          'The criterias for admission are strict.',
          'The criteria for admission is strict.',
        ],
        a: 1,
        e: "'The criteria for admission ARE strict' — 'criteria' is the PLURAL of 'criterion' (Greek origin), and as a plural noun, it takes the plural verb 'are'. 'Criterions' and 'criterias' are both incorrect.",
        h: 'Criteria = plural of criterion; takes a PLURAL verb.',
        yr: 'SS2',
      },
      {
        q: "What does 'tenacious' mean?",
        o: [
          'Easily discouraged and giving up quickly',
          'Holding firm to something; persistent and determined',
          'Very generous and giving',
          'Excessively talkative',
        ],
        a: 1,
        e: "'Tenacious' describes someone who holds firmly to their purpose, beliefs, or possessions — not giving up easily. Example: 'Her tenacious pursuit of justice finally led to the conviction.'",
        h: "This word means PERSISTENT and holding FIRMLY to one's purpose.",
        yr: 'SS2',
      },
      {
        q: "Which word means 'stubbornly resistant to authority or control'?",
        o: ['Indefatigable', 'Tenacious', 'Recalcitrant', 'Perspicacious'],
        a: 2,
        e: "'Recalcitrant' describes someone who is stubbornly resistant to authority, control, or direction. Example: 'The recalcitrant prisoner refused to comply with any prison rules.'",
        h: 'This word means STUBBORNLY RESISTANT to authority.',
        yr: 'SS2',
      },
      {
        q: "Which word means 'having a ready insight into things; shrewd and perceptive'?",
        o: ['Sycophant', 'Perspicacious', 'Salubrious', 'Recalcitrant'],
        a: 1,
        e: "'Perspicacious' describes someone with a natural ability to notice and understand things quickly and clearly — perceptive and insightful. Example: 'The perspicacious analyst immediately identified the flaw in the business model.'",
        h: 'This word means PERCEPTIVE and SHREWD — having quick insight.',
        yr: 'SS2',
      },
      {
        q: "Which word means 'persisting tirelessly; not able to be wearied'?",
        o: ['Intrepid', 'Salubrious', 'Indefatigable', 'Perspicacious'],
        a: 2,
        e: "'Indefatigable' describes someone who never seems to tire or give up — persistent and untiring. Example: 'The indefatigable activist fought for human rights for over fifty years.'",
        h: 'This word means TIRELESS — never becoming fatigued in pursuit of a goal.',
        yr: 'SS2',
      },
      {
        q: "Choose the sentence that correctly uses the prefix 'ir-':",
        o: [
          'The result was irlogical.',
          'Her behaviour was irresponsible.',
          'The plan seemed irpossible.',
          'He made an iraccurate calculation.',
        ],
        a: 1,
        e: "'Irresponsible' correctly uses the prefix 'ir-' (not) before a word beginning with 'r'. 'Irlogical' should be 'illogical' (il- before 'l'), 'irpossible' should be 'impossible' (im- before 'p'), and 'iraccurate' should be 'inaccurate' (in- before 'a').",
        h: "'Ir-' goes before words starting with 'R'; other letters need different prefix forms.",
        yr: 'SS2',
      },
      {
        q: 'Which sentence correctly uses formal letter writing conventions?',
        o: [
          'Dear Chukwu, I am writing to complain about... Yours sincerely, Dr. Balogun',
          'Dear Sir/Madam, I am writing to complain about... Yours faithfully, Dr. Balogun',
          "Dear Chukwu, I'm writing to complain about... Love, Dr. Balogun",
          'Dear Sir/Madam, I am writing to complain about... Yours sincerely, Balogun',
        ],
        a: 1,
        e: "'Dear Sir/Madam' + 'Yours faithfully' is the correct pairing — when the recipient's name is unknown, 'Yours faithfully' is the appropriate close. Option A incorrectly pairs a personal name salutation with 'Yours sincerely' but omits the formal requirement of title+surname in formal letters.",
        h: 'Dear Sir/Madam → Yours faithfully. Dear Mr. [Name] → Yours sincerely.',
        yr: 'SS2',
      },
      {
        q: "What does 'salubrious' mean?",
        o: [
          'Unhealthy and polluted',
          'Health-giving; pleasant, clean, and suitable for good health',
          'Extremely expensive',
          'Stubbornly resistant',
        ],
        a: 1,
        e: "'Salubrious' describes something conducive to health — a clean, pleasant, healthy environment or lifestyle. Example: 'The salubrious mountain air was a welcome change from the city pollution.'",
        h: 'This word means HEALTH-GIVING; clean and pleasant for good health.',
        yr: 'SS2',
      },
    ],
  },
];
